import DirectionsRunSharpIcon from "@mui/icons-material/DirectionsRunSharp";
import HouseOutlinedIcon from "@mui/icons-material/HouseOutlined";
import MessageSharpIcon from "@mui/icons-material/MessageSharp";
import NotificationsIcon from "@mui/icons-material/Notifications";
import PhoneIcon from "@mui/icons-material/Phone";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import type { DateTime } from "luxon";
import type { PersistedMonthlyExportSettings, PersistedWeeklyExportSettings } from "../components/staffing/ScheduleExport/types";
import type { MetaChipTone } from "../components/UIToolkit/Badges/MetaChip";
import type { Department } from "../contexts/AuthProvider";
import type { PersistedWeekScheduleExportSettings } from "../downloads/weekStaffScheduleExport/persistedSettings";
import type { RecommendationId } from "../pages/PbjModel/PredictionTab/recommendations.constants";
import { STATUS_BADGE, SURFACE } from "../theme/tokens";
import type { AgencyIntegrationState } from "./agencyIntegrations";

export enum EmploymentType {
	FullTime = "fullTime",
	PartTime = "partTime",
	PerDiem = "perDiem",
	Travel = "travel",
	FloatPool = "floatPool",
	PRN = "PRN",
	Unknown = "unknown",
}

export const AGENCY_DISPLAY = "Agency";
export type DisplayEmploymentType = `${EmploymentType}` | typeof AGENCY_DISPLAY;

export type FeedbackValue = "Too High" | "Too Low" | "Precisely";

export const DAYS_PER_WEEK = 7;
export const MAX_SCHEDULE_PERIODS = 6; // Maximum number of schedule cycles to show
export const DEFAULT_ALLOWED_HOLIDAYS = ["New Year's Day", "Memorial Day", "Independence Day", "Labor Day", "Thanksgiving Day", "Christmas Day"];
export const DEFAULT_HOLIDAY_START_HOUR = 6;
export const DEFAULT_HOLIDAY_END_HOUR = 6;
export const DEFAULT_WEEKEND_START_HOUR = 18;
export const DEFAULT_WEEKEND_END_HOUR = 6;
export const DEFAULT_WEEK_START_DAY = 0; // Sunday (0=Sunday, 1=Monday, ..., 6=Saturday)
export const DEFAULT_WEEKEND_START_DAY = 5;
export const DEFAULT_WEEKEND_END_DAY = 1;
export const DEFAULT_DAY_SHIFT_START_HOUR = 6;
export const DEFAULT_DAY_SHIFT_END_HOUR = 18;

export enum Days {
	Sunday = 0,
	Monday = 1,
	Tuesday = 2,
	Wednesday = 3,
	Thursday = 4,
	Friday = 5,
	Saturday = 6,
}

export enum ShiftType {
	Day = "Day",
	Morning = "Morning",
	Evening = "Evening",
	Afternoon = "Afternoon",
	Night = "Night",
	FullDay = "Full Day",
	All = "All",
}

export enum AgencySource {
	Nursa = "nursa",
	Clipboard = "clipboard",
}

export const DEFAULT_SHIFT_TIMES: Record<string, { start: [number, number]; end: [number, number] }> = {
	[ShiftType.Day]: { start: [7, 0], end: [19, 0] },
	[ShiftType.Night]: { start: [19, 0], end: [7, 0] },
	[ShiftType.Morning]: { start: [6, 0], end: [14, 0] },
	[ShiftType.Evening]: { start: [14, 0], end: [22, 0] },
	[ShiftType.Afternoon]: { start: [12, 0], end: [20, 0] },
};

export enum NurseTrainingStatus {
	Trainee = "Trainee",
	Trainer = "Trainer",
	Regular = "Regular",
	Orientation = "Orientation",
	Education = "Education",
}

export enum Timezone {
	UTC = "UTC",
}

export enum WageType {
	Hourly = "Hourly",
	Annually = "Annually",
}

export enum Position {
	Nursing = "Nursing",
	Dietary = "Dietary",
	Housekeeping = "Housekeeping",
	Physician = "Physician",
	Activities = "Activities",
	NursingAdmin = "Nursing Admin",
	Ancillary = "Ancillary",
	Administration = "Administration",
	// Sentinel: marks a role_string as intentionally skipped during HRIS sync.
	// Literal value is "UDF" (matches the backend enum + NurseFullRole.UDF); the UI renders it
	// as "Don't sync" via the `hris.mapping.dontSync` i18n key.
	DontSync = "UDF",
}

export enum DepartmentNames {
	AutoScheduler = "Auto_Scheduler",
	CenterAtLowryMainDept = "CenterAtLowry__MainDept",
	ColonialMainDept = "Colonial__MainDept",
	SouthPlatteMainDept = "South_Platte__MainDept",
	PoudreMainDept = "Poudre__MainDept",
	AssutaOrtho = "Assuta__Ortho",
	AssutaGeneralSurgery = "Assuta__GeneralSurgery",
	AssutaHeartChest = "Assuta__Heart-Chest",
	AssutaENT = "Assuta__ENT",
	AssutaDailyGeneral = "Assuta__daily-general",
	InHouseSNF = "InHouse__SNF",
	InHouseALF = "InHouse__ALF",
	InHouseHospitalDemo = "InHouse__Hospital_Demo",
	ValleyVista = "ValleyVista__Valley_Vista",
	CenterAtGrande = "Veritas__Center_at_Grande",
	CenterAtEdenHill = "Veritas__Center_at_Eden_Hill",
	Scottsdale = "Pacifica__Scottsdale_Village",
	Highland = "TL_Highland",
	// InHouse campaign demo facility: its feedback link is overridden below.
	BrightwaterSunriseMeadows = "Brightwater__Sunrise_Meadows",
	// Demo facility that opens with the UKG look (see theme/departmentLooks.ts).
	BayviewGeneralHospital = "inhouse2__Bayview_General_Hospital",
}

// The UKG demo departments: they never show the Hiring tab or the Staffing drawer's Summary tab,
// even to support users. Ids must match exactly.
const UKG_DEMO_DEPARTMENT_IDS: readonly string[] = [DepartmentNames.BayviewGeneralHospital];

export const isHiringTabHiddenForDepartment = (departmentId: string): boolean => UKG_DEMO_DEPARTMENT_IDS.includes(departmentId);

export const isSummaryTabHiddenForDepartment = (departmentId: string): boolean => UKG_DEMO_DEPARTMENT_IDS.includes(departmentId);

/**
 * Matches `inhouse2__`, `inhouse3__`, etc. Case-sensitive on purpose: never lowercase the id
 * first, or the legacy fake-data `InHouse__` depts would match too.
 * Mirrors `is_real_demo_dept` in `backend/utils.py` — keep in step.
 */
const REAL_DEMO_DEPARTMENT_PATTERN = /^inhouse\d*__/;
const LEGACY_DEMO_DEPARTMENT_PREFIX = "InHouse__";

/** True for real-data demo departments, as opposed to the legacy `InHouse__` fake-data ones. */
export const isRealDemoDepartmentId = (departmentId: string | undefined): boolean =>
	departmentId !== undefined && REAL_DEMO_DEPARTMENT_PATTERN.test(departmentId);

/** The legacy fake-data demos (`InHouse__…`), served from S3. Mirrors `should_return_demo_data` in `backend/utils.py`. */
export const isLegacyDemoDepartmentId = (departmentId: string): boolean => departmentId.startsWith(LEGACY_DEMO_DEPARTMENT_PREFIX);
/**
 * Name fragments identifying the ICU/Telemetry demo. Its Shift-Insights extras (per-area
 * census/score/nurse splits) are specific to a two-area department and must not reach the other
 * hospital demos, several of which also have "Hospital" in the name.
 *
 * Two fragments because the department was seeded as "Holland Hospital" before being renamed to
 * "ICU/Tele" — both ids are live across dev/staging/prod, so matching either keeps every existing
 * department working without a data migration.
 */
export const ICU_TELE_DEMO_NAME_FRAGMENTS = ["holland", "icu_tele"] as const;

/**
 * Is this the ICU/Telemetry demo? Only it gets the per-area breakdown in Shift Insights.
 * `isHospitalView` covers Hospital type + inhouse2; this adds the name check.
 * Lives here, not in the provider, so the near-miss ids can be tested.
 */
export const isIcuTeleDemoDepartment = (departmentId: string | undefined, isHospitalView: boolean): boolean => {
	const id = departmentId?.toLowerCase() ?? "";
	return isHospitalView && ICU_TELE_DEMO_NAME_FRAGMENTS.some((fragment) => id.includes(fragment));
};

// "Share your feedback" menu link (AdminDrawer + MobileDrawerOptions).
export const DEFAULT_FEEDBACK_FORM_URL = "https://docs.google.com/forms/d/e/1FAIpQLSfhCIVRBxbTSzo48nzu729-qwAv0TfrFuaP2pDsy_0lmSRXBQ/viewform?usp=sf_link";
// InHouse campaign facility feedback form, confirmed open to respondents.
export const CAMPAIGN_FEEDBACK_FORM_URL =
	"https://docs.google.com/forms/d/e/1FAIpQLSfacpwQ915KaqR67fs-2qi3TPtxitS0uaoponlH-rGMwJa1uQ/viewform?usp=publish-editor";

export enum DepartmentType {
	Hospital = "Hospital",
	SNF = "SNF",
	ALF = "ALF",
}

export enum StatsMode {
	PPD_HPPD = "ppd-hppd",
	Ratio = "ratio",
}

export interface HppdTarget {
	monday: number;
	tuesday: number;
	wednesday: number;
	thursday: number;
	friday: number;
	saturday: number;
	sunday: number;
}

export const HPPD_TARGET_DAYS: readonly (keyof HppdTarget)[] = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"] as const;

export const uniformHppdTarget = (value: number): HppdTarget => ({
	monday: value,
	tuesday: value,
	wednesday: value,
	thursday: value,
	friday: value,
	saturday: value,
	sunday: value,
});

/** Resolve the target for a specific date. Uses ISO weekday (Mon=1..Sun=7) to map to keys. */
export const hppdTargetForDate = (target: HppdTarget, weekdayIso: number): number => {
	return target[HPPD_TARGET_DAYS[weekdayIso - 1]];
};

export const hppdTargetAverage = (target: HppdTarget): number =>
	(target.monday + target.tuesday + target.wednesday + target.thursday + target.friday + target.saturday + target.sunday) / 7;

export enum CostDeepDiveUnit {
	Cost = "cost",
	Hours = "hours",
}

export enum RemoveUnionStaffWarning {
	None = "None",
	OnlyRecurring = "OnlyRecurring",
	All = "All",
}
/** FlatRate = add to base wage; Percentage = multiply (e.g. 1.1 for 10%). */
export enum RateHoursBonusType {
	FlatRate = "FlatRate",
	Percentage = "Percentage",
}

/** Overtime rule type: which limit the hours_limit applies to. */
export enum OvertimeConfigType {
	weekly = "weekly",
	daily = "daily",
	all = "all",
	/** Triggered by how many days in a row were worked. Not covered by `all`. */
	consecutive_day = "consecutive_day",
}

/** Where a consecutive-day streak counter resets. */
export enum ConsecutiveWindow {
	/** Resets at the department's OT week start. */
	work_week = "work_week",
}

/** Which overtime cap produced a predicted delta. Mirrors `OvertimeKind` in `backend/models/cost.py`. */
export type OvertimeKind = "weekly" | "daily" | "consecutive";

export interface OvertimeConfig {
	id?: string;
	name: string;
	hoursLimit: number;
	roles: NurseRole[];
	/** Employment types; [] = All, [t,...] = specific types. Required, never null. */
	employmentType: string[];
	/** Unions; [] = All (no filter), [u,...] = specific unions. */
	union: string[];
	/** null=all nurses, true=union members only, false=non-union only */
	isUnion?: boolean | null;
	type: OvertimeConfigType;
	/** Consecutive-day rules only; null for every other type. */
	consecutiveWindow?: ConsecutiveWindow | null;
	/** First streak day the rule applies to (Pharos: 7). */
	consecutiveDaysFrom?: number | null;
	/** Last streak day, inclusive. null/undefined = "and onward". */
	consecutiveDaysTo?: number | null;
	rate: number;
	rateType: RateHoursBonusType;
	departmentId: string;
	/** [] = all jobs; non-empty = only these jobs */
	jobIds?: string[];
}

export interface HoursRateConfig {
	id?: string;
	name: string;
	/** undefined/null = All (weekday and weekend); false = Weekday only; true = Weekend only */
	weekend?: boolean | null;
	/** undefined = not chosen; [] = All roles; non-empty = only these roles */
	roles?: NurseRole[];
	/** [] = all jobs; non-empty = only these jobs */
	jobIds?: string[];
	departmentId: string;
	/** HH:MM or null for "all day" */
	startTime?: string | null;
	/** HH:MM or null for "all day" */
	endTime?: string | null;
	bonusVal: number;
	bonusType: RateHoursBonusType;
	/** undefined/null = All; true = charge only; false = non-charge only */
	isCharge?: boolean | null;
	/** undefined/[] = All training statuses; non-empty = only these training statuses */
	trainingStatuses?: NurseTrainingStatus[];
	/** undefined/[] = All employment types; non-empty = only these types */
	employmentTypes?: EmploymentType[];
	/** undefined/[] = All agencies; non-empty = match only these agencies (by name) */
	agency?: string[];
	/** Unions; undefined/[] = All (no filter), [u,...] = specific unions. */
	union?: string[];
	/** null=all nurses, true=union members only, false=non-union only */
	isUnion?: boolean | null;
}

export type BreakRuleType = "addition" | "override";
/** Scopes a break rule: "scheduling" (cost-calc) vs "pbj" (Staffing Hours). */
export interface BreakRulesConfig {
	id?: string;
	name: string;
	/** [] = all employment types; non-empty = only these types */
	employmentTypes?: EmploymentType[];
	/** [] = all roles; non-empty = only these roles */
	roles?: NurseRole[];
	/** [] = all jobs; non-empty = only these jobs */
	jobIds?: string[];
	/** Hours threshold for rule to apply */
	breakLimit: number;
	/** Hours into shift where break is placed; defaults to breakLimit if not set */
	breakStart?: number;
	/** "addition" accumulates, "override" resets total */
	type: BreakRuleType;
	/** Break time in minutes */
	breakTime: number;
	/** Unions; undefined/[] = All (no filter), [u,...] = specific unions. */
	union?: string[];
	/** null=all nurses, true=union members only, false=non-union only */
	isUnion?: boolean | null;
	/**
	 * false = unpaid: taken out of worked hours, paid $0. true = paid: stays in worked hours at the normal
	 * rate, and counts as worked time when later rules check their break limit.
	 */
	isPaid: boolean;
	departmentId: string;
}

/** One time-of-day window a shift's start must fall in for a rule to apply. */
export interface ShiftHourWindow {
	/** "HH:MM" */
	start: string;
	/** "HH:MM"; when end < start the window wraps past midnight (e.g. 18:00-06:00). */
	end: string;
}

/** % of the shift a pickup must cover to auto-approve. 100 = whole shift only. */
export const MIN_SHIFT_COVERAGE_OPTIONS = [50, 75, 100] as const;
export type MinShiftCoverage = (typeof MIN_SHIFT_COVERAGE_OPTIONS)[number];
/** Whole shift only. */
export const FULL_SHIFT_COVERAGE: MinShiftCoverage = 100;

/** A per-department auto-approval rule set. All enabled selectors/gates must hold for a pickup to
 * auto-approve under this rule; a nurse is approved if ANY of the department's rules approves them. */
export interface AutoApproveRule {
	id?: string;
	name: string;
	/** [] = all jobs; non-empty = only these jobs (selector) */
	jobIds?: string[];
	/** Max resulting weekly overtime hours allowed; null = no OT allowed (manual) (gate) */
	maxOt?: number | null;
	/** Only auto-approve if the shift starts within this many hours from now; null = no time gate */
	timeBeforeShift?: number | null;
	/** Only auto-approve if the shift is still short by at least this many employees; null = no gate */
	staffingShortage?: number | null;
	/** [] = all days; weekday names (Mon..Sun) the shift's start-day must be in (selector) */
	shiftDays: string[];
	/** [] or 00:00-23:59 = all hours; the shift's start must fall in one window (selector) */
	shiftHours: ShiftHourWindow[];
	/** Minimum % of the shift a pickup must cover (gate) */
	minShiftCoverage: MinShiftCoverage;
	departmentId: string;
}

export enum NurseRole {
	CNA = "CNA",
	CMA = "CMA",
	RN = "RN",
	LPN = "LPN",
	LVN = "LVN",
	CG = "CG",
	RNA = "RNA",
	MT = "MT",
	NAS = "NAS",
	RA = "RA",
	DA = "DA",
	CK = "CK",
	RD = "RD",
	tech = "tech",
	CRNA = "CRNA",
	CA = "CA",
	MD = "MD",
	DO = "DO",
	NP = "NP",
	ACN = "ACN",
	PO = "PO",
	UC = "UC",
	HK = "HK",
	ACT = "ACT",
	REC = "REC",
	MDS = "MDS",
	HA = "HA",
	RLPN = "RLPN",
	CKA = "CKA",
	LNA = "LNA",
	UDF = "UDF",
	RT = "RT",
	LCSW = "LCSW",
}

export enum NurseFullRole {
	CNA = "Certified Nursing Assistant (CNA)",
	CMA = "Certified Medical Assistant (CMA)",
	RN = "Registered Nurse (RN)",
	RNA = "Restorative Nursing Assistant (RNA)",
	LPN = "Licensed Practical Nurse (LPN)",
	LVN = "Licensed Vocational Nurse (LVN)",
	CG = "Caregiver (CG)",
	MT = "Med Tech (MT)",
	NAS = "Nurse Assistant (NAS)",
	RA = "Resident Assistant (RA)",
	DA = "Dietary Aid (DA)",
	CK = "Cook (CK)",
	RD = "Registered Dietitian (RD)",
	tech = "Surgical Tech (tech)",
	CRNA = "Certified Registered Nurse Anesthetist (CRNA)",
	CA = "Care Assistant (CA)",
	MD = "Medical Doctor (MD)",
	DO = "Doctor of Osteopathic Medicine (DO)",
	NP = "Nurse Practitioner (NP)",
	ACN = "Advanced Certified Nurse (ACN)",
	PO = "Podiatry (PO)",
	UC = "Unit Clerk (UC)",
	HK = "Housekeeper (HK)",
	ACT = "Activity (ACT)",
	REC = "Receptionist (REC)",
	MDS = "MDS Coordinator (MDS)",
	HA = "Health Aide (HA)",
	RLPN = "Restorative Licensed Practical Nurse (R-LPN)",
	CKA = "Cook Aide (CKA)",
	LNA = "Laundry Aide (LNA)",
	UDF = "Undefined (UDF)",
	RT = "Respiratory Therapist (RT)",
	LCSW = "Licensed Clinical Social Worker (LCSW)",
}

export enum NurseFullRoleShort {
	"Certified Nursing Assistant (CNA)" = "CNA",
	"Certified Medical Assistant (CMA)" = "CMA",
	"Registered Nurse (RN)" = "RN",
	"Restorative Nursing Assistant (RNA)" = "RNA",
	"Licensed Practical Nurse (LPN)" = "LPN",
	"Licensed Vocational Nurse (LVN)" = "LVN",
	"Caregiver (CG)" = "CG",
	"Med Tech (MT)" = "MT",
	"Nurse Assistant (NAS)" = "NAS",
	"Resident Assistant (RA)" = "RA",
	"Dietary Aid (DA)" = "DA",
	"Cook (CK)" = "CK",
	"Registered Dietitian (RD)" = "RD",
	"Surgical Tech (tech)" = "tech",
	"Certified Registered Nurse Anesthetist (CRNA)" = "CRNA",
	"Care Assistant (CA)" = "CA",
	"Medical Doctor (MD)" = "MD",
	"Doctor of Osteopathic Medicine (DO)" = "DO",
	"Nurse Practitioner (NP)" = "NP",
	"Advanced Certified Nurse (ACN)" = "ACN",
	"Podiatry (PO)" = "PO",
	"Unit Clerk (UC)" = "UC",
	"Housekeeper (HK)" = "HK",
	"Activity (ACT)" = "ACT",
	"Receptionist (REC)" = "REC",
	"MDS Coordinator (MDS)" = "MDS",
	"Health Aide (HA)" = "HA",
	"Restorative Licensed Practical Nurse (R-LPN)" = "RLPN",
	"Cook Aide (CKA)" = "CKA",
	"Laundry Aide (LNA)" = "LNA",
	"Undefined (UDF)" = "UDF",
	"Respiratory Therapist (RT)" = "RT",
	"Licensed Clinical Social Worker (LCSW)" = "LCSW",
}

export const DEFAULT_NURSE_TYPES: NurseFullRole[] = [NurseFullRole.RN, NurseFullRole.CNA, NurseFullRole.LPN];

export const INHOUSE_SNF_DEPARTMENT_IDS: string[] = [
	DepartmentNames.InHouseSNF,
	DepartmentNames.CenterAtLowryMainDept,
	DepartmentNames.ColonialMainDept,
	DepartmentNames.SouthPlatteMainDept,
	DepartmentNames.PoudreMainDept,
];

export const INHOUSE_ALF_DEPARTMENT_IDS: string[] = [DepartmentNames.InHouseALF];

export const ASSUTA_DEPARTMENTS: string[] = [
	DepartmentNames.AssutaOrtho,
	DepartmentNames.AssutaGeneralSurgery,
	DepartmentNames.AssutaHeartChest,
	DepartmentNames.AssutaENT,
	DepartmentNames.AssutaDailyGeneral,
];

export const SNF_DEPARTMENTS_MAX_WEEKS: Record<string, number> = {
	[DepartmentNames.CenterAtLowryMainDept]: 2,
	[DepartmentNames.ColonialMainDept]: 2,
	[DepartmentNames.SouthPlatteMainDept]: 2,
	[DepartmentNames.PoudreMainDept]: 2,
};

export interface Patient {
	id: number;
	procedure: string;
	surgeon: string;
	firstPostopShift: number;
}

export interface CensusOverride {
	override_value: number;
	note?: string;
	user_id: string;
	updated_at: string;
}

/** An override the census sync deleted. The day keeps showing it until the admin accepts or retypes. */
export interface CensusReplacement {
	previous_value: number;
	replacement_value: number;
}

/**
 * One day's patient census, sliced by the dimension combinations a department's locations need.
 *
 * Keys are built by `censusDimensionKey` in `utils/censusResolution` (`unit=2N|floor=2ndNW|room=101`,
 * dimensions in that fixed order, absent ones omitted). Read it through `resolveLocationCensus` — or
 * `byDimensionsOf` for the map itself — rather than indexing it directly.
 *
 * `by_dimensions` is the only field declared here on purpose. The backend still SENDS three
 * deprecated `grouped_by_*` maps, derived from this one, because two of them are required properties
 * of its response schema and removing them would be a breaking API change. They are wire ballast for
 * clients left on the old shape — leaving them out of this type is what stops a new reader appearing.
 * The fourth, `grouped_by_location`, is gone outright: a location UUID is no longer a census bucket
 * (and it was never populated for non-EMR departments anyway).
 */
export interface CensusGrouping {
	by_dimensions: Record<string, number>;
}

export interface Day {
	date: DateTime;
	census_low: number;
	census_high: number;
	score: number;
	staffNo: number;
	certainty: number;
	isPlusActive: boolean;
	isMinusActive: boolean;
	nurses: Array<NurseInShift>;
	nursesActual?: Array<ActualNurse>;
	staffDifficulty: string;
	censusDifficulty: string;
	patientDifficulty: string;
	shiftEvents?: ShiftEvents;
	changeAlert: boolean;
	census_override?: CensusOverride;
	census_replacement?: CensusReplacement;
	census_grouping?: CensusGrouping;
}

export type DayWageStats = {
	totalSpent: number;
	traineeCost: number;
	orientationCost: number;
	ptoCost: number;
};

export type DepartmentWageDataResponse = {
	dayWageStats: Record<string, DayWageStats>;
	dayWageStatsActual: Record<string, DayWageStats>;
};

export type Break = {
	id: number;
	shiftId: string;
	startTime: DateTime;
	endTime: DateTime;
	createdAt: DateTime;
	updatedAt: DateTime;
};

export interface BaseNurseShift {
	id: string;
	nurseId: number;
	departmentId: string;
	startTime: DateTime;
	endTime: DateTime;
	isPublished?: boolean;
	isPending: boolean;
	isPendingToDelete: boolean;
	isPendingToUpdate?: boolean;
	message?: string;
	removalMessage?: string;
	location?: string;
	role: NurseFullRole;
	charge: boolean;
	name: string;
	type: EmploymentType;
	shiftType: string;
	bonus: number;
	notes: string;
	cost?: number;
	breaks: Break[];
	trainingStatus: NurseTrainingStatus;
	missingPunchIn?: boolean;
	missingPunchOut?: boolean;
	previousStartTime?: DateTime;
	previousEndTime?: DateTime;
	previousLocation?: string;
	previousNotes?: string;
	jobId?: string;
	jobName?: string;
	// Resolved against the SHIFT's job (with dept-level override applied), not the nurse's
	// primary job. Source of truth for filtering shifts out of PPD/HPPD calcs in the FE.
	// Optional so legacy/locally-constructed shifts can omit it; consumers should treat
	// undefined as "included" to match the backend SQL filter for rows without job_id.
	excludeFromPpd?: boolean;
}

export interface ActualNurse extends BaseNurseShift {}

export interface NurseInShift extends BaseNurseShift {
	isPendingToMove?: boolean;
	isPendingToSwap?: boolean;
	isDiscard?: boolean;
	swapWith?: number;
	originalShift?: number;
	originalDay?: number;
	hasAcceptedSwap?: boolean;
	replacedWith?: number;
	recurringShiftId?: number | null | undefined;
	requestId?: string;
	externalStartTime?: DateTime;
	externalEndTime?: DateTime;
	parentShiftId?: string | null;
}

export interface WeekDayShiftData {
	date: DateTime;
	staff: number;
	nurses: Record<number, NurseInShift>;
	type: ShiftType;
}

export interface ShiftEvents {
	numberAdmissionsLow: number;
	numberAdmissionsHigh: number;
	patientScore: number;
	difficultyAdmissions: string;
	numberDischargesLow: number;
	numberDischargesHigh: number;
	intermediate: number;
	numberIntermediateLow: number;
	numberIntermediateHigh: number;
	difficultyDischarges: string;
}

export interface IRM {
	numberIntermediateLow: number;
	numberIntermediateHigh: number;
	difficultyIntermediate: string;
}

export interface CVM {
	numberCathLow: number;
	numberCathHigh: number;
	difficultyCath: string;
}

export interface Shift {
	type: string;
	days: Day[];
}

export interface WeekData {
	weeklyShifts: Shift[];
	weekScore: string;
	weekLoad: string;
	weekSeason: string;
	workForce: string;
	loadText: string;
	seasonText: string;
	workForceText: string;
	weekDifficulty: string;
}

export interface ExperienceInDepartments {
	id: string;
	starting_date: DateTime;
	end_date: DateTime;
}

export interface WeeklyPreference {
	day: string;
	notes: string;
	type: Preferences.Prefer | Preferences.Avoid;
	shift: string;
	unavailable?: boolean;
	exclusive?: boolean;
	week_number: number | null;
	nurse_id: number;
}

interface WeeklyConstraint {
	consecutiveAllowedShifts?: number;
}

export enum SuggestionAction {
	Add = "Add",
	Remove = "Remove",
	Swap = "Swap",
	Replace = "Replace",
	Move = "Move",
}

export interface Suggestion {
	action: SuggestionAction;
	shiftNo: number;
	dayNo: number;
	otherNurseId: number;
	nurseId: string | number;
	displayReject: boolean;
	weekIndex: number;
	shouldHighlight: boolean;
	isPending?: boolean;
}

export enum IssuePriority {
	TopPriority = "Top priority",
	Important = "Important",
	Optional = "Optional",
}

export enum IssueType {
	Nurse = "Nurse",
	Shift = "Shift",
}

export interface SpecificPreference {
	id: string;
	notes: string;
	nurse_id: number;
	type: Preferences.Prefer | Preferences.Avoid;
	data?: string;
	start_date: DateTime;
	end_date: DateTime;
	hours: number;
	reason: PtoTypes | string;
	post_shifts: boolean;
	external_time_off?: ExternalTimeOff;
}

export enum AbsentReason {
	RDO = "RDO",
	PTO = "PTO",
	FMLA = "FMLA",
	ADMIN = "Admin",
	EDUCATION = "Ed.",
	RQRUIED_CLASS = "RC",
}
export interface Qualification {
	title: string;
	issuer: string;
	issue_date: string;
	expires: string | null;
	skills: string[];
	credential_id: string | null;
}

export interface BaseWage {
	wage: number;
	wageType: WageType;
	startTime: DateTime;
	endTime?: DateTime;
}

export interface Job {
	id: string;
	name: string;
	displayName: string;
	sortOrder: number;
	excludeFromPpd: boolean;
}

export interface DepartmentJobPpdOverride {
	departmentId: string;
	jobId: string;
	excludeFromPpd: boolean;
}

export interface CertificationJobMapping {
	roleString: string;
	// Ordered: jobs[0] is the job employees with this role string sync as; the rest are additional
	// jobs they also carry (DAT-466). Empty for DONT_SYNC overrides (`position === Position.DontSync`).
	jobs: Job[];
	// Populated for rows sourced from `certification_job_hris` or `department_certification_job`;
	// null for rows sourced from `certification_job_default` only (the defaults table doesn't carry position/role).
	position?: Position | null;
	role?: string | null;
	isOverride: boolean;
	isCustom: boolean;
	defaultDisplay?: string | null;
	employeeSync?: SyncScheduledResponse | null;
}

/**
 * DAT-586: the job combination that employees with `role` already hold in this facility, used to
 * suggest jobs when an admin creates or edits an employee by hand.
 *
 * `jobIds` is a modal set — the exact combination the most same-role employees share, not a union —
 * ordered by job sort order, and already restricted to the jobs this department has enabled. Roles
 * with no qualifying employees are absent from the response entirely rather than carrying an empty
 * list, so "no entry" means "no signal" and the UI shows nothing.
 */
export interface RoleJobRecommendation {
	role: NurseFullRole;
	jobIds: string[];
	/** Employees holding exactly this combination. */
	employeeCount: number;
	/** All employees with this role holding at least one selectable job — the denominator. */
	roleEmployeeCount: number;
}

/**
 * CMS `processType` stamped on the PBJ submission files (XML `<staffingHours processType=…>` and
 * the XLSX "Staffing Hours" preamble). MERGE adds/updates the filed hours on top of whatever CMS
 * already holds for the quarter — the normal first filing. REPLACE tells CMS to discard what it
 * holds for the quarter and take this file as the truth, which is what a resubmission requires.
 * Mirrors the backend `PbjSubmissionProcessType`; the literal values are the CMS wire tokens.
 */
export enum PbjSubmissionProcessType {
	Merge = "merge",
	Replace = "replace",
}

// ── PBJ Jobs tab (DAT-198): per-department role_string → pbj_job mapping + job registry ──

export interface PbjJob {
	id: string;
	departmentId: string;
	code: string;
	description: string;
	deleted: boolean;
	updatedBy?: string | null;
	createdAt?: string | null;
	updatedAt?: string | null;
}

/** One role_string and the PBJ job it maps to. code/description are null when unmapped. */
export interface PbjJobMappingRow {
	roleString: string;
	pbjJobId: string | null;
	code: string | null;
	description: string | null;
}

/** A pbj_job create (no id, tempId set) or edit (id set). Matched by code on save. */
export interface PbjJobUpsertItem {
	id?: string;
	tempId?: string;
	code: string;
	description: string;
}

export interface PbjJobDeleteItem {
	id: string;
}

/** Maps a role_string to a job by CODE (null clears the mapping). */
export interface PbjJobMappingEditItem {
	roleString: string;
	code: string | null;
}

export interface PbjJobsSaveBody {
	departmentId: string;
	applyToNetwork: boolean;
	jobUpserts: PbjJobUpsertItem[];
	jobDeletes: PbjJobDeleteItem[];
	mappingEdits: PbjJobMappingEditItem[];
}

export interface PbjJobsSaveResult {
	affectedDepartmentIds: string[];
	jobs: PbjJob[];
	mapping: PbjJobMappingRow[];
}

/** A PBJ Staffing Hours break rule (the dedicated pbj_break_rules table, separate from BreakRulesConfig).
 * Filters by rawJobs (normalized HRIS role strings; [] = all) + employmentTypes ([] = all). */
export interface PbjBreakRule {
	id?: string;
	name: string;
	/** [] = all employment types; non-empty = only these types */
	employmentTypes?: EmploymentType[];
	/** [] = all; normalized HRIS role strings the rule applies to (eligibility filter) */
	rawJobs?: string[];
	/** Hours threshold for rule to apply */
	breakLimit: number;
	/** Hours into shift where break is placed; defaults to breakLimit if not set */
	breakStart?: number;
	/** "addition" accumulates, "override" resets total */
	type: BreakRuleType;
	/** Break time in minutes */
	breakTime: number;
	departmentId: string;
}

/** The Breaks-tab changeset: the full set of PBJ break rules for the department. */
export interface PbjBreakRulesSaveBody {
	departmentId: string;
	applyToNetwork: boolean;
	rules: PbjBreakRule[];
}

export interface PbjBreakRulesSaveResult {
	affectedDepartmentIds: string[];
	rules: PbjBreakRule[];
}

export enum HrisRawSyncStatus {
	Synced = "synced",
	UnmappedRole = "unmapped_role",
	UnmappedEmploymentType = "unmapped_employment_type",
	UnresolvedDepartment = "unresolved_department",
	FilteredByPosition = "filtered_by_position",
	// DAT-464: supersedes FilteredByPosition as the sync gate's rejection. FilteredByPosition is kept
	// because persisted hris_raw_employee rows still carry it and are never backfilled.
	FilteredByJob = "filtered_by_job",
	FilteredByDepartment = "filtered_by_department",
	EmptyId = "empty_id",
	ConversionError = "conversion_error",
	DuplicateEmployee = "duplicate_employee",
}

export interface HrisRawEmployeeRow {
	employeeId: string;
	firstName: string;
	lastName: string;
	fullName: string | null;
	syncStatus: HrisRawSyncStatus;
	rejectionDetail: string | null;
	role: string;
	employmentType: string;
	internalDepartment: string;
	position: string | null;
	updatedAt: string;
	/** DAT-435 provenance: the InHouse department this row resolved to (may be a sibling sharing the same HRIS file). Null = unresolved row that syncs nowhere. */
	departmentId?: string | null;
	/** Display name for `departmentId`, resolved server-side. Null when `departmentId` is null. */
	departmentDisplayName?: string | null;
}

/**
 * Per-employee activity stats for the HRIS Mapping screen (DAT-154). Keyed by `employeeId`
 * so it can be joined onto `HrisRawEmployeeRow`. Only employees linked to a nurse appear.
 */
export interface HrisEmployeeShiftStats {
	employeeId: string;
	nurseId: number;
	active: boolean;
	actualShiftsLast30: number;
	scheduledShiftsLast30: number;
}

/**
 * One employee ACTIVE in a department whose PRIMARY job that department doesn't enable (DAT-834).
 *
 * Read-only configuration signal — it replaced the `job_not_enabled` status decision, which framed a
 * settings mismatch as a deactivate recommendation nobody could answer. The two fixes are enabling the
 * job, or remapping the HRIS role; neither is an action on the employee.
 */
export interface JobNotEnabledEmployee {
	/**
	 * `external_employee.id` — the join key onto the HRIS Mapping pane's raw rows, which carry no nurse
	 * id. `null` for a nurse with no HRIS record in this department (a manually created employee): just
	 * as misconfigured, but with no raw row to mark.
	 */
	employeeId: string | null;
	nurseId: number;
	/** "First Last", or `null` when the record carries neither — never an empty string, so a caller
	 *  joining names drops the person instead of printing a dangling separator. */
	name: string | null;
	jobId: string;
	/** `null` when the id no longer resolves to a job — `nurse.job_ids` is a bare UUID array with no FK. */
	jobName: string | null;
}

export interface EmploymentTypeMapping {
	rawString: string;
	employmentType: EmploymentType;
	isOverride: boolean;
	isCustom: boolean;
	defaultDisplay?: string | null;
	/** Only set on mutation responses (DAT-386): the employee re-sync scheduled by the mapping change. */
	employeeSync?: SyncScheduledResponse | null;
}

export interface EmployeeStatusMapping {
	rawString: string;
	// `null` means the raw HRIS status string is UNMAPPED — the system has no interpretation yet
	// and Ops must resolve it by picking one of the four EffectiveEmployeeStatus values.
	status: EffectiveEmployeeStatus | null;
	isOverride: boolean;
	defaultDisplay?: string | null;
}

export interface TimeOffTypeMapping {
	rawString: string;
	ptoType: PtoTypes | null;
	reason?: TimeOffReasons | null;
	isOverride: boolean;
	isCustom: boolean;
	defaultDisplay?: string | null;
}

/** One raw PTO event in the last 60 days, shown when a Time Off mapping row is expanded (DAT-200). */
export interface HrisPtoTimeOff {
	employeeName: string;
	eventDate: string | null;
	hours: number | null;
}

/**
 * Per-raw-type PTO activity for the Time Off mapping screen (DAT-200): the time-offs over the last 60 days.
 * Keyed by the trim+lowercased `rawString` so it can be joined onto `TimeOffTypeMapping` rows. `countLast60`
 * equals `timeOffs.length` — surfaced explicitly so the badge doesn't depend on the list being loaded.
 */
export interface HrisPtoTypeStats {
	rawString: string;
	countLast60: number;
	timeOffs: HrisPtoTimeOff[];
}

/** Pre-count for the DAT-200 confirm dialog: how many synced time-off records a re-map would actually touch. */
export interface TimeOffRemapPreview {
	count: number;
}

/** One active linked employee in the DAT-535 primary-job change preview (keyed by `nurseId`). */
export interface CertPrimaryJobChangePreviewRow {
	nurseId: number;
	name: string;
	// The employee's current first job, or null when they hold no jobs.
	currentJob: Job | null;
	// true ⇒ this employee's first job is overwritten with `newJob` on save; false ⇒ left untouched.
	willChange: boolean;
}

/**
 * Preview for the DAT-535 confirm dialog, shown before saving a primary-job change on an existing cert
 * role-string mapping. Lists every active linked employee — who will change (current job → `newJob`) and
 * who won't (with their current job) — so the propagation is confirmed before the backend applies it.
 */
export interface CertPrimaryJobChangePreview {
	newJob: Job;
	rows: CertPrimaryJobChangePreviewRow[];
}

// Delete-override results. `revertedTo` is non-null when a global default survives
// after override removal; null when the row was a custom entry and has been fully removed.
export interface CertMappingDeleteResult {
	key: string;
	revertedTo: CertificationJobMapping | null;
	/** Only set on mutation responses (DAT-386): the employee re-sync scheduled by the mapping change. */
	employeeSync?: SyncScheduledResponse | null;
}

/**
 * A bulk job change on the Team page (DAT-844). `add` appends `toJobId` and leaves each employee's
 * primary job alone; `replace` swaps `fromJobId` for `toJobId` in place, so a replaced primary job
 * makes the new job primary.
 */
export type BulkNurseJobsAction = "add" | "replace";

/**
 * What to do with employees who still have future shifts under the job being replaced: move those
 * shifts onto the new job and change them, or leave those employees exactly as they are.
 */
export type BulkNurseJobsBlockedMode = "move_shifts" | "leave_unchanged";

export interface BulkNurseJobsRequest {
	action: BulkNurseJobsAction;
	/** The job being replaced; `null` for `add`. */
	fromJobId: string | null;
	toJobId: string;
	nurseIds: number[];
}

export interface BulkNurseJobsApplyRequest extends BulkNurseJobsRequest {
	blockedMode: BulkNurseJobsBlockedMode;
}

/** Why each left-out employee was skipped. The server keeps the groups disjoint. */
export interface BulkNurseJobsSkipped {
	notInDepartment: number[];
	/** Belongs to several facilities, and job changes are only made from their home facility. */
	notHomeFacility: number[];
	/**
	 * `add` only: the employee already holds the job being added. A `replace` onto a job the employee
	 * already holds still changes them — the old job is dropped, with no duplicate — so it never lands here.
	 */
	alreadyHasJob: number[];
	/** `replace` only: the employee doesn't hold the job being replaced. */
	doesNotHoldJob: number[];
}

export interface BulkNurseJobsPreview {
	willChange: number[];
	/** Employees with future shifts under the replaced job. Always `[]` for `add`. */
	blockedByShifts: number[];
	skipped: BulkNurseJobsSkipped;
}

export interface BulkNurseJobsResult {
	updated: number[];
	/** Employees left unchanged because they have future shifts under the replaced job (`leave_unchanged`). */
	blockedByShifts: number[];
	skipped: BulkNurseJobsSkipped;
	/** Employees the server tried to change and couldn't. */
	failed: number[];
}

export interface EmploymentMappingDeleteResult {
	key: string;
	revertedTo: EmploymentTypeMapping | null;
	/** Only set on mutation responses (DAT-386): the employee re-sync scheduled by the mapping change. */
	employeeSync?: SyncScheduledResponse | null;
}

export interface TimeOffMappingDeleteResult {
	key: string;
	revertedTo: TimeOffTypeMapping | null;
}

export interface EmployeeStatusMappingDeleteResult {
	key: string;
	revertedTo: EmployeeStatusMapping | null;
}

export enum HrisIgnoreCategory {
	Role = "role",
	EmploymentType = "employment_type",
	TimeOffType = "time_off_type",
	PunchEventType = "punch_event_type",
	EarningCode = "earning_code",
}

export interface HrisIgnoredValueRow {
	id: number;
	departmentId: string | null;
	source: ExternalSource | null;
	category: HrisIgnoreCategory;
	rawValue: string;
}

export interface Nurse {
	id: number;
	userId?: string;
	firstName: string;
	lastName: string;
	employmentType: EmploymentType;
	extraShifts: boolean;
	departments: Array<string>;
	email: string;
	qualifications: Array<Qualification>;
	jobPercentage?: number;
	hourlyWage?: number;
	gender?: string;
	reliabilityScore: number;
	nursingStartingDate?: DateTime;
	dateOfBirth?: DateTime;
	phoneNumber?: string;
	maritalStatus?: string;
	country: string;
	city?: string;
	street?: string;
	zipCode?: string;
	idNumber?: string;
	role: NurseFullRole;
	status?: string;
	allowedShifts: number;
	notSchedule: number[];
	profileImage?: string;
	shiftsNotWorking: any;
	employeeWeeklyPreferences: WeeklyPreference[];
	employeeSpecificPreferences: SpecificPreference[];
	canBeReliefCharge: boolean;
	weekendShiftFlexibility: boolean;
	shiftConstraints?: WeeklyConstraint;
	isOnSchedule: boolean;
	isShiftManager: boolean;
	shiftType: ShiftType;
	trainingStatus: NurseTrainingStatus;
	avoidDates?: {
		date: DateTime;
		reason: string;
		type: string;
	}[];
	noWeeklyPreferences?: boolean;
	isGlobalSalary?: boolean;
	/** @deprecated Use `baseWages` instead — the date-specific source of truth. Resolve via getBaseWageByTime. */
	wage?: number;
	/** @deprecated Use `baseWages` instead — the date-specific source of truth. Resolve via getBaseWageByTime. */
	wageType?: WageType | null;
	prnShiftsInCycle?: number | null;
	prnWeekendShiftsInCycle?: number | null;
	scheduledDeactivationDate?: DateTime | null;
	isUserInit?: boolean;
	baseWages: BaseWage[];
	lastLoginTime?: DateTime;
	/** `null` when the nurse never punched here; absent on payloads predating punch history. */
	lastPunchTime?: DateTime | null;
	punchHistoryLoaded?: boolean;
	agencyName?: string;
	// Per-nurse PPD exclusion flag — used as fallback when job_management_enabled FF is OFF.
	excludeFromPpd?: boolean;
	ignoreFromSchedule?: boolean;
	createdAt?: DateTime;
	updatedAt?: DateTime;
	union?: string | null;
	doNotDisturbCalling?: boolean;
	/** Per-employee opt-out for auto-approve; default true. When false, this nurse's pickups always go to manual review. */
	autoApproveEligible?: boolean;
	homeDepartmentId: string | undefined;
	jobIds?: string[];
	jobs?: Job[];
	departmentStatuses?: Record<string, boolean>;
	departmentScheduledDeactivationDates?: Record<string, string | null>;
	departmentEmployeeStatuses?: Record<string, EffectiveEmployeeStatus>;
	departmentStatusDetails?: Record<string, DepartmentStatusDetail>;
	externalEmployees?: ExternalEmployee[];
}

/**
 * Computed per-department employee status (never stored as-is; mirrors the backend's
 * `EffectiveEmployeeStatus`). Resolved server-side via `derive_effective_status`.
 */
export enum EffectiveEmployeeStatus {
	Active = "Active",
	Inactive = "Inactive",
	OnLeave = "OnLeave",
	Suspended = "Suspended",
}

/** Stored per-department status values the admin can set (subset of effective statuses). */
export enum SettableEmployeeStatus {
	OnLeave = "OnLeave",
	Suspended = "Suspended",
	Inactive = "Inactive",
}

/** Raw per-department status detail row (dates are ISO strings as sent by the backend). */
export interface DepartmentStatusDetail {
	/** Raw stored status; lets the client derive the effective status at any date, not just today. */
	status: EffectiveEmployeeStatus | null;
	statusReason: string | null;
	statusStartDate: string | null;
	statusEndDate: string | null;
}

export interface NurseWithExternalData extends Nurse {
	departmentDisplayNames?: Record<string, string>;
	agencyProfileUrls?: string[];
}

export type EditableNurse = Omit<Nurse, "role"> & { role?: NurseFullRole };

export interface Agency {
	name: string;
}

export interface AgencyWage {
	id?: string;
	departmentId: string;
	agencyName: string;
	role: string; // Can be actual role or special like "T&T Terms", "T&T Bill"
	/** `0` is a real zero rate; `null` is a configured-but-blank cell. The two are not interchangeable. */
	wage?: number | null;
	createdAt?: string;
	updatedAt?: string;
}

/** One cell of the Agency Wages grid. `wage: 0` is a real zero rate; `null` is a blank cell. */
export interface AgencyWageRoleEntry {
	role: string;
	wage?: number | null;
}

/** One Save of the Agency Wages grid: an agency's whole role→wage set, sent as a single request. */
export interface AgencyWageBulkUpsertRequest {
	departmentId: string;
	agencyName: string;
	entries: AgencyWageRoleEntry[];
}

export interface AgencyWageBulkUpsertResponse {
	/** The agency's FULL post-save row set, every row carrying its id. */
	wages: AgencyWage[];
	updatedNurseCount: number;
}

export interface NurseAbsentToSubmit {
	id: number;
	absentNurseId: number;
	replacementNurseId?: number;
	startDate: string;
	endDate: string;
	reason?: string;
	departmentId: string;
}

export type NurseProfileToSubmit = Omit<Nurse, "dateOfBirth" | "nursingStartingDate" | "nurseAbsents" | "scheduledDeactivationDate"> & {
	dateOfBirth?: string;
	nursingStartingDate?: string;
	scheduledDeactivationDate?: string;
	nurseAbsents?: Array<NurseAbsentToSubmit>;
};

export interface Assignment {
	id: string;
	startDate: DateTime;
	endDate: DateTime;
}

export interface NurseAbsent {
	id: number;
	absentNurseId: number;
	replacementNurseId?: number;
	startDate: DateTime;
	endDate: DateTime;
	reason?: string;
	departmentId: string;
	requestIds: string[];
	postShifts: boolean;
}

export interface Feedback {
	shiftDate: DateTime;
	shiftType: string;
	alignment: string;
	comment: string;
	filled: boolean;
}

export type NurseOvertimeShift = {
	startTime: string;
	endTime: string;
	departmentDisplayName: string;
};

export enum OvertimeBadgeType {
	Weekly = "weekly",
	Daily = "daily",
	/** Nth consecutive day worked. Its own badge: a 4h shift labelled "Daily OT" is untrue. */
	Consecutive = "consecutive",
	Approaching = "approaching",
}

export type NurseOvertimeInfo = {
	type: OvertimeBadgeType;
	hours: number;
	shifts: NurseOvertimeShift[];
};

/** nurseId -> ISO date string -> OT info */
export type NurseOvertimeMap = Record<number, Record<string, NurseOvertimeInfo>>;

export type RecurringOvertimeBadge = {
	type: OvertimeBadgeType;
	weeklyHours: number;
	dailyHours: number;
};

/** nurseId -> OT-week-start ISO date -> badge derived from recurring shifts alone. */
export type RecurringOvertimeMap = Record<number, Record<string, RecurringOvertimeBadge>>;

export type DepartmentDataResponse = {
	weeks: WeekData[];
	patients: Patient[];
	units: string[];
	floors: string[];
	staff_schedule_display_until_date: DateTime | null;
};

/** One range of `department_data`'s weeks, holding only one nurse's scheduled and actual shifts. */
export type NurseScheduleResponse = {
	weeks: WeekData[];
	// The first and last day the department has a schedule for; both null when it has none yet.
	scheduleStartDate: DateTime | null;
	scheduleEndDate: DateTime | null;
};

/** One shift type's numbers on one schedule day, from its staffing row. They exist even when nobody works that shift. */
export interface ScheduleShiftTypeNumbers {
	census_low: number | null;
	census_high: number | null;
	score: number | null;
	staffNo: number | null;
	isPlusActive: boolean | null;
	isMinusActive: boolean | null;
	patientDifficulty: string | null;
	censusDifficulty: string | null;
	staffDifficulty: string | null;
	changeAlert: boolean | null;
	certainty: number;
	shiftEvents: ShiftEvents | null;
}

/** One schedule day of `GET /departments/{id}/schedule`. The census fields keep `department_data`'s day names. */
export interface ScheduleDay {
	date: DateTime;
	census_low: number | null;
	census_high: number | null;
	census_override: CensusOverride | null;
	census_grouping: CensusGrouping | null;
	byShiftType: Record<string, ScheduleShiftTypeNumbers>;
	scheduled: NurseInShift[];
	actual: ActualNurse[];
	// Every time-off or preference row overlapping this day; a several-day vacation is listed on each day.
	timeOff: SpecificPreference[];
}

/** Someone who works a shift or has time off in the returned days. */
export interface SchedulePerson {
	id: number;
	firstName: string;
	lastName: string;
	role: NurseFullRole | null;
	employmentType: EmploymentType;
	union: string | null;
	jobIds: string[];
	agencyName: string | null;
	// Historic weeks keep people who have since left the department.
	activeInDepartment: boolean;
}

export type DepartmentScheduleResponse = {
	days: ScheduleDay[];
	nurses: SchedulePerson[];
	// The first and last day the department has a schedule for; both null when it has none yet.
	scheduleStartDate: DateTime | null;
	scheduleEndDate: DateTime | null;
};

/** One cached schedule week: its seven days and each person in them once. */
export type ScheduleWeek = {
	days: ScheduleDay[];
	nurses: Record<number, SchedulePerson>;
	scheduleStartDate: DateTime | null;
	scheduleEndDate: DateTime | null;
};

export interface HospitalData {
	patients: Array<Patient>;
	nurses: Array<Nurse>;
	weeks: Array<WeekData>;
	procedures: Record<string, Record<string, Array<ScheduledProcedure>>>;
	feedbacks: Array<Feedback>;
	departments?: Array<{
		department_id: string;
		name: string;
		display_name: string;
	}>;
	units?: string[];
	floors?: string[];
}

export interface DayTimeMapping {
	[dayTimeKey: string]: number;
}

export interface AccuracyMapping {
	[timeType: string]: number;
}

export interface ShiftCounts {
	atTarget: string;
	understaffed: string;
	overstaffed: string;
	totalNumber: number;
	atTargetNumber: number;
	understaffedNumber: number;
	overstaffedNumber: number;
}

export interface ScheduledProcedure {
	activityDate: DateTime;
	birthDate: DateTime;
	surgeon: string;
	duration: number;
	status: string;
	activity: string;
	department?: string;
	patientName?: string;
	gender: string;
	wls: number;
	notes: string;
	mainCalcDepartment: string;
	mainCalcDepartmentChance: number;
	activities: string[];
	departmentArrivalTimeMin: DateTime;
	departmentArrivalTimeMax: DateTime;
	hashPatientId: string;
}

export interface HourlyCensus {
	hour: number;
	census: number;
}

export interface CensusDataPoint {
	date: DateTime;
	strDate: string;
	census: number;
	admissions: number;
	futureCensus?: number;
	historicalCensus?: number;
	futureAdmissions?: number;
	historicalAdmissions?: number;
	census_avg_low?: number;
	census_avg_high?: number;
	admissionsAvgLow?: number;
	admissionsAvgHigh?: number;
}

export interface PPNDataPoint {
	date: DateTime;
	strDate: string;
	ppn: number;
	historicalPPN?: number;
	futurePPN?: number;
	target_ppn_low: number;
	target_ppn_high: number;
}

export interface DepartmentSummary {
	name: string;
	display_name: string;
	target_ppn: number;
	actual_ppn: number;
	previousDay: number;
	admissions: number;
	discharges: number;
	isolation_covid: number;
	isolation_non_covid: number;
	total_beds: number;
	census: number;
	money: number;
	date: DateTime;
	shift_type: string;
	hourly_census: Array<HourlyCensus>;
	hourly_ppn: Array<HourlyPPN>;
	wls: number;
	nurses: number;
	census_graph: Array<CensusDataPoint>;
	ppn_graph: Array<PPNDataPoint>;
}

export enum Preferences {
	Avoid = "avoid",
	Prefer = "prefer",
}

export type ShiftChange = {
	nurse: NurseInShift;
	shiftIndex: number;
	shiftType: string;
	weekIndex: number;
	dayIndex: number;
	date: DateTime;
	departmentId: string;
	otherNurseShift?: NurseInShift;
	changeType: ChangeType;
	cost?: number;
};

export enum ChangeType {
	Insert = "insert",
	Swap = "swap",
	Delete = "delete",
	Update = "update",
	Move = "move",
}

export enum AbsenceType {
	NO_SHOW = "No Show No Call",
	OVERSTAFFED = "Overstaffed",
	LOW_CENSUS = "Low Census",
	CALL_IN = "Call-In",
	SICK = "Sick",
	CHILD_CARE = "Child Care",
	FAMILY_OBLIGATIONS = "Family Obligations",
	APPOINTMENT = "Appointment",
	CANCEL = "Cancel",
	SUSPENSION = "Suspension",
	BEREAVEMENT = "Bereavement",
	OTHER = "Other",
}

// Mirrors backend CallOffReplacementOutcome — the mode-aware call-off gate decides if a removed shift needs a replacement search.
export enum CallOffReplacementOutcome {
	SEARCHING = "searching",
	SUFFICIENT_STAFF = "sufficient_staff",
	NOT_STARTED = "not_started",
	NO_LOCATION_MATCH = "no_location_match",
}

// Mirrors backend CallOffGateBasis — WHAT the gate's outcome rests on, so the dialog never names a calculation it didn't make.
export enum CallOffGateBasis {
	HPPD = "hppd",
	RATIO = "ratio",
	// The department searches on every call-off (gate flag off) — no staffing calculation was made.
	ALWAYS_SEARCH = "always_search",
	// The calculation failed or its data is missing; the gate fails open to a search.
	UNAVAILABLE = "unavailable",
}

export interface CallOffReplacementGateResponse {
	outcome: CallOffReplacementOutcome;
	basis: CallOffGateBasis;
	should_find_replacement: boolean;
}

export interface DayAmount {
	amount: number;
	amount_required: number;
}

export interface DepartmentLocation {
	department_id: string;
	location: string;
	roles: (NurseFullRole | "all")[];
	is_charge: boolean;
	shift_type: string;
	id: string;
	section: string;
	location_short_name: string;
	days: string[];
	shift_start: string;
	shift_end: string;
	week_numbers: number[];
	dayAmounts: Record<string, DayAmount>;
	/** Computed by backend: max amount across all days */
	amount: number;
	/** Computed by backend: max amount_required across all days */
	amountRequired: number;
	departmentType?: DepartmentType;
	unit?: string;
	floor?: string;
	address?: string;
	ratioLadder?: RatioLadder[];
	jobId?: string;
	rooms?: ScheduleTemplateRoom[];
	/** Admin flag: staff here are tied to the location (e.g. a 1:1 sitter) and excluded from the moveable ratio pool */
	cantMove?: boolean;
}

export interface ScheduleTemplateRoom {
	id: string;
	schedule_template_id: string;
	room_name: string;
}

export interface RatioLadder {
	id: string;
	location_id: string;
	roles: NurseRole[];
	censusStart: number;
	censusEnd: number;
	amount: number;
	days?: string[];
	createdAt?: string;
	updatedAt?: string;
	jobId?: string;
}

export type GroupedDepartmentLocation = {
	allRoles: (NurseFullRole | "all")[];
	locationName: string;
	locations: DepartmentLocation[];
	earliestLocationStartTime: DateTime;
	latestLocationEndTime: DateTime;
};

export enum IgnoreShiftType {
	NotExisting = "NotExisting",
	MultipleMatching = "MultipleMatching",
	LongShift = "LongShift",
	EmptyShift = "EmptyShift",
}

export interface IgnoredExternalShift {
	name: string;
	startTime: string;
	endTime: string;
	reason: IgnoreShiftType;
}

export interface ExternalShift {
	id?: number;
	externalEmployeeId: string;
	nurseId: number;
	departmentId: string;
	source: string;
	startTime: string;
	endTime: string;
	missingPunchIn: boolean;
	missingPunchOut: boolean;
	breaks?: any[];
	shiftId?: string;
	createdAt?: string;
	updatedAt?: string;
}

export interface ExternalShiftDiff {
	toInsert: ExternalShift[];
	toDelete: ExternalShift[];
	toIgnore: IgnoredExternalShift[];
}

export enum ExternalSource {
	Paycom = "paycom",
	PaycomSftp = "paycom_sftp",
	Empeon = "empeon",
	Hostdime = "hostdime",
	Workday = "workday",
	Webclock = "webclock",
	Excelforce = "excelforce",
	Sbv = "sbv",
	Clipboard = "clipboard",
	Paycor = "paycor",
	Isolved = "isolved",
	UkgReady = "ukg_ready",
	MatrixHr = "matrix_hr",
	TimePro = "time_pro",
	Aod = "aod",
	Chs = "chs",
	Flcl = "flcl",
	Adp = "adp",
	Demo = "demo",
}

/** Direction of a raw punch. Mirrors the backend `PunchEventType` ("in"/"out"); null when the raw
 * event type didn't map to a direction (e.g. a dangling/unknown punch). */
export enum PunchEventType {
	In = "in",
	Out = "out",
}

/**
 * DAT-221: one raw HRIS punch event behind a shift — actual or scheduled (support-only read model).
 * Mirrors the backend `ShiftPunchEvent` (camelCase) and carries every persisted PunchEvent field.
 * Dangling/missing punches can carry a null `eventType` and only one of in/out, so nullable fields
 * must be rendered gracefully.
 */
export interface ShiftPunchEvent {
	id: string; // shift_punch PK — stable React key
	eventTime: string | null; // ISO datetime, formatted client-side via Luxon
	eventType: PunchEventType | null;
	rawEventType: string | null;
	source: ExternalSource;
	job: string | null;
	hrisDepartmentId: string | null;
	externalEmployeeId: string | null;
	rawEarningCodes: string[] | null;
	earnHours: number | null;
	priority: number | null;
	departmentName: string | null;
	earnCode: string | null;
	agencyName: string | null;
	distJobCode: string | null;
	pairToken: string | null;
	syncRunId: string | null; // DAT-629: lineage link to the sync_run that last wrote this punch
}

export type HrisIntegrationType = "employees" | "shifts" | "time_off";

export interface HrisIntegration {
	id: string;
	department_id: string;
	hris_department_id: string;
	external_network_id?: string;
	source: ExternalSource;
	type: HrisIntegrationType;
	folder?: string;
	file?: string;
	active: boolean;
	created_at?: string;
	updated_at?: string;
}

export interface HrisIntegrationInput {
	id?: string;
	hris_department_id: string;
	external_network_id?: string;
	source: ExternalSource;
	type: HrisIntegrationType;
	folder?: string;
	file?: string;
	active: boolean;
}

export interface IntegrationSummarySection<T> {
	total: number;
	truncated: boolean;
	rows: T[];
}

// The axios interceptor (`convertIsoDates`) turns every ISO date/datetime string in a response into a
// Luxon `DateTime`, so date fields on these summaries are typed as what actually arrives.
export interface IntegrationSummaryHeader {
	windowStart: DateTime;
	windowEnd: DateTime;
	integrationConfigured: boolean;
	hasEverSynced: boolean;
	lastSyncAt: DateTime | null;
}

export interface CreatedEmployeeSummaryRow {
	externalEmployeeId: string;
	nurseId: number;
	fullName: string;
	role: string;
	job: string | null;
	roleString: string | null;
	employmentType: string;
	source: ExternalSource;
	createdAt: string;
	active: boolean;
}

export interface DuplicateNurseDetail {
	nurseId: number;
	fullName: string;
	departmentIds: string[];
	departmentNames: string[];
	active: boolean;
	actualShiftCount: number;
	scheduledShiftCount: number;
	hasExternalEmployee: boolean;
	hasEmail: boolean;
	hasPhone: boolean;
}

export interface DuplicateNurseGroup {
	normalizedName: string;
	nurses: DuplicateNurseDetail[];
}

export interface UnmappedRoleStringSummaryRow {
	roleString: string;
	employeeCount: number;
	sampleEmployeeNames: string[];
	internalDepartments: string[];
}

export interface UnmappedTimeOffTypeSummaryRow {
	rawString: string;
	countLast60: number;
}

export interface IntegrationEmployeesSummary extends IntegrationSummaryHeader {
	createdCount: number;
	activeCreatedCount: number;
	createdEmployees: IntegrationSummarySection<CreatedEmployeeSummaryRow>;
	activeHrisEmployeesTotal: number;
	unmappedRoleStrings: IntegrationSummarySection<UnmappedRoleStringSummaryRow>;
	nursesWithoutIntegration: IntegrationSummarySection<DuplicateNurseDetail>;
	duplicateNursesSameDepartment: IntegrationSummarySection<DuplicateNurseGroup>;
	duplicateNursesSameNetwork: IntegrationSummarySection<DuplicateNurseGroup>;
}

export interface IntegrationTimeOffSummary extends IntegrationSummaryHeader {
	createdCount: number;
	linkedToScheduleCount: number;
	paidCount: number;
	unpaidCount: number;
	createdByType: Record<string, number>;
	unmappedTimeOffTypes: IntegrationSummarySection<UnmappedTimeOffTypeSummaryRow>;
	manualTimeOffLast30Count: number;
	manualTimeOffBySource: Record<string, number>;
	manualTimeOffByEmployee: IntegrationSummarySection<{
		nurseId: number;
		fullName: string;
		count: number;
	}>;
}

export interface ShiftsDailyPoint {
	day: DateTime;
	scheduledCount: number;
	actualCount: number;
	scheduledWithoutActualCount: number;
	actualWithoutScheduleCount: number;
}

export interface ShiftMismatchEmployeeRow {
	nurseId: number;
	fullName: string;
	jobName: string | null;
	externalRole: string | null;
	payType: WageType | null;
	actualWithoutScheduleCount: number;
	scheduledWithoutActualCount: number;
	actualCount: number;
	scheduledCount: number;
	/** Ever, in this department — not limited to the summary window. */
	hasAnySchedule: boolean;
	/** Ever, in this department, excluding actuals the sync copied from the schedule. */
	hasAnyActual: boolean;
}

export type ShiftMismatchType = "no_schedule" | "no_actual";

export interface ShiftMismatchRow {
	shiftId: string;
	nurseId: number;
	fullName: string;
	jobName: string | null;
	mismatchType: ShiftMismatchType;
	day: DateTime;
	startTime: DateTime;
	endTime: DateTime;
	hours: number;
}

export interface IntegrationShiftsSummary extends IntegrationSummaryHeader {
	startDate: DateTime;
	endDate: DateTime;
	scheduledCount: number;
	actualCount: number;
	scheduledWithoutActualCount: number;
	actualWithoutScheduleCount: number;
	excludedScheduledCount: number;
	copiedActualCount: number;
	/** 0..1; null when there are no scheduled shifts to divide by. */
	noPunchesRatio: number | null;
	/** 0..1; null when there are no actual shifts to divide by. */
	noSchedulesRatio: number | null;
	scheduledHours: number;
	actualHours: number;
	daily: ShiftsDailyPoint[];
	employees: IntegrationSummarySection<ShiftMismatchEmployeeRow>;
	mismatches: IntegrationSummarySection<ShiftMismatchRow>;
}

export interface MissingRecurringPatternRow {
	nurseId: number;
	fullName: string;
	jobName: string | null;
	week: 1 | 2;
	/** Sun=0 … Sat=6. */
	weekday: number;
	/** Department-local "HH:mm" — a plain string, not converted to a DateTime by the interceptor. */
	startTime: string;
	endTime: string;
	location: string | null;
	occurrences: number;
	possible: number;
	/** 0..1 */
	confidence: number;
}

export interface MissingRecurringPatternsSummary {
	rangeStart: DateTime;
	rangeEnd: DateTime;
	/** 0..1 */
	minConfidence: number;
	weekAnchorSet: boolean;
	patterns: IntegrationSummarySection<MissingRecurringPatternRow>;
}

export enum EmrType {
	PCC = "pcc",
	Yardi = "yardi",
	Matrix = "matrix",
	Matrix360 = "matrix_360",
	ReliableHealth = "reliable_health",
}

export type EmrIntegrationInput = {
	type: EmrType;
	/** Facility-level id: Facility ID (PCC/Matrix/Matrix360/ReliableHealth) or Facility UUID (Yardi). */
	facilityId?: string;
	/** Org/tenant-level id: Org UUID (PCC), Tenant ID (Yardi), Facility OID (Matrix 360); unused for Matrix/RH. */
	orgId?: string;
	/** When false the EMR is disabled — census falls back to manual (like disabling an HRIS feed). */
	active: boolean;
};

export interface EmrIntegration {
	id: string;
	department_id: string;
	type: EmrType;
	facilityId?: string;
	orgId?: string;
	active: boolean;
	created_at?: string;
	updated_at?: string;
}

/**
 * One facility our Nursa account can post to, as Nursa lists it.
 *
 * Mirrors `backend/services/nursa_facilities.py` — the field names are that model's camelCase aliases.
 */
export interface NursaFacility {
	facilityId: string;
	/** `null` when Nursa's row carries no name — the picker then falls back to `description`, then the id. */
	name: string | null;
	/**
	 * A human descriptor assembled by the backend from the row's OWN location/system fields — a health
	 * system plus a city, a county, a street. `null` when the row carries nothing usable.
	 *
	 * Exists because Nursa's sandbox returns `name: ""` on every row (its `sampleRowKeys` diagnostic
	 * confirmed `name` IS the key we read, and Nursa's own docs mark it required), which left the picker
	 * showing a column of UUIDs. This is what an operator can actually recognise in that state. It is a
	 * FALLBACK, never a second line: a facility that has a name shows its name.
	 */
	description: string | null;
	/** The department already mapped to this facility; `null` when nobody has claimed it. */
	takenByDepartmentId: string | null;
}

/**
 * Why the facility list looks the way it does. Mirrors `backend/services/nursa_facilities.py`.
 *
 * Only `ok` means Nursa answered with a usable roster. The other three are *reported* rather than
 * thrown, which is the whole point of the discriminator: it lets the picker tell "Nursa is down"
 * apart from "our account has no facilities", and keep manual entry open in either case.
 */
export type NursaFacilitiesStatus = "ok" | "no_facilities" | "credentials_missing" | "nursa_error";

/** The facilities this department could be mapped to, for the Nursa Facility ID picker. */
export interface NursaFacilitiesResponse {
	status: NursaFacilitiesStatus;
	facilities: NursaFacility[];
	/**
	 * Rows Nursa returned that we could not read (no usable id). They are absent from `facilities`.
	 *
	 * Genuinely unreadable rows only — a roster the backend could not finish reading is reported by
	 * `truncated`, not folded in here, so this number never overstates what Nursa sent us.
	 */
	unusableRowCount: number;
	/**
	 * The roster fetch did NOT complete, so `facilities` is a PREFIX of reality.
	 *
	 * Load-bearing next to `status: "ok"`: a facility missing from a truncated list is missing because
	 * we stopped reading, not because Nursa doesn't have it — which is why the picker says so rather
	 * than letting the operator conclude their facility isn't on our account.
	 */
	truncated: boolean;
	/**
	 * Diagnostic ONLY — the key names seen on Nursa's rows when not one facility carried a name.
	 *
	 * Deliberately never rendered: raw upstream key names are support/debug material, not something an
	 * operator can act on. It exists because "every row came back nameless" is indistinguishable, from
	 * the UI, between "Nursa sends no names" and "we read the wrong key" — and this is how support tells
	 * those apart from a response body without a backend deploy.
	 */
	sampleRowKeys: string[];
}

/**
 * One page of Nursa's public facility DIRECTORY, searched by name.
 *
 * A different population from `NursaFacilitiesResponse`: that one lists the facilities our API
 * client already has a connection to — three, in production — so a building being onboarded is by
 * definition absent from it. This one reaches every facility registered with Nursa.
 *
 * It shares the status vocabulary and nothing else. `unusableRowCount` and `sampleRowKeys` are
 * absent because they exist to explain a dropdown of bare UUIDs, and directory rows carry names.
 */
export interface NursaFacilitySearchResponse {
	status: NursaFacilitiesStatus;
	facilities: NursaFacility[];
	/**
	 * Nursa filled the page, so another one MAY exist.
	 *
	 * Not "does exist": their `meta` carries no total, so a full last page is indistinguishable from
	 * a full page with more behind it. Treat it as "narrow your search", never as a page count.
	 */
	hasMore: boolean;
}

/**
 * Whether a department's Nursa integration is actually live, or only configured.
 *
 * Three things must hold before shifts flow — an ID is saved, our API client has a CONNECTION to
 * that facility, and the facility is on our shared webhook registration — and the last two fail
 * silently. A department can hold a valid ID, a live switch and an "On" chip and still be inert.
 *
 * **`null` is a third answer, not a missing one.** It means "we could not ask" — Nursa unreachable,
 * credentials absent. Rendering it as `false` would paint a healthy department as broken during a
 * vendor outage, and invite an operator to "fix" a working mapping.
 */
export interface NursaReadiness {
	status: NursaFacilitiesStatus;
	facilityId: string | null;
	/** Nursa will act on behalf of this facility for our account. `null` = unknown. */
	connected: boolean | null;
	/** Anything Nursa does will actually reach us. `null` = unknown. */
	webhookSubscribed: boolean | null;
}

/** Outcome of adding or removing a facility on our Nursa webhook registration. */
export interface NursaWebhookSubscriptionResult {
	/** `already_subscribed` / `not_subscribed` mean the registration already matched — no write was made. */
	status: "added" | "already_subscribed" | "removed" | "not_subscribed";
	facility_id: string;
	/** How many facilities the registration fires for after the call. A count, not the roster: the
	    list spans every customer we integrate with and the UI never needed more than the number. */
	subscribed_facility_count: number;
}

/**
 * What turning one agency integration off would touch, for the confirmation dialog (DAT-885).
 * The twin of `DeactivationImpact` in `backend/services/agency_deactivation.py:69`.
 *
 * Every figure except `inboundEventsRecent` is FUTURE-only — the dialog says what is about to change,
 * and nothing in the past can be changed by this. `inboundEventsRecent` is the deliberate exception:
 * it answers a different question ("is this integration actually alive right now?") over the last
 * `inboundEventLookbackDays` days. Display only — the mutation re-derives its own un-post set.
 */
export interface AgencyDeactivationImpact {
	source: AgencySource;
	/** Open seats still advertised at the vendor, counted as REQUESTS (a three-nurse shift is one). */
	openShiftsPosted: number;
	/** Agency clinicians already booked onto a future shift. Deactivation does NOT cancel them. */
	scheduledAgencyNurses: number;
	/** Applications sitting in `pending` — the 2026-09-17 incident, named. */
	pendingResponses: number;
	inboundEventsRecent: number;
	/** The window `inboundEventsRecent` was counted over. Render this; never hardcode a number. */
	inboundEventLookbackDays: number;
}

/**
 * The per-agency switch both toggle responses echo back is the SAME wire shape `/user_info` carries
 * (`backend/models/entities/agency.py:57`), so it is imported from where it is already declared
 * rather than restated here. A second identical interface would be two descriptions of one shape,
 * free to drift the moment the backend adds a field to one of them.
 *
 * Type-only, so nothing is added to the runtime module graph — `agencyIntegrations.ts` imports
 * `AgencySource` from here as a VALUE, and this edge must stay erasable for that not to become a
 * cycle.
 */
export type { AgencyIntegrationState };

/** The outcome of switching one integration OFF. */
export interface AgencyDeactivationResult {
	transition: "deactivated";
	/**
	 * The switch's new state — or **null when the row vanished mid-transition** (DAT-885 review H3).
	 *
	 * Null is a success, not an error. If another operator unmaps the facility after the un-post has
	 * already run, the retractions have physically reached the vendor over HTTP and cannot be taken
	 * back; answering 409 there would roll our local `status=deleted` writes back and leave our rows
	 * claiming `posted` for postings the vendor no longer has. So the backend commits and reports
	 * null. (A row missing at ENTRY is different — nothing ran, and that is still a 409.)
	 *
	 * Semantically null means the department now has NO agency mapping at all: the switch is gone
	 * rather than off. Nothing needs to act on it here — the `updateUserInfo()` refresh drops the
	 * entry from `agencyIntegrations`, which the availability helpers read as "no facility id" and the
	 * toggle renders as `unmapped`. It is typed nullable so the next reader cannot dereference it
	 * without deciding what that case means.
	 */
	integration: AgencyIntegrationState | null;
	/** How many open-shift requests were actually retracted at the vendor. */
	unposted: number;
	/** Non-fatal per-request vendor failures. The switch is off regardless. */
	failures: AgencyPostingFailure[];
	/** True when more requests matched than the server's batch limit allowed — postings are still live. */
	unpostTruncated: boolean;
}

/** The outcome of switching one integration back ON. */
export interface AgencyActivationResult {
	transition: "activated";
	integration: AgencyIntegrationState;
	/** Nursa only — Clipboard has no webhook to subscribe and reports null rather than a fake success. */
	webhook?: NursaWebhookSubscriptionResult | null;
	/** Why the webhook subscribe failed, when it did. `active` is still true in that case. */
	webhookError?: string | null;
}

/**
 * Branch on `transition`, NEVER on shape. Both arms carry an `integration` and the backend's
 * `extra="ignore"` would let one validate as the other, silently dropping the arm-specific fields.
 */
export type AgencyIntegrationToggleResult = AgencyActivationResult | AgencyDeactivationResult;

/** Result of queueing an on-demand PCC census recalculation for a date range. */
export interface RecalculateCensusResponse {
	enqueued: boolean;
	queueId: number | null;
	/** True when this exact department + window was already queued — no second run was started. */
	alreadyPending: boolean;
	startDate: string;
	endDate: string;
}

export interface ExternalEmployee {
	id: string;
	pbj_id?: string | null;
	nurse_id?: number | null;
	source: ExternalSource;
	department_id: string;
	sync_key: string;
	first_name: string;
	last_name: string;
	wage_type: string;
	wage: number;
	employment_type: string;
	role: string;
	email?: string | null;
	phone_number?: string | null;
	hire_date?: string | null;
	birth_date?: string | null;
	termination_date?: string | null;
	country?: string | null;
	city?: string | null;
	street?: string | null;
	house_number?: number | null;
	zipcode?: string | null;
	agency_name?: string | null;
	ref_id?: string | null;
	union_code?: string | null;
	raw_role?: string | null;
	/** The source's raw status token (e.g. "A", "FMLA", "Terminated") that `active` was resolved from. Null when the source reports no status. */
	raw_employee_status?: string | null;
	/** The source's raw employment-type string (e.g. "1099", "FT", "Per Diem") that `employment_type` was resolved from. */
	raw_employment_type?: string | null;
	/** The source's raw primary email. Blank here next to a populated `email` means the sync fell back to the secondary address. */
	raw_email?: string | null;
	/** The source's raw primary phone number, before normalization. */
	raw_phone_number?: string | null;
	/** The source's raw wage string, which can carry currency characters (e.g. "$21.70") that the numeric `wage` cannot. */
	raw_wage?: string | null;
	active: boolean;
	created_at?: string;
	updated_at?: string;
}

export interface ExternalEmployeeDiff {
	to_insert: ExternalEmployee[];
	to_update: ExternalEmployee[];
	to_delete: ExternalEmployee[];
}

export interface ExternalTimeOff {
	id?: number;
	department_id?: string;
	nurse_id?: number;
	external_employee_id?: string;
	source?: ExternalSource;
	sync_key?: string;
	start_date?: string;
	end_date?: string;
	type?: string;
	hours?: number;
	reason?: string;
	created_at?: string;
	updated_at?: string;
}

export interface ExternalTimeOffDiff {
	to_insert: ExternalTimeOff[];
	to_update: ExternalTimeOff[];
	to_delete: ExternalTimeOff[];
}

export interface SyncScheduledResponse {
	task_names: string[];
	already_running_task_names: string[];
	scheduled_for: string;
}

export interface NurseActualHours {
	start_time: DateTime;
	end_time: DateTime;
}

export type SortDirection = "asc" | "desc" | null;

export interface SortConfig {
	key: keyof NurseInShift;
	direction: SortDirection;
}

/**
 * What a shift's base wage is — which line the wage breakdown's composition starts from. Mirrors
 * `PayBasis` in `backend/models/cost.py`; only one applies per shift.
 */
export type PayBasis = "employee" | "agency" | "salaried" | "estimated" | "trainee" | "orientation" | "override";

/*
 * DAT-906 · A rule's scope as it stood when the row was priced (`HoursRateScope` / `OvertimeScope` /
 * `BreakScope` in `backend/models/cost.py`). Plain strings rather than enums: a stored breakdown must stay
 * readable after a role or employment type is renamed. Every field arrives; an empty list / null means
 * the rule does not restrict on it.
 */
type RuleAudienceScope = {
	roles: string[];
	jobIds: string[];
	employmentTypes: string[];
	union: string[];
	isUnion: boolean | null;
};

export type HoursRateScope = RuleAudienceScope & {
	/** "HH:MM:SS", or null for all day. */
	startTime: string | null;
	endTime: string | null;
	weekend: boolean | null;
	isCharge: boolean | null;
	trainingStatuses: string[];
	agency: string[];
};

export type OvertimeScope = RuleAudienceScope & {
	consecutiveWindow: string | null;
	consecutiveDaysFrom: number | null;
	consecutiveDaysTo: number | null;
};

export type BreakScope = RuleAudienceScope;

/*
 * One line of a breakdown row's rate. For every row: `baseExact + Σ amountPerHour === rate` (4dp).
 * `name` is the rule's name exactly as configured when the row was priced; `ruleId` its id then — ids
 * change on every Wage Settings save, so a link falls back to the name when the id is gone.
 */
export type HoursRateContribution = {
	kind: "hours_rate";
	ruleId: string | null;
	name: string;
	scope: HoursRateScope;
	valueType: RateHoursBonusType;
	/** A flat $/h, or a percentage stored as a total multiplier (110 = +10%). */
	value: number;
	amountPerHour: number;
};

export type OvertimeContribution = {
	kind: "overtime";
	ruleId: string | null;
	name: string;
	scope: OvertimeScope;
	valueType: RateHoursBonusType;
	value: number;
	amountPerHour: number;
	otKind: OvertimeKind;
	/** The tier's hours limit: this band is paid because its counter passed it. */
	thresholdHours: number;
	/** Hours already on the counter when the band starts — why the threshold was already passed. */
	hoursBefore: number;
	/** The OT week a weekly counter ran over; null for daily and consecutive bands. */
	weekStart: DateTime | null;
	/**
	 * When the week's hours reached the limit, if before this shift began — the day the cause lies on. Null
	 * for daily and consecutive bands, for a limit crossed during the shift, and on older stored lines.
	 */
	limitReachedAt: DateTime | null;
	/**
	 * The department whose settings hold the rule — not always the shift's: a float-pool nurse's OT
	 * follows their home department. Null on a line stored before it was captured.
	 */
	departmentId: string | null;
};

export type HolidayContribution = {
	kind: "holiday";
	valueType: RateHoursBonusType;
	/** The department's holiday percentage (200 = double time). */
	value: number;
	amountPerHour: number;
};

/** Trainee / Orientation pay REPLACES the base before any rule applies, so it adds 0 to the rate. */
export type TrainingContribution = {
	kind: "training" | "orientation";
	valueType: RateHoursBonusType;
	value: number;
	/** The nurse's own hourly wage, which the replacement was taken from (a percentage) or instead of (flat). */
	ownBase: number;
	amountPerHour: number;
};

/** Names the rule that placed a break: on the $0 Break row when unpaid, on the priced rows when paid. Adds 0 to the rate. */
export type BreakContribution = {
	kind: "break";
	ruleId: string | null;
	name: string;
	scope: BreakScope;
	breakMinutes: number;
	/** The shift's work hours must exceed this for the break to be placed. */
	appliesOverHours: number;
	/** The rule's type; null only on a line stored before it was captured. */
	ruleType: BreakRuleType | null;
	/** Hours into the shift the break is placed (the rule's break start, else its limit); null as above. */
	startsAfterHours: number | null;
	/** A line stored before pay was captured reads as unpaid, as every break was then. */
	isPaid: boolean;
	amountPerHour: number;
};

/** Percentages multiply, so together they add more than each measured alone; this line is that difference. */
export type CompoundingContribution = {
	kind: "compounding";
	amountPerHour: number;
};

export type RuleContribution =
	| HoursRateContribution
	| OvertimeContribution
	| HolidayContribution
	| TrainingContribution
	| BreakContribution
	| CompoundingContribution;

export type WageBreakdownRow = {
	id: number;
	base: number;
	rateType: string;
	rate: number;
	hours?: string;
	totalHours: number;
	wage: number;
	startTime: DateTime;
	endTime: DateTime;
	/** Which rules produced `rate`. `null` = a legacy row stored before they were captured — never "no rules" (that is `[]`). */
	contributions: RuleContribution[] | null;
	/** The unrounded (4dp) base the contributions add to; `null` exactly when `contributions` is. */
	baseExact: number | null;
};

export type WageBreakdownResult = {
	rows: WageBreakdownRow[];
	total: number;
	/**
	 * The status the shift was priced under. Trainee and Orientation replace the nurse's base wage with
	 * the department's training/orientation rate before any row is built, so no row label says so.
	 */
	trainingStatus: NurseTrainingStatus;
	/** `null` only for a stored breakdown written before pay basis was captured. */
	payBasis: PayBasis | null;
	/** Set only when `payBasis` is agency and the nurse has an agency name. */
	agencyName: string | null;
	isActual: boolean;
	/** The shift the rows price — the punch times for an actual. `null` when a punch was cleared after pricing. */
	shiftStartTime: DateTime | null;
	shiftEndTime: DateTime | null;
};

export type CostPredictionRequest = {
	departmentId: string;
	nurseId: number;
	startTime: string;
	endTime: string;
	isCharge?: boolean;
	trainingStatus?: NurseTrainingStatus;
	bonus?: number;
	excludedShiftIds?: string[];
};

export type CostPredictionResponse = {
	shiftCost: number;
	wageBreakdown: WageBreakdownResult;
	weeklyCostBefore: number;
	weeklyCostAfter: number;
	weeklyHoursBefore: number;
	weeklyHoursAfter: number;
	overtimeHoursDelta: number;
	/** DAT-289: which cap produced `overtimeHoursDelta`. The delta is a max across the three kinds, so the
	 * number alone does not say what it is — without this the swap dialog called every hour "weekly OT".
	 * `null` when no cap produced it (a salaried nurse), never a substituted kind. */
	overtimeType: OvertimeKind | null;
	weeklyOvertimeHoursBefore: number;
	isEstimated?: boolean;
};

export type BatchCostSortItem = {
	field: "name" | "nurseType" | "nurseStartDate" | "overtimeHours" | "predictedCost" | "id";
	order: "asc" | "desc";
};

export type BatchCostPredictionRequest = {
	departmentId: string;
	nurseIds: number[];
	startTime: string;
	endTime: string;
	offset?: number;
	limit?: number;
	sort?: BatchCostSortItem[];
	jobId?: string;
};

export type BatchCostPredictionResponse = {
	predictions: Record<number, CostPredictionResponse>;
	total: number;
	sortedNurseIds: number[];
};

export type DayExpensesResponse = {
	overtimeExpenses: number;
	agencyExpenses: number;
	totalCost: number;
};

/** Shift payload for draft overlay — full for `adds`, partial for `updates`. */
export type DraftOverlayShift = {
	id: string;
	nurseId: number;
	startTime?: string;
	endTime?: string;
	charge?: boolean;
	trainingStatus?: NurseTrainingStatus;
	bonus?: number;
	departmentId?: string;
	// On UPDATE diffs, undefined means "unchanged"; an explicit list (incl. []) means full replacement.
	breaks?: Break[];
};

/** In-memory diff applied on top of the published baseline. */
export type DraftOverlay = {
	adds: DraftOverlayShift[];
	removes: string[];
	updates: Record<string, DraftOverlayShift>;
};

export type DraftDayStatsRequest = {
	departmentId: string;
	date: string;
	overlay: DraftOverlay;
};

export type DraftDayStatsResponse = {
	dayExpenses: DayExpensesResponse;
	shiftBreakdowns: Record<string, WageBreakdownResult>;
	nurseOvertime: NurseOvertimeMap;
};

export interface NurseCostItem {
	nurseId: number;
	date: DateTime;
	cost: number;
}

export interface NurseCostsResponse {
	costs: NurseCostItem[];
}

export interface HourlyPPN {
	hour: number;
	ppn: number;
}

export interface DayOff {
	id: string;
	dateFrom: DateTime;
	dateTo: DateTime;
	hours: number;
	reasons: (PtoTypes | string)[];
	post_shifts: boolean;
	notes?: string | TimeOffReasons;
	externalTimeOff?: ExternalTimeOff;
}
export interface DayOffWithNurseId extends DayOff {
	nurseId: number;
}

export interface StatsCalculationParams {
	weeklyShifts: Shift[];
	nurses: Nurse[];
	dayIndex: number;
	department: Department;
	timezone: string;
	rolesToDisplay?: NurseFullRole[];
	hospitalData: HospitalData;
	weekIndex: number;
	federalHolidaysUSASet: Set<string>;
	dayHours?: number;
	jobManagementEnabled: boolean;
	wageData?: DepartmentWageDataResponse | null;
}

/** One CDPH §72329.2 census reading: the facility-local hour it was taken at, and the patient count. */
export interface CensusPointInTime {
	hour: number;
	census: number;
}

/**
 * The CDPH §72329.2 census readings for one calendar day (midnight / 8 AM / 4 PM) and their mean.
 *
 * `points` holds only the readings whose instant has already passed at the facility, so a day printed
 * at 2 PM carries two of them and `average` is the mean of those two. `available` is false when the
 * department has no ADT-capable EMR or the feature is off for it — in both cases the printout omits
 * the block entirely.
 */
export interface CensusPointInTimes {
	date: string;
	points: CensusPointInTime[];
	average: number | null;
	available: boolean;
}

/**
 * Response of `GET /departments/{department_id}/has-shifts` — mirrors the backend's
 * `DepartmentHasShifts`. The field stays snake_case because that model declares no alias, and
 * nothing in the network layer rewrites key casing.
 *
 * True when the department has any non-deleted scheduled or worked shift. It is the timezone-edit
 * lock's only input — see `useDepartmentHasShifts` / `isTimezoneLocked` in the Department
 * Configuration hooks.
 */
export interface DepartmentHasShifts {
	has_shifts: boolean;
}

export interface DayStats {
	totalStaff: number;
	census: number;
	totalHours: number;
	wantedHours: number;
	totalSpent: number;
	ppd: number;
	laborPPD: number;
	hppd: number;
	isActual?: boolean;
}

export enum ConflictErrors {
	NoMatch = "no match in conflict",
	IndexError = "IndexError",
	ConflictShift = "conflict shift",
	GeneralException = "General exception",
}

export const NurseActionsErrorTypes = {
	IntegrityError: "IntegrityError",
	MissingNurseEmailPhone: "MissingNurseEmailPhone",
	DuplicateReplaceRequest: "DuplicateReplaceRequest",
	RequestNotActive: "RequestNotActive",
	UIVisibleError: "UIVisibleError",
} as const;

export interface DeleteNurseData {
	id: string;
	nurse_id: number;
	message: string | null;
	department_id: string;
}

export interface ProcessedShift {
	date: DateTime;
	shiftType: string;
	startTime: DateTime;
	endTime: DateTime;
	location?: string;
	notes?: string;
	isPublished: boolean;
	isPending: boolean;
	nurseRole: string;
	nurseName: string;
}

export interface ShiftItem {
	date: DateTime;
	startTime?: DateTime;
	endTime?: DateTime;
	location?: string;
	shiftType?: ShiftType;
	hasDayRequest?: boolean;
	departmentId: string;
	hasNightRequest?: boolean;
	isTimeOff?: boolean;
	timeOffType?: string;
	isPreferredNot?: boolean;
	isPreferred?: boolean;
	isCharge?: boolean;
	requests?: number;
	bonus?: number;
}

export interface SelectedShift {
	shiftId?: string;
	date: string;
	shiftType?: ShiftType;
	startTime?: DateTime;
	endTime?: DateTime;
}

export interface TransformedShift {
	id?: string;
	date: DateTime;
	startTime?: DateTime;
	endTime?: DateTime;
	departmentId: string;
	location?: string;
	shiftType?: ShiftType;
	hasDayRequest?: boolean;
	hasNightRequest?: boolean;
	isTimeOff?: boolean;
	timeOffType?: string;
	timeOffReason?: string;
	isPreferredNot?: boolean;
	isPreferred?: boolean;
	isCharge?: boolean;
	requests?: number;
	bonus?: number;
	notes?: string;
	jobName?: string;
	jobId?: string;
	/** Only on time off that covers part of a single staffing day, so it reads as hours rather than a whole day off. */
	timeOffWindow?: TimeOffWindow & { hours: number };
}

export type TimeOffWindow = { start: DateTime; end: DateTime };

export interface OpenShift {
	id: string;
	created_by: number;
	request_for: number;
	shift_type: ShiftType.Day | ShiftType.Night;
	shift_start: DateTime;
	shift_end: DateTime;
	location: string;
	needed_nurses: number;
	request_data?: RequestResponsesStatus[];
	department_id: string;
}

export enum RequestResponsesStatusOptions {
	PENDING = "pending",
	APPROVED = "approved",
	DECLINED = "declined",
	CLOSED = "closed",
}

export enum NotificationType {
	OpenShift = "open_shift",
	ReplaceShift = "replace_shift",
	ReplaceAgency = "replace_agency",
	PTORequest = "pto_request",
	RemovedFromShift = "removed_from_shift",
	AddedToShift = "added_to_shift",
	Approved = "approved",
	ApprovedReplace = "approved_replace",
	Declined = "declined",
	DeclinedReplace = "declined_replace",
	NurseTermination = "nurse_termination",
	Swap = "swap",
	CallOff = "call_off",
	SchedulePublished = "schedule_published",
	DailyPickupSummary = "daily_pickup_summary",
	PerformanceReport = "performance_report",
	EmployeeStatusDecision = "employee_status_decision",
	/** Someone @mentioned the reader in a Careers interview note. Bell only: never emailed, texted or pushed. */
	CareersNoteMention = "careers_note_mention",
	CareersFeedbackMention = "careers_feedback_mention",
	CareersFeedbackReminder = "careers_feedback_reminder",
}

export interface RequestResponsesStatus {
	status: RequestResponsesStatusOptions;
	nurse_id: number;
	accepted_start_time?: DateTime;
	accepted_end_time?: DateTime;
	bonus?: number | null;
}

export enum RequestStatus {
	Active = "Active",
	Canceled = "Canceled",
	Done = "Done",
}

export interface BaseRequest {
	id: string;
	request_type: NotificationType;
	created_by: string;
	created_at: DateTime;
	status: RequestStatus;
	charge_only: boolean;
	department_id: string;
	bonus: number;
	request_target_shift?: string;
	location?: string;
	source?: RequestSource | null;
	job_id?: string;
	sub_role?: NurseRole;
	incentive_configuration_id?: string;
	current_bonus?: number;
	hours_left?: number | null;
	incentive_windows?: IncentivePlan[];
	// future use
	// allow_agency: boolean;
	// allow_overtime: boolean;
	message: {
		date: DateTime;
		shift_period: number;
		shift_type: string;
		reason: string;
		accepted_nurses: number[];
		roles: string[];
		weekIndex: number;
		dayNo: number;
		request_data: RequestResponsesStatus[];
		shift_start: DateTime;
		shift_end: DateTime;
	};
}

export interface OpenShiftRequest extends BaseRequest {
	needed_nurses: number;
	agency_shift_ids?: string[];
	agency_source_name?: string;
	agency_post_delay_hours?: number;
}

export interface ReplaceShiftRequest extends BaseRequest {
	replacement_nurse_id: number;
	request_for_shift?: string;
	// Set only on an Active replace-agency request: when it closes unless a pickup is pending.
	auto_close_at?: DateTime | null;
	// True when auto_close_at comes from the agency's T&T Bill hours, false for the 4-hour default.
	auto_close_uses_agency_terms?: boolean | null;
}

export interface SwapShiftRequest extends BaseRequest {
	request_for_shift?: string;
	request_target_shift?: string;
}

export type ShiftFilterType = ShiftType.All | ShiftType.Day | ShiftType.Night;

export type ShiftStatus =
	| ""
	| RequestResponsesStatusOptions.PENDING
	| RequestResponsesStatusOptions.APPROVED
	| RequestResponsesStatusOptions.DECLINED
	| RequestResponsesStatusOptions.CLOSED;

export interface ShiftCard {
	date: DateTime;
	shiftType: string;
	startTime: DateTime;
	endTime: DateTime;
	location?: string;
	isPublished: boolean;
	isPending: boolean;
}

export interface OpenShiftItem {
	date: DateTime;
	shiftType?: ShiftType;
	hasDayRequest?: boolean;
	hasNightRequest?: boolean;
	isTimeOff?: boolean;
	timeOffType?: string;
	isPreferredNot?: boolean;
	isPreferred?: boolean;
	requests?: number;
}

export interface OpenShiftType {
	id: string;
	startTime: DateTime;
	endTime: DateTime;
	type: ShiftType.Day | ShiftType.Night;
	status: ShiftStatus;
	myStatus: ShiftStatus;
	location: string;
	locations?: string[];
	neededNurses: number;
	created_by: string;
	is_merged?: boolean;
	original_shifts?: OpenShift[];
	accepted_start_time?: DateTime;
	accepted_end_time?: DateTime;
	charge_only?: boolean;
	bonus?: number;
	department_id: string;
	job_id?: string;
	incentive_configuration_id?: string;
	current_bonus?: number;
	hours_left?: number | null;
	posted_at?: DateTime;
	incentive_windows?: IncentivePlan[];
}

export interface IncentivePlan {
	id?: string;
	incentiveConfigurationId?: string;
	value: number;
	startHour: number;
	endHour?: number | null;
}

export interface IncentiveConfiguration {
	id: string;
	departmentId: string;
	name: string;
	baseValue: number;
	minimalValue: number;
	plans: IncentivePlan[];
	deleted?: boolean;
	deletedAt?: DateTime | null;
	createdAt?: DateTime;
	updatedAt?: DateTime;
}

export interface IncentivePlanWrite {
	value: number;
	startHour: number;
	endHour: number | null;
}

export interface IncentiveConfigurationWrite {
	departmentId: string;
	name: string;
	plans: IncentivePlanWrite[];
}

export interface TimeConfig {
	startTime: DateTime;
	endTime: DateTime;
}

export enum ChangesSource {
	Staffing = "Staffing",
	MasterSchedule = "Master Schedule",
	DaySchedule = "Day Schedule",
	Profile = "Profile",
	Availability = "Availability",
	Requests = "Requests",
	SelfScheduling = "Self Scheduling",
	NurseApp = "Nurse App",
	SupportLine = "SupportLine",
	SupportLineSms = "SupportLineSms",
	SupportLinePhone = "SupportLinePhone",
	Assistant = "Assistant",
	// Mirrors backend `models/enums.py::ChangesSource`. These are written by server-side flows only,
	// but they still come back on call-off rows, so the frontend must be able to name them.
	AddNurse = "Add Nurse",
	Script = "Script",
	Integration = "Integration",
	Login = "Login",
	AutoApprove = "AutoApprove",
	DataLoading = "DataLoading",
	HolidayGroups = "HolidayGroups",
}

export enum NurseInShiftCreationSource {
	MasterSchedule = "Master Schedule",
	AddNurse = "Add Nurse",
	Requests = "Requests",
	// Retrospective add of a punched-but-unscheduled actual shift onto the schedule (Actual daily view).
	DuplicateActual = "Duplicate Actual",
}

export enum RequestSource {
	CallOff = "call_off",
	Pto = "pto",
	PostShift = "post_shift",
	PostShiftRecommendation = "post_shift_recommendation",
	Clipboard = "clipboard",
	AutoPostShift = "auto_post_shift",
	CancelAgency = "cancel_agency",
}

/** Why a request's open slots changed: one per entry on the "Why this shift was posted" card. Mirrors `OriginEntryKind`. */
export enum RequestOriginKind {
	// Openings
	CallOff = "call_off",
	TimeOff = "time_off",
	Deactivation = "deactivation",
	AutoPost = "auto_post",
	FromRecommendation = "from_recommendation",
	PostedByPerson = "posted_by_person",
	PostedByAnnie = "posted_by_annie",
	ReplaceRequest = "replace_request",
	SwapRequest = "swap_request",
	ReplacingAgency = "replacing_agency",
	Agency = "agency",
	AgencyCanceled = "agency_canceled",
	// Either way
	CountRaised = "count_raised",
	CountLowered = "count_lowered",
	MergedIn = "merged_in",
	CountAdjusted = "count_adjusted",
	// Removals
	Filled = "filled",
	Canceled = "canceled",
	MergedAway = "merged_away",
	AgencySyncLowered = "agency_sync_lowered",
	// No recorded cause
	NotRecorded = "not_recorded",
	Unknown = "unknown",
}

/** The Origin filter's three buckets of what opened a slot. */
export enum RequestOriginGroup {
	Automation = "automation",
	People = "people",
	External = "external",
}

/** `Unavailable` = the department does not get origins, which is not the same as "still loading". */
export enum RequestOriginsStatus {
	Unavailable = "unavailable",
	Loading = "loading",
	Ready = "ready",
	Failed = "failed",
}

/**
 * A person on the card, as name parts the name-order formatter puts together. `nurse_id` is null for an account off the
 * roster (a scheduler, a support user); `last_name` is null for such an account, which has one name field.
 */
export type OriginPerson = { nurse_id: number | null; first_name: string; last_name: string | null };

/** The day's HPPD numbers just before a post (`HppdPostRationale`). Hours are the calculator's, break-adjusted. */
export type HppdPostNumbers = {
	kind: StatsMode.PPD_HPPD;
	/** The recommendation's day: the shift start's calendar date in the department's zone (not day-start aware). */
	staffing_date: DateTime;
	hppd_before_post: number;
	hppd_target: number;
	/** Posting starts below this share of the target; `threshold_hppd` is that share in HPPD. */
	threshold_percent: number;
	threshold_hppd: number;
	census: number;
	census_by_shift: Record<string, number>;
	scheduled_hours: number;
	posted_hours: number;
	needed_hours: number;
	hours_short: number;
};

/** The ratio ladder's counts for one role or job and shift just before a post (`RatioPostRationale`). */
export type RatioPostNumbers = {
	kind: StatsMode.Ratio;
	staffing_date: DateTime;
	census: number;
	role_group: string;
	job_id: string | null;
	location: string | null;
	required: number;
	scheduled: number;
	posted: number;
	deficit: number;
};

export type PostNumbers = HppdPostNumbers | RatioPostNumbers;

export type TimeOffCandidate = { nurse: OriginPerson; starts_at: DateTime | null; ends_at: DateTime | null };
export type SwapCounterpart = { nurse: OriginPerson; shift_start: DateTime; shift_end: DateTime };

/** What one entry rests on, discriminated on `kind` (`OriginDetails`). */
export type OriginDetails =
	| { kind: RequestOriginKind.CallOff; nurse: OriginPerson; shift_start: DateTime; shift_end: DateTime; channel: ChangesSource }
	| { kind: RequestOriginKind.TimeOff; candidates: TimeOffCandidate[]; ambiguous: boolean }
	| { kind: RequestOriginKind.Deactivation; nurse: OriginPerson; shifts_removed: number }
	| { kind: RequestOriginKind.AutoPost; numbers: PostNumbers; posted_count: number; recommended_count: number | null }
	| { kind: RequestOriginKind.FromRecommendation; numbers: PostNumbers | null; posted_count: number | null; recommended_count: number | null }
	| { kind: RequestOriginKind.PostedByPerson }
	| { kind: RequestOriginKind.PostedByAnnie }
	| { kind: RequestOriginKind.ReplaceRequest; nurse: OriginPerson; shift_start: DateTime; shift_end: DateTime }
	| { kind: RequestOriginKind.SwapRequest; nurse: OriginPerson; shift_start: DateTime; shift_end: DateTime; counterpart: SwapCounterpart | null }
	| { kind: RequestOriginKind.ReplacingAgency; nurse: OriginPerson; agency_name: string | null }
	| { kind: RequestOriginKind.Agency; agency_name: string; from_count: number; to_count: number }
	| { kind: RequestOriginKind.AgencyCanceled; nurse: OriginPerson; agency_name: string }
	| { kind: RequestOriginKind.CountRaised; from_count: number; to_count: number }
	| { kind: RequestOriginKind.CountLowered; from_count: number; to_count: number }
	| { kind: RequestOriginKind.MergedIn; from_request_id: string; entries: LedgerEntry[]; history_complete: boolean }
	| { kind: RequestOriginKind.CountAdjusted }
	| { kind: RequestOriginKind.Filled; nurse: OriginPerson; approved_via: ChangesSource | null }
	| { kind: RequestOriginKind.Canceled; source: ChangesSource | null }
	| { kind: RequestOriginKind.MergedAway; into_request_id: string }
	| { kind: RequestOriginKind.AgencySyncLowered; agency_name: string; from_count: number; to_count: number }
	| { kind: RequestOriginKind.NotRecorded }
	| { kind: RequestOriginKind.Unknown; entry_point: string | null; from_open: number; to_open: number };

/** One change to a request's open slots, and why. `delta` > 0 opened slots, < 0 closed them. */
export type LedgerEntry = {
	kind: RequestOriginKind;
	delta: number;
	at: DateTime | null;
	/** Who did it; null for automation (tasks, agency syncs, auto-approve). */
	actor: OriginPerson | null;
	/** From `at` to the shift's start as it was then; negative when the slot changed after the shift began. */
	lead_minutes: number | null;
	/** The HTTP request or task run; null when the change ran outside one. */
	batch_id: string | null;
	/** How many requests in the department that same request or run created or changed. */
	batch_size: number | null;
	details: OriginDetails;
};

/** A request's whole story: its entries, in order, add up to `open_slots_now`. */
export type RequestLedger = {
	request_id: string;
	status: RequestStatus;
	/** `needed_nurses` while Active, else 0. The card's footer reads this, never the request's own count. */
	open_slots_now: number;
	/** When the change log started recording this request's causes; null when it never has. */
	recorded_from: DateTime | null;
	/** Empty only for a request that never had a slot open (`useRequestOrigins` drops those); a request with nothing recorded is one "not recorded" entry. */
	entries: LedgerEntry[];
};

/** `GET /requests/origins`: one page of a window's ledgers; `next_offset` is null on the last page. */
export type RequestOriginsPage = { items: RequestLedger[]; next_offset: number | null };

/** Whether an open shift was opened by the system (call-off flow, auto-post, agency) or posted by a person. */
export enum FillShiftCreation {
	Auto = "auto",
	Manual = "manual",
}

/** Where an open shift ended up, counted in slots: a two-slot shift with one taker is partially filled. */
export enum FillShiftStatus {
	Filled = "filled",
	PartiallyFilled = "partially_filled",
	NotFilled = "not_filled",
	Cancelled = "cancelled",
}

/** How a person was put on a shift: approved automatically, or by a scheduler. */
export enum FillShiftApproval {
	Auto = "auto",
	Manual = "manual",
}

export enum FillShiftAskResult {
	Approved = "approved",
	NotApproved = "not_approved",
}

/** Overtime hours a shift added: over the department's daily limit, and over its weekly limit. 0 = none of that kind. */
export type FillShiftOvertime = { daily: number; weekly: number };

export type FillShiftRequestType = NotificationType.OpenShift | NotificationType.ReplaceShift | NotificationType.ReplaceAgency;

/**
 * Someone asking to work an open shift. An agency worker asks through their agency, so `agencyName` is set.
 * Invariant the endpoint keeps: an ask is `approved` exactly when one of the row's fills carries its `id` as `askId`.
 */
export type FillShiftAsk = {
	id: string;
	person: OriginPerson;
	jobId: string;
	agencyName: string | null;
	requestedAt: DateTime;
	result: FillShiftAskResult;
	/**
	 * Overtime working this shift adds (approved) or would have added (not approved) to the requester's pay week —
	 * so a reader can see whether someone who'd have stayed out of overtime asked too.
	 */
	overtime: FillShiftOvertime;
};

/** One person on the shift: through an approved ask, or assigned directly with no ask (`askId` null). */
export type FillShiftFill = {
	person: OriginPerson;
	agencyName: string | null;
	approval: FillShiftApproval;
	/**
	 * The scheduler who approved or assigned them. `approval` decides auto vs manual, not this field: auto ⇒ always
	 * null; manual ⇒ the scheduler, or null when no scheduler is on record (shown as "Scheduler not recorded").
	 */
	approvedBy: OriginPerson | null;
	askId: string | null;
	hours: number;
	/** Overtime this shift added to the person's pay week, by kind. */
	overtime: FillShiftOvertime;
};

/** One open shift in the Fill Shift Report: why it opened, who asked to work it, and who got it. */
export type FillShiftRow = {
	requestId: number;
	jobId: string;
	shiftStart: DateTime;
	shiftEnd: DateTime;
	requestType: FillShiftRequestType;
	location: string | null;
	/** Chronological and never empty: merged call-offs are several openings on one row. Each opened one slot. */
	openings: LedgerEntry[];
	creation: FillShiftCreation;
	status: FillShiftStatus;
	slots: number;
	asks: FillShiftAsk[];
	fills: FillShiftFill[];
};

export type FillShiftReport = { rows: FillShiftRow[] };

export interface DayPreference {
	weekIndex: number;
	dayIndex: number;
	preferDay: boolean;
	preferNight: boolean;
}

export interface TransformedMasterScheduleShift {
	startTime?: DateTime;
	endTime?: DateTime;
	location?: string;
	shiftType?: ShiftType;
	weekNumber: number;
}

export interface ShiftNotificationDetails {
	shift_date: DateTime;
	shift_start: DateTime;
	shift_end: DateTime;
	shift_type: ShiftType.Day | ShiftType.Night;
	reason: string | null;
	message: string;
	notification_type: string;
	replacement_nurse_id: number | null;
	weekIndex?: number;
	dayNo?: number;
	username?: string;
	original_shift?: DateTime;
	original_shift_type?: string;
	location?: string;
	needed_nurses?: number;
	nurse_id?: number;
	nurse_name?: string;
	role?: string;
}

export interface NotificationResponse {
	id: number;
	user_id: string;
	title: string;
	message: string;
	created_at: DateTime;
	seen_at: DateTime | null;
	clicked_at: DateTime | null;
	expires_at: DateTime | null;
	priority: number;
	notification_type: string;
	notification_email_sent: boolean;
	notification_sms_sent: boolean;
	notification_email_sent_timestamp: DateTime | null;
	notification_sms_sent_timestamp: DateTime | null;
	nurse_id: string | null;
	reason: string | null;
	department_id: string;
	notification_data: any;
	request_id: string;
	parsed_data?: ShiftNotificationDetails;
	isSeen: boolean;
	isClicked: boolean;
}

export interface MarkNotificationsSeenRequest {
	notification_ids: number[];
}

export interface MarkNotificationClickedRequest {
	notification_id: number;
}

export enum NotificationPage {
	BedsideHome = "BedsideHome",
}

export enum UserRole {
	BedSideNurse = "BedSideNurse",
	HeadNurse = "HeadNurse",
}

// Which app surface the current token is for. Mirrors the backend UserView enum.
export enum UserView {
	admin = "admin",
	staff = "staff",
}

/**
 * Which mobile admin Staffing experience a head nurse chose for a department — the gaps-first shell or
 * the classic weekly list. Persisted per user AND per department (`localStorageService`), because the
 * choice is about how one facility is read, not a global taste: an area manager can want gaps-first on
 * the SNF they are short-staffing and classic on the one they only audit.
 */
export enum MobileStaffingView {
	Gaps = "gaps",
	Classic = "classic",
}

/**
 * The gaps-first home's saved scope, as it is persisted.
 *
 * Arrays rather than Sets because this crosses `JSON.stringify` into `localStorage`; the screen turns
 * them into Sets on read. `null` is a row the user never narrowed — everything, whatever the department
 * has next time — and an empty array is a real value meaning "none of them".
 */
export type GapsViewFilterPreference = {
	jobLabels: string[] | null;
	locationNames: string[] | null;
	timeLabels: string[] | null;
};

/** A Schedule Template time-filter chip as it is persisted: its start and end as `"HH:mm"`. */
export type StoredShiftWindow = { start: string; end: string };

export interface PanelShiftData {
	shiftType: string | null;
	tooltip?: string;
	hasWarning?: boolean;
	originalDayIndex?: number;
	isFromMasterSchedule?: boolean;
	isRecurringShift?: boolean;
	/** When set, the nurse is On Leave / Suspended on this day — the cell renders a pause marker instead of the shift icon. */
	unavailableStatus?: "OnLeave" | "Suspended";
}

export interface NurseData {
	id: number;
	/**
	 * Optional grid row key. `id` is the NURSE id and is NOT unique when the same nurse appears as
	 * multiple picker candidates (e.g. MoveNursePanel lists a nurse once per eligible source/target day).
	 * When set, the grid uses this as the DataGrid row id so duplicates stay distinct and selectable;
	 * defaults to `id` for the common one-row-per-nurse case.
	 */
	rowKey?: string;
	/** Set by `NursesMatrixGrid` in selection mode: the row is in the current pick. */
	picked?: boolean;
	name: string;
	role: string;
	weekends?: number;
	changes?: number;
	predictedCost?: string;
	/** Numeric weekly cost delta backing `predictedCost` (a formatted string). Used to sort cost numerically, not lexically. */
	predictedCostValue?: number;
	nurseType?: string;
	preferences?: string;
	shifts: PanelShiftData[];
	charge?: boolean;
	shiftType?: ShiftType;
	selectable?: boolean;
	isShiftManager?: boolean;
	trainingStatus: NurseTrainingStatus;
	canBeReliefCharge?: boolean;
	overtimeHours?: number;
	weeklyOvertimeHoursBeforeChange?: number;
	dailyHours?: number;
	lastWeekCalloffs?: { count: number; lastCalloffTime: DateTime };
	nurseStartDate?: DateTime;
	/**
	 * Pickup submission time. A `DateTime`, not a string — `convertIsoDates` parses it on the way in.
	 * Nullable, not just optional: the API always emits the key and sends `null` for a declined nurse.
	 */
	requestedAt?: DateTime | null;
	shiftTimes?: { start: DateTime; end: DateTime };
	shiftStartTime?: DateTime;
	shiftEndTime?: DateTime;
	shiftId?: string;
	accepted_start_time?: DateTime;
	accepted_end_time?: DateTime;
	scheduledDeactivationDate?: DateTime | null;
	employeeWeeklyPreferences?: WeeklyPreference[];
	bonus?: number;
	agencyProfileUrls?: string[];
	jobId?: string;
	jobName?: string;
	isDenied?: boolean;
	/**
	 * Already-translated reason this nurse can't take the shift ("Already on 3:00–11:00 PM"). Setting it
	 * makes the row unselectable in `NursesMatrixGrid` and gives it the blocked look: an ✕ in place of the
	 * picker control, a struck name, the reason across the metadata columns, a muted week strip.
	 */
	unavailableReason?: string;
	/**
	 * Soft, still-selectable notes shown as quiet chips beside the name (PTO, overtime, a long day,
	 * fully booked). Labels are already translated; `kind` is a stable key for React keys and test ids.
	 */
	badges?: NurseRowBadge[];
	/**
	 * Already-formatted secondary line under the name in `NursesMatrixGrid`'s name cell — e.g. a shift's
	 * custom hours ("8:00 AM–3:00 PM") when they differ from the window the row is listed under. Omit
	 * for a one-line name (every existing caller).
	 */
	nameCaption?: string;
}

/** One soft annotation chip on a `NurseData` row — rendered as a small `MetaChip`. */
export interface NurseRowBadge {
	/** Stable identifier (e.g. `"pto"`, `"overtime"`) — React key and test-id suffix, never shown. */
	kind: string;
	/** Short, already-translated chip text. */
	label: string;
	tone: MetaChipTone;
	/** Optional already-translated hover text with the detail the short label leaves out. */
	tooltip?: string;
}

export interface ColumnDefinition {
	id: string;
	label: string;
	width: string;
	align: "left" | "center" | "right";
	render: (nurse: NurseData) => React.ReactNode;
	sortable?: boolean;
	sortColumns?: Array<keyof NurseData>;
}

export enum WeeklyHoursCountingMethod {
	ShiftBased = "ShiftBased", // Count from first shift of week to last shift
	TimeBased = "TimeBased", // Count from specific time to specific time
}

export interface WeeklyHoursDefinition {
	method: WeeklyHoursCountingMethod;
	// For ShiftBased method
	startDay?: Days; // e.g., Sunday
	startShiftType?: ShiftType; // e.g., Day shift
	endDay?: Days; // e.g., Saturday
	endShiftType?: ShiftType; // e.g., Night shift

	// For TimeBased method
	startTime?: string; // Format: "HH:MM", e.g., "00:00" for midnight
	startDayOfWeek?: Days; // e.g., Sunday
	endTime?: string; // Format: "HH:MM", e.g., "23:59" for end of day
	endDayOfWeek?: Days; // e.g., Saturday
}

export interface ShiftTimes {
	start: DateTime;
	end: DateTime;
	date: DateTime;
	shiftType?: ShiftType;
}

export interface RemoveNurseRequest {
	nurse: Nurse;
	shiftTimes: {
		startTime: DateTime;
		endTime: DateTime;
	};
	shiftType: ShiftType;
	message: string;
	shiftIndex: number;
	weekIndex: number;
	dayNo: number;
	department_id: string;
}

export interface AddNurseRequest {
	nurse: Nurse;
	shiftTimes: {
		startTime: DateTime;
		endTime: DateTime;
	};
	shiftType: ShiftType;
	shiftIndex: number;
	weekIndex: number;
	dayNo: number;
	selectedRoom: string;
	department_id: string;
}

export interface StaffTableNote {
	color: string;
	text: string;
	textColor: string;
	tooltip?: string;
}

export const StaffTableNotes: Record<string, StaffTableNote> = {
	WeeklyOT: {
		color: STATUS_BADGE.CORAL,
		text: "Weekly OT",
		textColor: SURFACE,
	},
	ApproachingOT: {
		color: STATUS_BADGE.PEACH,
		text: "Approaching OT",
		textColor: STATUS_BADGE.AMBER_INK,
	},
	DailyOTHigh: {
		color: STATUS_BADGE.ORANGE,
		text: "Daily OT",
		textColor: SURFACE,
	},
	// DAT-289. Shares the Coral treatment with Weekly OT: both are week-scoped premiums, and the
	// Orange/Peach pair already reads as "daily" on the grid.
	ConsecutiveOT: {
		color: STATUS_BADGE.CORAL,
		text: "Consecutive OT",
		textColor: SURFACE,
	},
	DailyOTLow: {
		color: STATUS_BADGE.MUTED_INK_80,
		text: "Daily OT",
		textColor: SURFACE,
	},
	Agency: {
		color: STATUS_BADGE.MUTED_INK_80,
		text: "Agency",
		textColor: SURFACE,
	},
	NotOnboarded: {
		color: STATUS_BADGE.MUTED_INK_80,
		text: "Not Onboarded",
		textColor: SURFACE,
	},
	ExcludedFromPpd: {
		color: STATUS_BADGE.LILAC,
		text: "Excluded from PPD",
		textColor: STATUS_BADGE.LILAC_INK,
	},
	Trainee: {
		color: STATUS_BADGE.MUTED_INK_80,
		text: "Trainee",
		textColor: SURFACE,
	},
	Orientation: {
		color: STATUS_BADGE.MUTED_INK_80,
		text: "Orientation",
		textColor: SURFACE,
	},
	Education: {
		color: STATUS_BADGE.GRAY_INK_80,
		text: "Education",
		textColor: SURFACE,
	},
	MissingPunchIn: {
		color: STATUS_BADGE.WARN_BG,
		text: "Missing Punch In",
		textColor: STATUS_BADGE.WARN_INK,
	},
	MissingPunchOut: {
		color: STATUS_BADGE.WARN_BG,
		text: "Missing Punch Out",
		textColor: STATUS_BADGE.WARN_INK,
	},
	MissingBothPunches: {
		color: STATUS_BADGE.WARN_BG,
		text: "Missing Both Punches",
		textColor: STATUS_BADGE.WARN_INK,
	},
	Pending: { color: STATUS_BADGE.ORANGE, text: "Pending", textColor: SURFACE },
	NoPunch: {
		color: STATUS_BADGE.ERROR_BG,
		text: "No Punch",
		textColor: STATUS_BADGE.ERROR_INK,
	},
	ActiveRequest: {
		color: STATUS_BADGE.BRAND_FALLBACK,
		text: "Active Request",
		textColor: SURFACE,
	},
	NotScheduled: {
		color: STATUS_BADGE.CORAL,
		text: "Not Scheduled",
		textColor: SURFACE,
	},
	PostedToAgency: {
		color: STATUS_BADGE.INFO,
		text: "Posted to Agency",
		textColor: SURFACE,
	},
	MissingBreakPunches: {
		color: STATUS_BADGE.WARN_BG,
		text: "Missing Break Punches",
		textColor: STATUS_BADGE.WARN_INK,
	},
	Incentive: {
		color: STATUS_BADGE.SUCCESS_BG,
		text: "Incentive",
		textColor: STATUS_BADGE.SUCCESS_INK,
	},
};

export interface NurseInShiftDetails {
	nurse: NurseInShift;
	dayIndex: number;
	weekIndex: number;
}

export interface CommandCenterNursesRequest {
	departmentId: string;
}

export interface CommandCenterNurse {
	id: number;
	userId: string;
	firstName: string;
	lastName: string;
	role: string;
	employmentType: EmploymentType;
	assignments: Assignment[];
	qualifiedForAssignments: string[];
}

export interface FloatingNursesRecData {
	nurse: CommandCenterNurse;
	newDepartment: string;
	startDate: DateTime;
	endDate: DateTime;
}

export interface PostShiftData {
	roles: string[];
	jobId?: string;
	neededNurses: number;
	shiftNo: number;
	startTime: DateTime;
	endTime: DateTime;
	departmentId: string;
	bonus: number;
	chargeOnly: boolean;
	location?: string;
	agencyShiftIds?: string[];
	postToAgency?: boolean;
	postWithDelay?: boolean;
	agencyPostDelayHours?: number;
	agencyLicenseTypes?: string[];
	agencyFacilityIds?: string[];
	agencySource?: string[];
	agencySourceName?: string;
	source?: RequestSource;
	subRole?: NurseRole;
	incentiveConfigurationId?: string;
}

export interface JobDepartmentSubRole {
	id: string;
	departmentId: string;
	jobId: string;
	subRole: NurseRole;
	isDefault: boolean;
	source: string;
}

export interface SubRoleUpsertBody {
	jobId: string;
	subRole: NurseRole;
	isDefault: boolean;
	source: string;
	replacesRowId?: string;
}

export interface PatchShiftData {
	neededNurses: number;
	shiftId: string;
	recommendationMerge: boolean;
}

export enum ChatbotActions {
	NoAction = "NO_ACTION",
	RemoveNurse = "REMOVE_NURSE",
	AddNurse = "ADD_NURSE",
	PostShift = "POST_SHIFT",
	ShowStats = "SHOW_STATS",
}

export interface ChatbotMessage {
	message: string;
	action: ChatbotActions;
	completed: boolean;
	parameters: any;
}

export interface Message {
	id: number | string;
	text: string;
	sender: "user" | "server";
	timestamp: string;
}

export interface ChatBotRequest {
	message: string;
	departmentId: string;
	sessionId: string;
}

export interface UpdateCensusData {
	department_id: string;
	date: DateTime;
	shift_type: string;
	census: number;
	update_specific_date: boolean;
}

export interface ShiftStaffTimes {
	count: number;
	requestedCount?: number;
	startTime: DateTime;
	endTime: DateTime;
	role: NurseFullRole;
	roleGroup?: string;
	jobId?: string;
	jobName?: string;
	actualStartTime: DateTime;
	actualEndTime: DateTime;
	names?: string[];
}

export interface StaffShiftMap {
	[role: string]: ShiftStaffTimes[];
}

export type ShiftTime = [DateTime, DateTime] | "Posted Shifts" | "All";

export interface DeleteShiftData {
	shiftId: string;
	departmentId: string;
}

export interface ChatbotConversation {
	id: number;
	userId: string;
	sessionId: string;
	role: "user" | "server";
	createdAt: DateTime;
	message: string;
}

export interface WelcomeResetRequest {
	isReset: boolean;
	nurseId: number;
	departmentId: string;
	emailOrPhoneNumber?: string;
	shouldCreateUser: boolean;
}

export interface PasswordResetRequestBody {
	emailOrPhoneNumber: string;
	sendFromMultipleCommunications?: boolean;
}

export enum SelfSchedulingPreference {
	WANT_TO_WORK = 0, //that's like signing up for the shift specifically
	WILLING_TO_WORK = 1,
	PREFER_NOT_TO_WORK = 2,
	RDO = 3, //cant work
	UNDEFINED = 4, // no preference set
	PTO = 5, // nurse has PTO on this day
}

export interface SelfSchedulingDayStructure {
	date: DateTime;
	dayPreference: SelfSchedulingPreference;
}

export interface GetNurseProfileRequest {
	nurse_id: string | undefined;
	department_id: string;
}

export interface GetNurseNameExistsRequest {
	first_name: string;
	last_name: string;
	department_id: string;
	nurse_id?: number;
	isProfileEditable: boolean;
}

export interface GetNurseProfileImage {
	nurse_id: string;
}
export interface DeleteDayOffRequest {
	nurseId: number;
	departmentId: string;
	dayOff: DayOff;
	restoreRecurringShifts?: boolean;
	// DAT-230 §7: false = scheduler chose "keep the schedule as it is now" for trimmed/split shifts.
	restoreTrimmedShifts?: boolean;
}

export interface PostChatMessageResult {
	userId: string;
	success: boolean;
	sid?: string;
	error?: string;
}
export enum MessageStatus {
	accepted = "accepted",
	queued = "queued",
	sending = "sending",
	sent = "sent",
	failed = "failed",
	delivered = "delivered",
	undelivered = "undelivered",

	//inbound only,
	receiving = "receiving",
	received = "received",

	scheduled = "scheduled",
	canceled = "canceled",
}

export type UserNewMessage = {
	id: string;
	department_id: string;
	to_user_id: string;
	from_user_id: string;
	status: string;
	message: string;
};
export type UserSentMessage = {
	id: number;
	departmentId: string;
	userId: string;
	status: MessageStatus;
	body: string;
	senderUserId?: string;
	tempId?: string;
	dateCreated: DateTime;
	direction: "inbound" | "outbound";
	senderFirstName?: string;
	isPush?: boolean;
	platform?: "ios" | "android";
	title?: string;
	sid?: string;
};
export type UserSentMessagesCollection = { [key: string]: UserSentMessage[] };

export enum RecurringShiftRecurrenceType {
	ConsecutiveDays = "ConsecutiveDays",
	Daily = "daily",
	Weekly = "weekly",
}

interface RecurrenceBaseType {
	recurringType: keyof typeof RecurringShiftRecurrenceType;
	interval: number;
}

export interface DailyRecurring extends RecurrenceBaseType {
	recurringType: "Daily";
}
export interface WeeklyRecurring extends RecurrenceBaseType {
	recurringType: "Weekly";
	weekdays: string[];
}

export interface ConsecutiveDaysRecurring extends RecurrenceBaseType {
	working_days_amount: number;
	rest_days_amount: number;
	interval: never;
	recurringType: "ConsecutiveDays";
}

export type RecurringShift = {
	id?: number;
	nurseId: number;
	startDate: string;
	endDate?: string;
	shiftStart: string;
	shiftEnd: string;
	recurringParams: DailyRecurring | WeeklyRecurring | ConsecutiveDaysRecurring;
	location?: string;
	departmentId: string;
	deleted: boolean;
	jobId?: string;
};

export type ResultOfRecurringCreation = {
	recurringShift: RecurringShift;
	createdNurseShifts: NurseInShift[];
};

export interface CreateRecurringShiftRequest {
	recurringShift: RecurringShift;
	source: ChangesSource;
}

export interface UpdateRecurringShiftRequest {
	recurringShift: RecurringShift;
	source: ChangesSource;
	startDate: string;
}

export type DeleteRecurringShiftMode = "all" | "biweekly_day";

export interface DeleteRecurringShiftRequest {
	id: number;
	nurse_id: number;
	department_id: string;
	source: ChangesSource;
	start_date: string;
	// Optional — backend defaults to "all" so existing callers (mobile / older web) need no changes.
	mode?: DeleteRecurringShiftMode;
	// Required when mode === "biweekly_day". Lowercase Python `calendar.day_name` value, e.g. "monday".
	weekday?: string;
}

export type RecurringShiftWithApplyFromDate = {
	recurringShift: RecurringShift;
	/**
	 * should be in format yyyy-MM-dd
	 */
	applyFromDate: string;
};

export interface UpdateMultipleRecurringShiftRequest {
	existingRecurringShifts: Array<RecurringShiftWithApplyFromDate>;
	newRecurringShifts: Array<RecurringShift>;
	shiftsToRemove: Array<RecurringShiftWithApplyFromDate>;
	departmentId: string;
	source: ChangesSource;
}

export type UpdateMultipleRecurringShiftRequestResult = {
	allSucceeded: boolean;
};

export interface PostStaffMessageRequest {
	userIds: string[];
	message: string;
	tempId: string;
	addAppLink?: boolean;
}

export interface DayItem {
	shifts: NurseInShift[];
	shiftsActual: NurseInShift[];
	date: DateTime;
	requests: (OpenShiftRequest | ReplaceShiftRequest)[];
	dayStats: DayStats;
	dayStatsActual: DayStats;
	earliestTime: DateTime;
	latestTime: DateTime;
	pto: SpecificPreference[];
}

export interface NurseRef {
	nurseId: number;
	name: string;
	userId?: string;
}

export interface ActualHoursEmployee extends NurseRef {
	actualHours: number;
}

export interface NotScheduledEmployee extends ActualHoursEmployee {
	/** Complete punched shifts that can be added to the schedule. */
	shifts: NurseInShift[];
}

export interface NoPunchEmployee extends NurseRef {
	/** Scheduled shifts behind this attendance gap, including multiple shifts on the same day. */
	shifts: BaseNurseShift[];
}

export interface ShiftDeltaEmployee extends NurseRef {
	scheduledHours: number;
	actualHours: number;
	diffHours: number;
}

export interface PtoRef extends NurseRef {
	preferenceId: string;
	hours: number;
	reason?: string;
}

export interface OtEmployee extends NurseRef {
	otHours: number;
}

export interface AgencyEmployee extends NurseRef {
	agencyHours: number;
	missingPunch: boolean;
	agencyName?: string;
}

export interface CallInEmployee extends NurseRef {
	reason: string;
}

export interface PunchSummary {
	noPunch: NoPunchEmployee[];
	notScheduled: {
		totalScheduledHours: number;
		totalActualHours: number;
		totalDiffHours: number;
		employees: NotScheduledEmployee[];
	};
	excludedFromPpd: {
		totalActualHours: number;
		employees: ActualHoursEmployee[];
	};
	missingPunch: NurseRef[];
	shiftDelta: {
		totalDeltaHours: number;
		employees: ShiftDeltaEmployee[];
	};
	pto: PtoRef[];
	ot: {
		totalOtHours: number;
		employees: OtEmployee[];
	};
	agency: {
		totalAgencyHours: number;
		employees: AgencyEmployee[];
	};
	callIn: {
		totalCount: number;
		employees: CallInEmployee[];
	};
	staff: NurseRef[];
}

export type PunchSummaryCategory = "noPunch" | "notScheduled" | "excludedFromPpd" | "missingPunch" | "shiftDelta" | "pto" | "ot" | "callIn" | "agency";

/**
 * The rows of the weekly view's Punch Summary footer, in display order (APP-87). PTO and Excluded from
 * PPD are left out on purpose: neither is something to act on at a glance, and seven day cards of
 * them cost the week too much height. Both buckets are still computed, because the Day View's Actual
 * table groups by every category and must keep showing them.
 */
export const WEEKLY_PUNCH_SUMMARY_CATEGORY_ORDER: PunchSummaryCategory[] = ["noPunch", "missingPunch", "notScheduled", "ot", "shiftDelta", "agency", "callIn"];

export const CALL_IN_DEFAULT_REASON = "Unspecified";

export const EMPTY_PUNCH_SUMMARY: PunchSummary = {
	noPunch: [],
	notScheduled: {
		totalScheduledHours: 0,
		totalActualHours: 0,
		totalDiffHours: 0,
		employees: [],
	},
	excludedFromPpd: { totalActualHours: 0, employees: [] },
	missingPunch: [],
	shiftDelta: { totalDeltaHours: 0, employees: [] },
	pto: [],
	ot: { totalOtHours: 0, employees: [] },
	agency: { totalAgencyHours: 0, employees: [] },
	callIn: { totalCount: 0, employees: [] },
	staff: [],
};

export interface TimelineRows {
	[role: string]: {
		[row: number]: {
			key: string;
			shift: ShiftStaffTimes;
		}[];
	};
}

export enum AppPlatform {
	IOS = "ios",
	Android = "android",
}

export const APP_STORE_URL = "https://apps.apple.com/us/app/in-house-health/id6748960919";
export const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=inhouse.health";

/** Where users email InHouse support — the sign-in page, the admin menu and the maintenance screen all point here. */
export const SUPPORT_EMAIL = "support@inhouse.health";

/** Every admin role. Must match the backend `AdminRole` enum in `models/enums.py`. */
export const ADMIN_ROLES = ["Scheduler", "Admin", "Supervisor", "HR", "Viewer", "NursingLeadership", "Network"] as const;

export type AdminRole = (typeof ADMIN_ROLES)[number];

export type ExistingAdminUser = {
	id: string;
	firstName: string;
	phoneNumber?: string;
	username?: string;
	requiresMFA: boolean;
	isReadonly: boolean;
	hideWage: boolean;
	disableEditMasterSchedule: boolean;
	isActive: boolean;
	support: boolean;
	receiveDailyAdminNotifications: boolean;
	receiveActionRequiredAdminNotifications: boolean;
	sendDailyActualShiftsCheck: boolean;
	sendDailyPickupSummary: boolean;
	receiveSmsResponseNotifications: boolean;
};

export interface ActivityLogLastChangeResult {
	changeCount: number;
	latestChangeTime?: DateTime;
}
export interface GroupedActivityLogChangeResult {
	requests: ActivityLogLastChangeResult;
	nurses: ActivityLogLastChangeResult;
	departmentData: ActivityLogLastChangeResult;
	notifications: ActivityLogLastChangeResult;
	wage: ActivityLogLastChangeResult;
}

export interface StoredActivityLog {
	lastChange: DateTime;
}
export interface StoredGroupedActivityLog {
	requests: StoredActivityLog;
	nurses: StoredActivityLog;
	departmentData: StoredActivityLog;
	notifications: StoredActivityLog;
	wage: StoredActivityLog;
}

interface StoredActivityLogSerialized {
	lastChange: string;
}
export interface StoredGroupedActivityLogSerialized {
	requests: StoredActivityLogSerialized;
	nurses: StoredActivityLogSerialized;
	departmentData: StoredActivityLogSerialized;
	notifications: StoredActivityLogSerialized;
	wage: StoredActivityLogSerialized;
}

/** Where a user parked a draggable floating button, as a pixel delta from its anchored corner. */
export interface DraggableFabOffset {
	x: number;
	y: number;
}

export interface PickupSuggestionState {
	/** ISO date (yyyy-MM-dd) of the day the weekly cycle started */
	lastShownDate: string;
	/** User dismissed the drawer on the show day */
	dismissed: boolean;
	/** User clicked quick apply — hide entirely until next cycle */
	quickApplied: boolean;
	/** Number of shifts picked via quick apply in this cycle */
	pickedCount?: number;
	/** IDs of shifts the user applied to — persisted so they show as "Pending" after refresh */
	appliedShiftIds?: string[];
	/** IDs of the originally suggested shifts — persisted so the same suggestions survive refresh */
	pinnedShiftIds?: string[];
}

export type InHouseRefreshOptions = {
	avoidRefreshFor: {
		departmentData?: boolean;
		nursesData?: boolean;
		requestsData?: boolean;
		notificationsData?: boolean;
		wageData?: boolean;
	};
};

export type GroupedActivityLogClientTimestamps = {
	requests: string;
	nurses: string;
	departmentData: string;
	notifications: string;
	wage: string;
};

export interface ReplaceAbsentNurse {
	absentNurse: any;
	replacementNurse: any | null;
	dateFrom: DateTime;
	dateTo: DateTime | null;
	reason: string;
	postShifts: boolean;
}

export enum ProfileMode {
	NEW = 0,
	VIEW = 1,
	EDIT = 2,
}

export enum RecommendationType {
	PostShift = "post_shift",
	ApproveRequest = "approve_request",
	ApproveReplacementRequest = "approve_replacement_request",
	RemoveStaff = "remove_staff",
}

export enum Severity {
	High = "high",
	Medium = "medium",
	Low = "low",
}

export interface RecommendationExtraData {
	data: unknown;
}

export interface RequestExtraData extends RecommendationExtraData {
	data: string;
}

export interface PostShiftDetails {
	recommended_role_groups: NurseFullRole[];
	recommended_job_id?: string;
	shift_type: ShiftType;
	recommended_staff_count: number;
	recommended_start_time: string; // ISO datetime string
	recommended_end_time: string; // ISO datetime string
	recommended_location?: string;
}

export interface PostShiftExtraData extends RecommendationExtraData {
	data: PostShiftDetails[];
}

export interface Recommendation {
	id: string;
	type: RecommendationType;
	date: DateTime;
	severity: Severity;
	extra_data: RecommendationExtraData;
}

export enum ClientType {
	WEB = "web",
	MOBILE = "mobile-app",
}

export interface RecommendationParams {
	departmentId: string;
	startDate: string;
	endDate: string;
}

export interface CalledOffShift {
	id: string;
	nurseId: number;
	departmentId: string;
	startTime: DateTime;
	endTime: DateTime;
	updatedAt: DateTime;
	deletedAt?: DateTime;
	// When a manager recorded the moment the nurse actually called in (the removal dialog's "Call-in
	// date"). Absent for the self-serve channels (app / SMS / phone), which never ask — use
	// `resolveCallInTime` rather than reading this directly.
	callInTime?: DateTime;
	shiftType: ShiftType;
	message?: string;
	removalMessage?: string;
	// The API sends `null` for removals recorded before the source was tracked (a large share of history).
	deletedSource?: ChangesSource | null;
}

export interface CalledOffShiftResponse {
	results: CalledOffShift[];
	hasMore: boolean;
}

export interface CallInReason {
	id: string;
	departmentId: string;
	label: string;
	sortOrder: number;
	archivedAt?: string;
	/** Hiring: whether a call-off with this reason counts in the Absences row — set from the Hiring page's checklist. */
	countsAsAbsence: boolean;
}

export type GetLimitedCallOffs = {
	nurseIds?: number[];
	page: number;
	pageSize: number;
	fromDate?: DateTime;
	toDate?: DateTime;
	includedMessages?: string[];
	/** Skip the fetch entirely (default true), same purpose as TanStack Query's own `enabled`. */
	enabled?: boolean;
};

export type CancelCallOffs = {
	departmentId: string;
	calledOffShifts: CalledOffShift[];
};

export interface InvalidActualNurses {
	missingFromScheduled: BaseNurseShift[];
	missingPunchIn: BaseNurseShift[];
	missingPunchOut: BaseNurseShift[];
	missingBothPunches: BaseNurseShift[];
}

export type InvalidActualNurseType = keyof InvalidActualNurses;

export enum WeeklyViewOption {
	V1 = "V1",
	V2 = "V2",
}

export enum DailyViewOption {
	standard = "standard",
	gaps = "gaps",
}

export enum WeeklyGroupingType {
	Role = "role",
	RoleGroups = "role_groups",
	Jobs = "jobs",
}

export enum HppdSeparationType {
	None = "none",
	Role = "role",
	RoleGroups = "role_groups",
	Jobs = "jobs",
}

export enum DailyPrintoutGroupingOption {
	START_TIME = "START_TIME",
	ROLE = "ROLE",
	LOCATION = "LOCATION",
	FLOOR = "FLOOR",
	NONE = "NONE",
	CUSTOM = "CUSTOM",
}

export type DailyPrintoutGrouping = DailyPrintoutGroupingOption[];

export type CustomSheetTabSelectionMode = "role_groups" | "jobs";

export interface CustomSheetTab {
	id: string;
	name?: string;
	selectionMode: CustomSheetTabSelectionMode;
	selectedKeys: string[];
	breakByShift: boolean;
}

export interface CustomSheetTabsConfig {
	tabs: CustomSheetTab[];
}

export interface PrintoutAdmin {
	name: string;
	role: string;
}

export interface PrintoutShowAdminsConfig {
	enabled: boolean;
	admins: PrintoutAdmin[];
	/** 1-indexed worksheet column where the first pair's role cell starts. */
	startColumn: number;
	/** Number of empty columns between adjacent pairs. */
	pairSpacing: number;
}

export enum LocationMode {
	None = "none",
	Floor = "floor",
	Unit = "unit",
	Both = "both",
	Auto = "auto",
}

export enum PrintoutOrientation {
	Landscape = "landscape",
	Portrait = "portrait",
}

export interface PrintoutColumnSetting {
	type: string;
	enabled: boolean;
	sortDirection?: "asc" | "desc";
}

export type PrintoutCustomizableColumnType = "location" | "name" | "certification" | "shiftTime" | "role" | "notes" | "initials" | "floor" | "scheduledHours";

export interface CustomTimeGroup {
	id: string;
	startMinute: number;
	endMinute: number;
	name: string;
}

export interface CustomTimeGroupsConfig {
	enabled: boolean;
	groups: CustomTimeGroup[];
}

export interface CustomColumnNamesConfig {
	enabled: boolean;
	labels: Partial<Record<PrintoutCustomizableColumnType, string>>;
}

export interface StaffingSettings {
	groupRoles: boolean;
	weeklyGroupingType: WeeklyGroupingType;
	disableTimeGroupsOverlaps: boolean;
	overlapShifts: boolean;
	view: WeeklyViewOption;
	printNotes: boolean;
	printGaps: boolean;
	showRoleGroupStats?: boolean;
	dailyView: DailyViewOption;
	dailyPrintoutGrouping: DailyPrintoutGrouping;
	censusByLocation: LocationMode;
	divideGroupsByPage?: boolean;
	separateSheetsBy?: DailyPrintoutGroupingOption;
	showPtoSheet?: boolean;
	showCallOutsSheet?: boolean;
	printoutSortBy?: PrintoutColumnSetting[];
	printoutColumns?: PrintoutColumnSetting[];
	staffPerRow?: number;
	rowGroupingBy?: string[];
	columnGroupingBy?: string[] | null;
	spaceBetweenGroups?: boolean;
	includeTimeOffReason?: boolean;
	printoutOrientation: PrintoutOrientation;
	printoutFitToPage: boolean;
	printoutFitToWidth: number;
	printoutFitToHeight: number;
	hideHppdNursingHours: boolean;
	printoutColumnWidths?: Record<string, number>;
	flagNotOnboarded?: boolean;
	flagAgency?: boolean;
	mergeSimilarShiftTimes?: boolean;
	alignGroupTimesToPopular?: boolean;
	customTimeGroups: CustomTimeGroupsConfig;
	appendRoleToName: boolean;
	hideColumnNames: boolean;
	appendEmptyColumns: number;
	customColumnNames: CustomColumnNamesConfig;
	customLocationOrder?: { enabled: boolean; orderedLocationNames: string[] };
	customSheetTabs?: CustomSheetTabsConfig;
	showAdmins?: PrintoutShowAdminsConfig;
	showLocationCensus: boolean;
	showDhppd?: boolean;
	showNhppd?: boolean;
	showActualStaffColumn?: boolean;
	showActualHoursColumn?: boolean;
	hppdIncludeTrainees?: boolean;
	hppdSeparationType: HppdSeparationType;
	hideHppdDailyView: boolean;
	wantedMode?: StatsMode;
	hiddenLocations: string[];
	// Management Center calculation settings (centralized settings menu)
	mcLaborCost: boolean;
	mcIncludeHolidays: boolean;
	mcIncludeLateScheduled: boolean;
	mcOtMode: OtMode;
	mcEstimateMissingWage: boolean;
	/** Weekly schedule export panel settings (Profile → Schedule → Export), stored as one JSON document. */
	weekScheduleExport: PersistedWeekScheduleExportSettings;
	/** Staffing export popup, monthly tab. Separate document from the Profile weekly export above. */
	staffingMonthlyExport: PersistedMonthlyExportSettings;
	staffingWeeklyExport: PersistedWeeklyExportSettings;
	/** `jobId → colour slot` this user pinned (or the department default); a job missing here is automatic. */
	jobColors: Record<string, number>;
}

export interface UserSettings {
	id?: string;
	user_id: string | null;
	department_id: string;
	group_roles: boolean;
	overlap_shifts: boolean;
	weekly_view_version: string;
	daily_view_type: string;
	print_notes: boolean;
	print_gaps: boolean;
	show_role_group_stats?: boolean;
	divide_groups_by_page?: boolean;
	separate_sheets_by?: string;
	show_pto_sheet?: boolean;
	show_call_outs_sheet?: boolean;
	daily_printout_grouping: string;
	secondary_daily_printout_grouping: string;
	daily_printout_grouping_list: string[];
	printout_sort_by?: PrintoutColumnSetting[];
	printout_columns?: PrintoutColumnSetting[];
	staff_per_row?: number;
	row_grouping_by?: string[];
	column_grouping_by?: string[] | null;
	printout_space_between_groups?: boolean;
	printout_include_time_off_reason?: boolean;
	printout_hide_hppd_nursing_hours?: boolean;
	printout_column_widths?: Record<string, number>;
	printout_flag_not_onboarded?: boolean;
	printout_flag_agency?: boolean;
	printout_merge_similar_shift_times?: boolean;
	printout_fit_to_page?: boolean;
	printout_fit_to_width?: number;
	printout_fit_to_height?: number;
	printout_align_group_times_to_popular?: boolean;
	printout_custom_time_groups: CustomTimeGroupsConfig;
	printout_append_role_to_name: boolean;
	printout_hide_column_names: boolean;
	printout_append_empty_columns: number;
	printout_custom_column_names: CustomColumnNamesConfig;
	custom_location_order?: { enabled: boolean; orderedLocationNames: string[] };
	custom_sheet_tabs?: CustomSheetTabsConfig | null;
	show_admins?: PrintoutShowAdminsConfig | null;
	show_location_census?: boolean;
	printout_show_dhppd?: boolean;
	printout_show_nhppd?: boolean;
	printout_show_actual_staff_column?: boolean;
	printout_show_actual_hours_column?: boolean;
	printout_hppd_include_trainees?: boolean;
	printout_orientation?: PrintoutOrientation;
	hidden_locations: string[] | null;
	weekly_grouping_type: string;
	disable_time_groups_overlaps: boolean;
	census_by_location: LocationMode;
	hppd_separation_type: string;
	hide_hppd_daily_view: boolean;
	wanted_mode?: string;
	mc_labor_cost: boolean;
	mc_include_holidays: boolean;
	mc_include_late_scheduled: boolean;
	mc_ot_mode: string;
	mc_estimate_missing_wage: boolean;
	recurring_shift_default_tab: string;
	/** NOT NULL column with a server default — always present when the API returned settings at all. */
	week_staff_schedule_export_settings: PersistedWeekScheduleExportSettings;
	staffing_monthly_export_settings: PersistedMonthlyExportSettings;
	staffing_weekly_export_settings: PersistedWeeklyExportSettings;
	/** `jobId → colour slot` pinned by this user (or the department default); a missing job is automatic. */
	job_colors: Record<string, number>;
	/** Short role code → colour slot selected by this user or the department default. */
	role_colors: Record<string, number>;
	created_at?: string;
	updated_at?: string;
}

export interface UpdateUserSettingsRequest {
	group_roles?: boolean;
	weekly_grouping_type?: string;
	disable_time_groups_overlaps?: boolean;
	overlap_shifts?: boolean;
	weekly_view_version?: string;
	daily_view_type?: string;
	print_notes?: boolean;
	print_gaps?: boolean;
	showRoleGroupStats?: boolean;
	divideGroupsByPage?: boolean;
	separateSheetsBy?: string;
	showPtoSheet?: boolean;
	showCallOutsSheet?: boolean;
	daily_printout_grouping?: string;
	secondary_daily_printout_grouping?: string;
	daily_printout_grouping_list?: string[];
	printoutSortBy?: PrintoutColumnSetting[];
	printoutColumns?: PrintoutColumnSetting[];
	staffPerRow?: number;
	rowGroupingBy?: string[];
	columnGroupingBy?: string[] | null;
	spaceBetweenGroups?: boolean;
	includeTimeOffReason?: boolean;
	hideHppdNursingHours?: boolean;
	printoutColumnWidths?: Record<string, number>;
	flagNotOnboarded?: boolean;
	flagAgency?: boolean;
	mergeSimilarShiftTimes?: boolean;
	alignGroupTimesToPopular?: boolean;
	customTimeGroups?: CustomTimeGroupsConfig;
	appendRoleToName?: boolean;
	hideColumnNames?: boolean;
	appendEmptyColumns?: number;
	customColumnNames?: CustomColumnNamesConfig;
	customLocationOrder?: { enabled: boolean; orderedLocationNames: string[] };
	customSheetTabs?: CustomSheetTabsConfig;
	showAdmins?: PrintoutShowAdminsConfig;
	showLocationCensus?: boolean;
	showDhppd?: boolean;
	showNhppd?: boolean;
	showActualStaffColumn?: boolean;
	showActualHoursColumn?: boolean;
	hppdIncludeTrainees?: boolean;
	printoutOrientation?: PrintoutOrientation;
	printoutFitToPage?: boolean;
	printoutFitToWidth?: number;
	printoutFitToHeight?: number;
	hidden_locations?: string[];
	censusByLocation?: LocationMode;
	hppdSeparationType?: string;
	hideHppdDailyView?: boolean;
	wantedMode?: string;
	mcLaborCost?: boolean;
	mcIncludeHolidays?: boolean;
	mcIncludeLateScheduled?: boolean;
	mcOtMode?: string;
	mcEstimateMissingWage?: boolean;
	recurringShiftDefaultTab?: string;
	/** camelCase on purpose: `toOptimisticUserSettingsEntries` (utils/userSettingsCacheKeys) converts it for the optimistic cache write. */
	weekStaffScheduleExportSettings?: PersistedWeekScheduleExportSettings;
	staffingMonthlyExportSettings?: PersistedMonthlyExportSettings;
	staffingWeeklyExportSettings?: PersistedWeeklyExportSettings;
	jobColors?: Record<string, number>;
	roleColors?: Record<string, number>;
	saveAsDefault?: boolean;
	/**
	 * With `saveAsDefault`: the saver's saved printouts (Printout 2+) become the department's too, replacing
	 * everyone's, and everyone goes back to Printout 1. Absent everywhere but the printout popup.
	 */
	includePrintoutPresets?: boolean;
}

export type UpdateSingleDepartmentLocationBody = {
	updatedLocation: DepartmentLocation;
	applyFromDate: string;
};
export type UpdateSingleDepartmentLocationResult = {
	updatedLocation: DepartmentLocation;
	amountOfUpdatedShifts: number;
};

/**
 * Which scheduled and worked shifts a location rename reaches (its template rows and recurring patterns always take the
 * new name): every one, past ones included (`includeHistorical`), or those from the department-local schedule day
 * `fromDate` (`yyyy-MM-dd`) on. The API refuses both together.
 */
export type RenameDepartmentLocationFrom = { includeHistorical: true } | { includeHistorical: false; fromDate: string };

/**
 * Rename every `shift_template` row that shares a location name in the department (group-level rename).
 * Consumed by `useRenameDepartmentLocation` → `POST /departments/locations/rename`.
 */
export type RenameDepartmentLocationBody = {
	oldName: string;
	newName: string;
} & RenameDepartmentLocationFrom;
export type RenameDepartmentLocationResult = {
	renamedTemplateRows: number;
	updatedRecurringShifts: number;
	updatedShifts: number;
};

/**
 * Body of `POST /departments/locations/emr` — set the EMR link for a whole location NAME group.
 * `unit`/`floor` are nullable so "Not set" is expressible, and `rooms` is the complete desired set
 * applied to every row in the group (so the group can never disagree with itself afterwards).
 */
export type SetLocationEmrBody = {
	locationName: string;
	unit: string | null;
	floor: string | null;
	rooms: string[];
};
export type SetLocationEmrResult = {
	updatedTemplateRows: number;
	updatedRooms: number;
};

/**
 * One row a location write would newly make a duplicate (backend `DuplicateMatch`, APP-780). Also the
 * element shape of the 409 `details.duplicates` (`schedule.template.duplicate.location`, and
 * `schedule.template.rename.duplicate.location` for a rename).
 *
 * - `candidateId` is always a new or changed row of the batch. A row resent unchanged is judged as the
 *   saved row, so it never appears here, though it can appear in another match's `duplicateOfIds`. The v1
 *   Schedule Template create resends every department row in `existing`; the Department Configuration
 *   save sends only the rows the user touched, and one touched outside the rule's columns (an EMR unit,
 *   say) comes back unchanged.
 * - `duplicateOfIds` are the rows it would newly match: saved rows, OR other candidates in the same batch
 *   (two new rows can match each other). Only matches its saved version didn't already have count, except
 *   that an exact copy always counts (no two saved rows can be exact copies).
 *
 * Consumers map `candidateId` back to their own row; don't assume it is the row the user just edited
 * (a batch can hold several changed rows).
 */
export interface DuplicateMatch {
	candidateId: string;
	duplicateOfIds: string[];
}

/** Body of `POST /departments/locations/duplicates`: the rows a save is about to write, judged as one batch. */
export interface FindDuplicateLocationsBody {
	candidates: DepartmentLocation[];
}

/** Every candidate that would add a duplicate; empty when the save would be accepted. */
export interface FindDuplicateLocationsResult {
	duplicates: DuplicateMatch[];
}

/**
 * Body of `POST /departments/locations/delete`: the template rows to delete, all from the department in
 * the query string. At least one id and no id twice; the backend deletes all of them or none.
 */
export interface DeleteDepartmentLocationsBody {
	ids: string[];
}

/** How many rows the delete removed; one per id sent when it succeeds. */
export interface DeleteDepartmentLocationsResult {
	deletedRows: number;
}

export enum RecommendationOpenSource {
	WeeklyView = "weekly_view",
	RecommendationsClick = "recommendations_click",
	SidePanel = "side_panel",
	DayView = "day_view",
}

export interface NursesToRemoveParams {
	departmentId: string;
	date: DateTime;
}

export interface CensusPointInTimesParams {
	departmentId: string;
	date: DateTime;
}

export interface RequestApprovalNursesParams {
	departmentId: string;
	requestId: string;
}

export enum PtoTypes {
	Payed = "Paid time off",
	Unpayed = "Unpaid time off",
	Makeup = "Makeup timeoff",
}

// In models.tsx - ordered from highest to lowest priority
export const NurseRolePriorityOrder: NurseRole[] = [
	NurseRole.RN,
	NurseRole.LPN,
	NurseRole.LVN,
	NurseRole.CNA,
	NurseRole.CMA,
	NurseRole.RNA,
	NurseRole.MT,
	NurseRole.NAS,
	NurseRole.RA,
	NurseRole.RD,
	NurseRole.DA,
	NurseRole.CK,
	NurseRole.CA,
	NurseRole.MD,
	NurseRole.DO,
	NurseRole.NP,
	NurseRole.ACN,
	NurseRole.PO,
	NurseRole.UC,
	NurseRole.HK,
	NurseRole.ACT,
	NurseRole.REC,
	NurseRole.MDS,
	NurseRole.HA,
	NurseRole.RLPN,
	NurseRole.CKA,
	NurseRole.LNA,
	NurseRole.UDF,
	NurseRole.RT,
	NurseRole.LCSW,
];

export const getRolePriority = (roles: string[]): number => {
	for (let i = 0; i < NurseRolePriorityOrder.length; i++) {
		const role = NurseRolePriorityOrder[i];
		if (roles.includes(role)) {
			return NurseRolePriorityOrder.length - i; // Higher index = lower priority number
		}
	}
	return 0;
};

export interface Request {
	id: string;
	created_by: string;
	request_for?: number | null;
	request_for_shift?: string;
	request_target_shift?: string;
	shift_type: string;
	shift_start: DateTime;
	shift_end: DateTime;
	location?: string;
	source?: RequestSource | null;
	department_id: string;
	needed_nurses: number;
	created_at: DateTime;
	roles: string[];
	request_data?: Record<string, unknown>[];
	reason?: string;
	request_type: NotificationType;
	accepted_nurses?: number[];
	status: RequestStatus;
	charge_only: boolean;
	bonus: number;
	agency_shift_ids?: string[];
	agency_source_name?: string;
	agency_post_delay_hours?: number;
	job_id?: string;
	sub_role?: NurseRole;
	incentive_configuration_id?: string;
	current_bonus?: number;
	hours_left?: number | null;
	incentive_windows?: IncentivePlan[];
	auto_close_at?: DateTime | null;
	auto_close_uses_agency_terms?: boolean | null;
	agency_posting_failures?: AgencyPostingFailure[];
}

/** Discriminator for the agency write that failed; mirrors the backend ``AgencyQueueAction`` enum
 *  values surfaced on a non-fatal failure. The FE renders ``agency.failed.<action>``. */
export type AgencyPostingAction = "create" | "cancel" | "edit_times" | "update_needed_nurses" | "manual_removal" | "approve";

/** One non-fatal agency-write failure. The orchestration layer (which knows the agency + action)
 *  emits these; the FE renders one translated warning snackbar per failure. */
export interface AgencyPostingFailure {
	agency: string;
	action: AgencyPostingAction;
}

export enum EditDepartmentLocationConfigMode {
	Edit = "Edit",
	Create = "Create",
}

export enum NotificationSettingsType {
	NewOpenShift = "NewOpenShift",
	ShiftChanges = "ShiftChanges",
	ShiftStartEnd = "ShiftStartEnd",
	PersonalRequests = "PersonalRequests",
	DailyNotifications = "DailyNotifications",
	DailyPickupSummary = "DailyPickupSummary",
}

export enum NotificationField {
	Email = "email",
	PushNotifications = "push_notifications",
	SMS = "sms",
}

export const NOTIFICATION_TYPE_LABELS: Record<NotificationSettingsType, string> = {
	[NotificationSettingsType.NewOpenShift]: "notif.new.open.shift",
	[NotificationSettingsType.ShiftChanges]: "notif.shift.changes",
	[NotificationSettingsType.ShiftStartEnd]: "notif.shift.start.end",
	[NotificationSettingsType.PersonalRequests]: "notif.personal.requests",
	[NotificationSettingsType.DailyNotifications]: "notif.daily.notifications",
	[NotificationSettingsType.DailyPickupSummary]: "notif.daily.pickup.summary",
};

export type ShiftRequest = OpenShiftRequest | ReplaceShiftRequest;

export enum AgentActionType {
	Regular = "regular",
	Message = "message",
	Call = "call",
	Approach = "approach",
	Agency = "agency",
	Evaluated = "evaluated",
	PreferredAgency = "preferred_agency",
}

export enum AgentSource {
	ShiftPickup = "shift_pickup",
	CallOff = "call_off",
}

// https://www.twilio.com/docs/voice/api/call-resource#call-status-values
export enum CallStatus {
	Queued = "queued",
	Ringing = "ringing",
	InProgress = "in-progress",
	Completed = "completed",
	Busy = "busy",
	NoAnswer = "no-answer",
	Failed = "failed",
	Canceled = "canceled",
}

export enum AgentStaffActionStatus {
	Pending = "pending",
	Completed = "completed",
	Paused = "paused",
	NotInitiated = "not_initiated",
	Loading = "loading",
	Skipped = "skipped",
}

export enum AgentStaffAction {
	Message = "message",
	Call = "call",
}

export enum AgentReportView {
	Grouped = "grouped",
	Raw = "raw",
}

interface AgentActionTypeDisplay {
	label: string;
	icon: React.ElementType;
	notification: (count: number, isFuture: boolean, nurseName?: string, negativeResponded?: number) => string;
	successMessage: (count: number, nurseName?: string) => string;
}

export const AgentActionTypeDisplay: Record<AgentActionType, AgentActionTypeDisplay> = {
	[AgentActionType.Regular]: {
		label: "Regular",
		icon: NotificationsIcon,
		notification: (count: number, isFuture: boolean, _nurseName?: string, negativeResponded?: number) => {
			if (isFuture) {
				return `Future Reminder (${count} staff)`;
			}
			// History items: show "waiting for responses" or "Y/X staff" format
			if (negativeResponded !== undefined && negativeResponded > 0) {
				return `Remindered (${negativeResponded}/${count} staff)`;
			}
			return `Waiting for responses (${count} staff)`;
		},
		successMessage: (count: number, _nurseName?: string) => `Notifications sent to ${count} staff`,
	},
	[AgentActionType.Message]: {
		label: "Message",
		icon: MessageSharpIcon,
		notification: (count: number, isFuture: boolean, _nurseName?: string, negativeResponded?: number) => {
			if (isFuture) {
				return `Message to ${count} staff`;
			}
			// History items: show "waiting for responses" or "Y/X staff" format
			if (negativeResponded !== undefined && negativeResponded > 0) {
				return `Responded (${negativeResponded}/${count} staff)`;
			}
			return "No response yet";
		},
		successMessage: (count: number, _nurseName?: string) => `Messages sent to ${count} staff`,
	},
	[AgentActionType.Call]: {
		label: "Call",
		icon: PhoneIcon,
		notification: (count: number, isFuture: boolean, nurseName?: string, negativeResponded?: number) => {
			if (nurseName) {
				// For history items with nurse name who didn't answer
				if (!isFuture && negativeResponded !== undefined && negativeResponded > 0) {
					return `Called ${nurseName} (didn't answer)`;
				}
				return isFuture ? `Call ${nurseName}` : `Called ${nurseName}`;
			}
			if (isFuture) {
				return `Scheduled Call ${count} staff`;
			}
			if (negativeResponded !== undefined && negativeResponded > 0) {
				return `Called (${negativeResponded}/${count} staff)`;
			}
			return `Waiting for responses (${count} staff)`;
		},
		successMessage: (count: number, nurseName?: string) => {
			if (nurseName) {
				return `Scheduled Call ${nurseName}`;
			}
			return `Called ${count} staff`;
		},
	},
	[AgentActionType.Approach]: {
		label: "Approach",
		icon: DirectionsRunSharpIcon,
		notification: (count: number, isFuture: boolean, _nurseName?: string, _negativeResponded?: number) => "Approach staff in facility",
		successMessage: (count: number, _nurseName?: string) => "Approached staff in facility",
	},
	[AgentActionType.PreferredAgency]: {
		label: "PreferredAgency",
		icon: HouseOutlinedIcon,
		notification: (count: number, isFuture: boolean, _nurseName?: string, _negativeResponded?: number) => "Post to Shift Key",
		successMessage: (count: number, _nurseName?: string) => "Posted to SK",
	},
	[AgentActionType.Agency]: {
		label: "Agency",
		icon: HouseOutlinedIcon,
		notification: (count: number, isFuture: boolean, _nurseName?: string, _negativeResponded?: number) => "Post to All Agencies",
		successMessage: (count: number, _nurseName?: string) => "Posted to All Agencies",
	},
	[AgentActionType.Evaluated]: {
		label: "Evaluated",
		icon: SkipNextIcon,
		notification: (count: number, isFuture: boolean, _nurseName?: string, _negativeResponded?: number) => "Evaluated (no action)",
		successMessage: (count: number, _nurseName?: string) => {
			throw new Error("Evaluated (no action) does not have a success message");
		},
	},
};

// TODO: Remove
export interface AgentActionHistory {
	created_at: string;
	action_type: AgentActionType;
	eligible_nurses_count?: number;
	target_notification_count?: number | null;
	notification_percentage?: number | null;
	skip_reason?: string | null;
	nurse_actions: Array<{
		user_id: string;
		nurse_id: number;
		score: number;
	}> | null;
	is_dry_run: boolean;
	negativeResponded?: number;
}

export interface DepartmentActionMetadata {
	agent_action_id: string;
	status: AgentStaffActionStatus;
	needed_nurses: number;
	created_at: string;
}

export interface RequestActionsMetadata {
	request_id: string;
	actions: DepartmentActionMetadata[];
	status: AgentStaffActionStatus;
	needed_nurses: number;
	shift_start: string;
	shift_end: string;
	roles: string[];
	approved_count: number;
	calls_amount: number;
	completed_calls_amount: number;
	message_amount: number;
	job_id?: string;
}

/**
 * One flat per-interaction row (a single call or text the agent made to a nurse), returned only in
 * `raw` view mode. Shift-level fields (shift_start/end, roles, job_id, agent_started_at,
 * needed_nurses, approved_count, request_status) are stitched onto every interaction row of a shift
 * by the backend, so cell rendering needs no extra joins. Mirrors the backend `RawInteractionRow`.
 */
export interface RawInteractionRow {
	request_id: string;
	shift_start: string;
	shift_end: string;
	roles: string[];
	job_id: string | null;
	/** Earliest agent-action time for the shift → drives the per-shift time-before-shift. */
	agent_started_at: string | null;
	nurse_id: number;
	interaction_type: AgentStaffAction;
	/** Calls: Twilio call status; messages: nurse-action lifecycle (`completed`/`pending`/`canceled`). */
	interaction_status: string;
	/** Twilio call status; null for SMS. */
	response_status: CallStatus | null;
	action_time: string | null;
	/** This nurse's requests_status == APPROVED. */
	is_approved: boolean;
	/** This nurse's confirmation time; null if they didn't confirm. */
	accepted_at: string | null;
	needed_nurses: number;
	approved_count: number;
	request_status: AgentStaffActionStatus;
}

export interface DepartmentActionsMetadataResponse {
	requests: RequestActionsMetadata[];
	/** Populated only in `raw` view mode; the grouped `requests` payload is empty then. */
	raw_interactions?: RawInteractionRow[];
}

export interface FlatNurseAction {
	id: string;
	user_id: string;
	nurse_id: number;
	score: number;
	type: string;
	status: string;
	action_time: string | null;
	agent_action_id: string;
	response_status?: CallStatus | null;
	is_approved: boolean;
	accepted_at: string | null;
}

export interface SpecificPreferencePayload {
	id: string;
	nurse_id: number;
	type: Preferences.Prefer | Preferences.Avoid;
	data?: string;
	start_date: string;
	end_date: string;
	hours: number;
	reason: PtoTypes | string;
	notes: string;
	post_shifts: boolean;
	// Exact-times (partial-day) PTO: "HH:mm" wall-clock times within the single
	// staffing date start_date (requires start_date === end_date).
	start_time?: string;
	end_time?: string;
}

export interface CreateSpecificPreferenceOptions {
	preference: SpecificPreferencePayload;
	source: ChangesSource;
}

export interface UpdateSpecificPreferenceResult {
	shiftsToAdd: NurseInShift[];
	shiftsToRemove: string[];
}

// How a time-off window overlaps a shift. Mirrors the BE `TimeOffOverlap` enum — the rule itself
// lives in `backend/utils.py::classify_time_off_overlap` and is only ever evaluated there, so this
// is a wire vocabulary, not a copy of the logic.
export type TimeOffOverlapKind = "none" | "remove" | "trim_start" | "trim_end" | "split";

// One scheduled shift overlapping a prospective time-off window, tagged with what saving that time
// off would do to it. `overlap: "none"` means the shift overlaps the window but the save leaves it
// alone.
//
// `startTime`/`endTime` are Luxon `DateTime`, NOT the ISO strings the API emits: the axios response
// interceptor runs `convertIsoDates` over every response body (`networkWrapper.tsx`), so they are
// already parsed by the time a caller sees them. Typing them as `string` compiles and then fails at
// runtime. Mirrors the BE `TimeOffImpactShift`.
export interface TimeOffImpactShift {
	id: string;
	startTime: DateTime;
	endTime: DateTime;
	overlap: TimeOffOverlapKind;
	// Set when the row is itself a remnant of an earlier trim. The post-shifts count excludes these:
	// a leftover segment is removed without being re-posted.
	parentShiftId: string | null;
}

// GET /team/departments/{departmentId}/nurse/{nurseId}/time-off-impact.
//
// `shifts` is EVERY non-deleted shift of the nurse in the department overlapping [windowStart,
// windowEnd), ascending by `startTime`; a shift that merely touches a boundary is absent entirely.
// Mirrors the BE `TimeOffImpactResponse`.
export interface TimeOffImpactResponse {
	shifts: TimeOffImpactShift[];
}

/** What deactivating a nurse on a day would remove from their schedule in one department. Mirrors the BE
 * `DeactivationImpactResponse`. */
export interface DeactivationImpactResponse {
	hasFutureShifts: boolean;
	hasFutureRecurringShifts: boolean;
}

/** A nurse as the Team page roster carries them: everything but their time off and preferences, which that page
 * never reads. Mirrors the backend `TeamRosterNurse`. */
export type TeamNurse = Omit<Nurse, "employeeSpecificPreferences" | "employeeWeeklyPreferences" | "baseWages">;

/** The nurses each schedule-based Team stat card counts, worked out by the server from the whole month. Mirrors the
 * backend `TeamStatsResponse`. */
export interface TeamStatsResponse {
	workedLastMonthNurseIds: number[];
	/** Scheduled from tomorrow through the same date next month; Management Center's staff app card reads it. */
	worksNextMonthNurseIds: number[];
	recurringShiftsNurseIds: number[];
	/** Null when the department has no self-scheduling period, so the card is hidden. */
	selfSchedulingSubmittedNurseIds: number[] | null;
}

// A shift the save will actually change — the `overlap !== "none"` subset of `TimeOffImpactShift`.
export interface AffectedShift extends Omit<TimeOffImpactShift, "overlap"> {
	overlap: Exclude<TimeOffOverlapKind, "none">;
}

// GET /team/departments/{departmentId}/nurse/{nurseId}/pto-trimmed-segments.
// Mirrors the BE `PtoTrimmedSegmentsResponse`.
export interface PtoTrimmedSegmentsResponse {
	hasTrimmedSegments: boolean;
}

export enum TimeOffReasons {
	// Medical
	Sick = "Sick",
	PreSick = "Pre-Sick",
	MedicalLeave = "Medical Leave",

	// Protected / Legal
	FMLA = "FMLA",
	Jury = "Jury",
	Bereavement = "Bereavement",

	// Personal
	Vacation = "Vacation",
	Holiday = "Holiday",
	Personal = "Personal",
	PersonalLeave = "Personal Leave",

	// Administrative / Disciplinary
	Suspension = "Suspension",

	// Generic / Fallback
	TimeOff = "Time Off",
	Other = "Other",
}

export interface UserPushMessage {
	id: number;
	pushId: string;
	userId: string;
	title: string;
	body: string;
	dateSent: DateTime;
	platform: "ios" | "android";
	status: "sent" | "failed";
}

export type UserPushMessagesCollection = Record<string, UserPushMessage[]>;

export interface CombinedMessagesResponse {
	sms: UserSentMessage[];
	push: UserPushMessage[];
	threadReadStatuses: Record<string, string>;
}

export interface StaffCommunicationSummaryResponse {
	sms: UserSentMessage[];
	push: UserPushMessage[];
	unreadCounts: Record<string, number>;
}

export interface StaffCommunicationHistoryResponse {
	sms: UserSentMessage[];
	push: UserPushMessage[];
	hasMore: boolean;
}

export type ManagementCenterDateToValueAggregation = Record</*date string*/ string, number>;

export type ManagementCenterStringToDatesAndValueAggregation = Record<string, ManagementCenterDateToValueAggregation>;

export type ManagementCenterStaffMixResponse = {
	census: ManagementCenterDateToValueAggregation;
	overtimePercent: ManagementCenterDateToValueAggregation;
	overtimeCost: ManagementCenterDateToValueAggregation;
	dailyOtCost: ManagementCenterDateToValueAggregation;
	weeklyOtCost: ManagementCenterDateToValueAggregation;
	/** DAT-289. Optional: absent from payloads produced before the field shipped. */
	consecutiveOtCost?: ManagementCenterDateToValueAggregation;
	/** DAT-289: the department configures a consecutive-day rule — drives column visibility. */
	hasConsecutiveOtRule?: boolean;
	dailyOtPercent: ManagementCenterDateToValueAggregation;
	weeklyOtPercent: ManagementCenterDateToValueAggregation;
	agencyPercent: ManagementCenterDateToValueAggregation;
	agencyCost: ManagementCenterDateToValueAggregation;
	ptoCost: ManagementCenterDateToValueAggregation;
};

export type ManagementCenterHPPDResponse = {
	census: ManagementCenterDateToValueAggregation;
	hppdScheduled: ManagementCenterDateToValueAggregation;
	hppdActual: ManagementCenterDateToValueAggregation;
	targetHppd: ManagementCenterDateToValueAggregation;
};

export type ManagementCenterPPDResponse = {
	census: ManagementCenterDateToValueAggregation;
	ppdScheduled: ManagementCenterDateToValueAggregation;
	ppdActual: ManagementCenterDateToValueAggregation;
	targetPpd: ManagementCenterDateToValueAggregation;
};

export type ManagementCenterHoursResponse = {
	census: ManagementCenterDateToValueAggregation;
	hoursScheduled: ManagementCenterDateToValueAggregation;
	hoursActual: ManagementCenterDateToValueAggregation;
	targetHours: ManagementCenterDateToValueAggregation;
};

/**
 * One multi-facility metric: per-facility date→value series (BE `MultiFacilitySeries`). The BE
 * also ships its census-weighted `portfolioAverage`, but the charts ignore it — the "Average"
 * anchor is a client-side mean over the facilities currently drawn, so it re-scopes with the
 * selector/legend without refetching.
 */
export type MultiFacilitySeries = {
	perFacility: Record</*departmentId*/ string, ManagementCenterDateToValueAggregation>;
	portfolioAverage: ManagementCenterDateToValueAggregation;
};

/** `POST /management_center/multi/{ppd-stats|hppd-stats|shift-pickups}` response. */
export type MultiFacilityStatsResponse = MultiFacilitySeries & {
	facilityNames: Record</*departmentId*/ string, string>;
};

/** `POST /management_center/multi/cost-stats` response — three series (total/agency/overtime). */
export type MultiFacilityCostStatsResponse = {
	total: MultiFacilitySeries;
	agency: MultiFacilitySeries;
	overtime: MultiFacilitySeries;
	facilityNames: Record</*departmentId*/ string, string>;
};

/** `POST /management_center/multi/disciplinary-summary` response — violation-type→count per facility. */
export type MultiFacilityDisciplinarySummaryResponse = {
	perFacility: Record</*departmentId*/ string, Record</*violationType*/ string, number>>;
	facilityNames: Record</*departmentId*/ string, string>;
};

/** `POST /management_center/multi/kpi-summary` response. KPIs are null when their denominator is zero or wage is hidden. */
export type MultiFacilityKpiSummaryResponse = {
	avgPpd: number | null;
	avgHppd: number | null;
	/** Percent 0-100: portfolio average daily census over total beds. */
	occupancy: number | null;
	agencyPercent: number | null;
	otPercent: number | null;
	totalCost: number | null;
	facilities: number;
};

/** One pre-aggregated actual/target pair in the multi-facility performance heatmap. */
export type MultiFacilityPerformanceValue = {
	actual: number | null;
	target: number | null;
	census: number | null;
};

/** A Monday–Sunday column, clipped by the backend to the selected date range at either edge. */
export type MultiFacilityPerformanceWeek = {
	startDate: string;
	endDate: string;
};

/** One facility row in `POST /management_center/multi/performance-over-time`. */
export type MultiFacilityPerformanceFacility = {
	departmentId: string;
	departmentName: string;
	hppd: MultiFacilityPerformanceValue[];
	ppd: MultiFacilityPerformanceValue[];
};

/**
 * `POST /management_center/multi/performance-over-time` response. Values and targets are already
 * census-weighted by the backend; the frontend only selects, sorts, and presents them.
 */
export type MultiFacilityPerformanceOverTimeResponse = {
	weeks: MultiFacilityPerformanceWeek[];
	facilities: MultiFacilityPerformanceFacility[];
	ppdAvailable: boolean;
	/** Facilities whose PPD cells were zeroed because this caller may not see their wage data. */
	wageRestrictedDepartmentIds: string[];
};

export type ShiftTimeRange = {
	startTime: string;
	endTime: string;
};

export type ShiftCategory = "Morning" | "Day" | "Evening" | "Night";

export type StaffMemberDailyBreakdown = {
	nurseId: number;
	firstName: string;
	lastName: string;
	role: string;
	/** Present when job_management_enabled covers the department and the shift carries a job (INH-3407). */
	jobDisplayName?: string | null;
	scheduledHours: number;
	actualHours: number;
	scheduledCost: number;
	actualCost: number;
	scheduledShifts: ShiftTimeRange[];
	actualShifts: ShiftTimeRange[];
	isExtendedHours: boolean;
	isDifferentShift: boolean;
	isUnscheduled: boolean;
	isLateScheduled: boolean;
	replacedCallOff: boolean;
	weeklyOtHours: number;
	consecutiveOtHours?: number;
	shiftCategory: ShiftCategory | null;
	shiftHoursLabel: string | null;
};

export type DailyStaffBreakdownResponse = {
	date: string;
	staff: StaffMemberDailyBreakdown[];
};

export type CostDeepDiveResponse = {
	baseCost: ManagementCenterDateToValueAggregation;
	dailyOtCost: ManagementCenterDateToValueAggregation;
	weeklyOtCost: ManagementCenterDateToValueAggregation;
	/** DAT-289. Optional: absent from payloads produced before the field shipped. */
	consecutiveOtCost?: ManagementCenterDateToValueAggregation;
	/** DAT-289: the department configures a consecutive-day rule — drives column visibility. */
	hasConsecutiveOtRule?: boolean;
	agencyCost: ManagementCenterDateToValueAggregation;
	bonusCost: ManagementCenterDateToValueAggregation;
	ptoCost: ManagementCenterDateToValueAggregation;
	totalCost: ManagementCenterDateToValueAggregation;
	baseHours: ManagementCenterDateToValueAggregation;
	dailyOtHours: ManagementCenterDateToValueAggregation;
	weeklyOtHours: ManagementCenterDateToValueAggregation;
	consecutiveOtHours?: ManagementCenterDateToValueAggregation;
	agencyHours: ManagementCenterDateToValueAggregation;
	totalHours: ManagementCenterDateToValueAggregation;
};

export type CostVarianceResponse = {
	actualOnlyCost: ManagementCenterDateToValueAggregation;
	scheduledOnlyCost: ManagementCenterDateToValueAggregation;
	overlapDiffCost: ManagementCenterDateToValueAggregation;
};

export type StaffMemberCostBreakdown = {
	nurseId: number;
	firstName: string;
	lastName: string;
	role: string;
	/** Present when job_management_enabled covers the department and the shift carries a job (INH-3407). */
	jobDisplayName?: string | null;
	baseCost: number;
	dailyOtCost: number;
	weeklyOtCost: number;
	consecutiveOtCost?: number;
	agencyCost: number;
	ptoCost: number;
	totalCost: number;
	baseHours: number;
	dailyOtHours: number;
	weeklyOtHours: number;
	consecutiveOtHours?: number;
	agencyHours: number;
	totalHours: number;
};

export type DailyCostBreakdownResponse = {
	date: string;
	staff: StaffMemberCostBreakdown[];
};

export type ManagementCenterMultiDepartmentItem = {
	department: string;
	region: string;
	wanted: number;
	actual: number;
};

export type ManagementCenterMultiDepartmentResponse = {
	items: ManagementCenterMultiDepartmentItem[];
};

export enum PushReportType {
	cost_deep_dive = "cost_deep_dive",
	schedule_vs_actual = "schedule_vs_actual",
	disciplinary = "disciplinary",
	/** The Management Center multi-facility grid, across the subscriber's whole network. */
	multi_facility_grid = "multi_facility_grid",
}

/**
 * One chosen grid column. The label travels with the key because the emailed spreadsheet's header
 * row must read like the table it was scheduled from, and the backend's own metric catalog words
 * several of these differently (its wording is ops-facing).
 */
export type PushReportColumn = {
	key: string;
	label: string;
};

export type PushReportSchedule = {
	weekdays: number[];
	enabled: boolean;
	/** Set only for `multi_facility_grid`; the other report types fix their own content. */
	columns: PushReportColumn[] | null;
};

export type HoursSummaryDayData = {
	/** @deprecated use recurringHours + pickupHours */
	regularHours: number;
	recurringHours: number;
	pickupHours: number;
	weeklyOtHours: number;
	consecutiveOtHours?: number;
	dailyOtHours: number;
	shiftOverrunHours: number;
	agencyHours: number;
	totalHours: number;
	targetHours: number;
};

export type HoursSummaryShiftPattern = {
	startTime: string;
	endTime: string;
	hours: number[];
};

export type HoursSummaryRoleGroup = {
	roleGroup: string;
	days: HoursSummaryDayData[];
	weeklyOtPatterns: HoursSummaryShiftPattern[];
	dailyOtPatterns: HoursSummaryShiftPattern[];
	shiftOverrunPatterns: HoursSummaryShiftPattern[];
	agencyPatterns: HoursSummaryShiftPattern[];
};

export type HoursSummaryEmployeeDayShift = {
	shiftId: string;
	date: string;
	shiftStart: string;
	shiftEnd: string;
	totalHours: number;
	dailyOtHours: number;
	weeklyOtHours: number;
	consecutiveOtHours?: number;
	shiftOverrunHours: number;
	agencyHours: number;
};

export type HoursSummaryEmployeeDetail = {
	nurseId: number;
	firstName: string;
	lastName: string;
	role: string;
	roleGroup: string;
	employmentType: string;
	weeklyOtHours: number;
	consecutiveOtHours?: number;
	dailyOtHours: number;
	shiftOverrunHours: number;
	agencyHours: number;
	extraPayHours: number;
	shifts: HoursSummaryEmployeeDayShift[];
};

export type HoursSummaryResponse = {
	roleGroups: HoursSummaryRoleGroup[];
	employeeDetails: HoursSummaryEmployeeDetail[];
	/** DAT-289: the department configures a consecutive-day OT rule — drives the Consecutive OT row. */
	hasConsecutiveOtRule?: boolean;
};

export enum DisciplinaryViolationType {
	NCNS = "ncns",
	CallIn = "call_in",
	LateArrival = "late_arrival",
	HomeEarly = "home_early",
	ShiftDelta = "shift_delta",
	SkippedLunch = "skipped_lunch",
	MissingPunch = "missing_punch",
}

/** Which half of the punch pair is absent. Only set on MissingPunch violations. */
export enum MissingPunchSide {
	In = "in",
	Out = "out",
}

export type DisciplinaryRule = {
	violationType: DisciplinaryViolationType;
	thresholdValue: number;
	maxThresholdValue: number | null;
	weekdayPoints: number;
	weekendPoints: number;
	sortOrder: number;
	countOnlyWhenActualLonger?: boolean;
};

export type DisciplinaryRulesResponse = {
	rules: DisciplinaryRule[];
};

export type DisciplinaryViolation = {
	shiftId: string;
	shiftActualId: string | null;
	date: string;
	violationType: DisciplinaryViolationType;
	plannedStart: DateTime;
	plannedEnd: DateTime;
	actualStart: DateTime | null;
	actualEnd: DateTime | null;
	details: string;
	points: number;
	isWeekend: boolean;
	notes: string | null;
	missingPunchSide: MissingPunchSide | null;
};

export type DisciplinarySummary = {
	shiftAmount: number;
	score: number;
};

export type DisciplinaryNurseData = {
	nurseName: string;
	summary: DisciplinarySummary;
	violations: DisciplinaryViolation[];
};

export type DisciplinaryReportResponse = {
	startDate: DateTime;
	endDate: DateTime;
	disciplinaryData: Record<string, DisciplinaryNurseData>;
};

export type LeakageReportJob = {
	id: string;
	displayName: string;
};

export type LeakageReportRow = {
	nurseId: number;
	fullName: string;
	role: string;
	/** The employee's jobs (id + display name); empty unless job management is enabled. */
	job: LeakageReportJob[];
	employmentType: EmploymentType;
	/** Weekly-average shift hours excluding PTO. */
	scheduledAvgHours: number;
	actualAvgHours: number;
	/** Weekly-average shift hours including PTO. The PTO toggle selects between these client-side. */
	scheduledAvgHoursWithPto: number;
	actualAvgHoursWithPto: number;
	ptoHours: number;
	weekendsWorked: number;
	/** Most recent actual shift, or null */
	lastShiftWorked: DateTime | null;
};

export type LeakageReportResponse = { rows: LeakageReportRow[] };

export interface PickShiftResponse {
	request_id: string;
	accepted_start_time: DateTime;
	accepted_end_time: DateTime;
}

export interface UserGuideMessageRequest {
	message: string;
	sessionId: string;
}

export interface ConversationMessage {
	sessionId: string;
	userId: string;
	message: string;
	timestamp: string;
	role: string;
}

export interface ChatMessage {
	id: string | number;
	text: string;
	sender: "user" | "server";
	timestamp: string;
	type: "text" | "task";
}

export interface Chat {
	id: string;
	messages: ChatMessage[];
	inputMessage: string;
	isThinking: boolean;
	isTyping: boolean;
	streamingMessageId: string | number | null;
	unreadCount: number;
}

export interface DocumentData {
	content: string;
	size_bytes: number;
}

export enum DraftChangeType {
	ADD = "ADD",
	REMOVE = "REMOVE",
	UPDATE = "UPDATE",
	MOVE = "MOVE",
	APPROVE_ADD_REQUEST = "APPROVE_ADD_REQUEST",
	APPROVE_REPLACE_REQUEST = "APPROVE_REPLACE_REQUEST",
	UPDATE_CENSUS = "UPDATE_CENSUS",
	DELETE_CENSUS_OVERRIDE = "DELETE_CENSUS_OVERRIDE",
}

export type DraftChange = {
	id: string;
	type: DraftChangeType;
	timestamp: DateTime;
	payload: {
		nurse?: NurseInShift;
		weekIndex: number;
		shiftIndex: number;
		dayIndex: number;
		isActual?: boolean;
		location?: string;
		message?: string;
		trainingStatus?: NurseTrainingStatus;
		bonus?: number;
		targetShiftIndex?: number;
		targetDayIndex?: number;
		shiftTimes?: any;
		source?: string;
		requestId?: string;
		nurseToRemove?: NurseInShift;
		censusOverride?: number;
		censusNote?: string;
		date?: string;
		shiftType?: string;
		// Diff object showing exactly what changed (for UPDATE type)
		diff?: Partial<NurseInShift>;
	};
};

export type SerializedDraftChange = Omit<DraftChange, "timestamp"> & {
	timestamp: string;
};

export type StoredDraftModeState = {
	isDraftMode: boolean;
	draftChanges: SerializedDraftChange[];
	savedAt: string;
	locallyApprovedRequests?: string[];
	locallyDeclinedRequests?: string[];
};

export type PublishRequestBody = {
	accepted_requests: Record<string, number[]>;
	declined_requests: Record<string, number[]>;
	actual_changes: ShiftChange[];
	changes: ShiftChange[];
	source: ChangesSource;
};

export type PostShiftRequestBody = {
	roles: string[];
	job_id?: string;
	needed_nurses: number;
	location?: string;
	shift_type: ShiftType;
	shift_start: string | DateTime;
	shift_end: string | DateTime;
	bonus: number;
	charge_only: boolean;
	agency_shift_ids?: string[];
	post_to_agency?: boolean;
	post_with_delay?: boolean;
	agency_post_delay_hours?: number;
	agency_license_types?: string[];
	agency_facility_ids?: string[];
	agency_source?: string[];
	agency_source_name?: string;
	source?: string;
	sub_role?: NurseRole;
	incentive_configuration_id?: string;
};

export type PostShiftRequestParams = {
	department_id: string;
	source: ChangesSource;
};

export interface LocationCredentialResponse {
	id: string;
	department_location_id: string;
	nurse_id: number;
	created_at: DateTime;
	expires_at: DateTime | null;
	nurse_first_name: string | null;
	nurse_last_name: string | null;
	location_name: string | null;
	status: CredentialStatus;
}

export enum CredentialStatus {
	WaitingForHR = "Waiting for HR",
	WaitingForFacilityApproval = "Waiting for facility approval",
	Approved = "Approved",
}

export interface CreateLocationCredentialRequest {
	nurse_ids: number[];
	location_id: string;
	expires_at: string | null;
	status: CredentialStatus;
}

export interface UnmatchedStaffMember {
	first_name: string | null;
	last_name: string | null;
	email: string | null;
	phone_number: string | null;
	contact_id: string | null;
}

export interface UnmatchedLocation {
	company_name: string;
	company_id: string | null;
	staff_count: number;
}

export interface MatchedStaffMember {
	first_name: string | null;
	last_name: string | null;
	email: string | null;
	phone_number: string | null;
	contact_id: string | null;
}

export interface MatchedLocation {
	company_name: string;
	company_id: string | null;
	staff_count: number;
}

export interface HubSpotCredentialUploadResponse {
	success: boolean;
	credentials_created: number;
	credentials_updated: number;
	matched_staff: MatchedStaffMember[];
	matched_locations: MatchedLocation[];
	unmatched_staff: UnmatchedStaffMember[];
	unmatched_locations: UnmatchedLocation[];
}

export enum CommunicationChannel {
	Email = "email",
	SMS = "sms",
}

interface CreatedUserIdForNurse {
	nurseId: number;
	userId: string;
	sentVia: CommunicationChannel;
	sentViaChannels: CommunicationChannel[];
}

interface FailedNurseCreation {
	nurseId: number;
	errorDesc: string;
}

export interface CreateBatchUsersForNurseResponse {
	createdUserIds: CreatedUserIdForNurse[];
	errors: FailedNurseCreation[];
}

export interface WeeklyMetric {
	week_start: string;
	week_end: string;
	avg_hppd: number;
	avg_ppd: number;
	avg_census: number;
	avg_agency_cost: number;
}

export interface PBJFacilityInfo {
	provnum: string;
	provname: string;
	city: string;
	state: string;
}

export interface PbjCalculatorResponse {
	facility_info: PBJFacilityInfo;
	weekly_data: WeeklyMetric[];
	csv_data: string;
	wrong_staffing_percentage: number;
	potential_savings: number;
	estimated_ppd: number;
	average_census: number;
	estimated_ppd_from_ot: number;
	total_ot_cost: number;
	estimated_ppd_from_agency: number;
	total_cost_of_agency: number;
	estimated_hppd_from_overstaffing: number;
	total_overstaffed_hours: number;
	average_hourly_wage: number;
	total_overstaffing_cost: number;
	reduction_in_ot: number;
	reduction_in_agency: number;
	reduction_in_overstaffing: number;
	total_estimated_savings: number;
	calculated_rn_percentage: number;
	calculated_lpn_percentage: number;
	calculated_cna_percentage: number;
}

export type PbjCalculatorParams = {
	facility_identifier: string;
	start_month: string;
	end_month: string;
	rn_wage: number;
	lpn_wage: number;
	cna_wage: number;
	rn_percentage: number;
	lpn_percentage: number;
	cna_percentage: number;
	ot_percentage: number;
	ot_cost_percentage: number;
	agency_cost_percentage: number;
	hppd_lower_threshold: number;
	hppd_upper_threshold: number;
	target_ot_reduction: number;
	target_overstaffed_reduction: number;
	target_agency_reduction: number;
};

export type PublishSelfSchedulingRequest = {
	departmentId: string;
	preferences: Array<{ startDate: string; endDate: string }>;
};

export type PublishSelfSchedulingResponse = {
	success: boolean;
	createdCount: number;
	updatedCount: number;
	deletedCount: number;
};

export type MetricsData = { signupShiftsCurrentAmount: number };

export type TutorialStep = {
	title?: string;
	body?: string;
	image?: string;
};

export type SelfScheduleCalendarDayData = SelfSchedulingDayStructure & {
	shiftType?: ShiftType;
	startTime?: DateTime;
	endTime?: DateTime;
	originalPreferenceId?: string;
};

export type SelfScheduleCalendarDaysData = {
	[dateISO: string]: SelfScheduleCalendarDayData;
};

export type GroupedNurses = {
	locationKey: string;
	locationName: string;
	censusCount: number;
	nurses: NurseInShift[];
	roleGroupCounts: Record<string, number>;
	roleGroupRatios: Record<string, number>;
	jobGroupCounts: Record<string, number>;
	jobGroupRatios: Record<string, number>;
	totalRatio: number;
	hppd: number;
};

export interface ImpersonatableUser {
	username?: string | null;
	phone_number?: string | null;
	first_name?: string | null;
	last_name?: string | null;
}

export enum SelfSchedulingType {
	Monthly = "Monthly",
}

export type SelfScheduling = {
	id: string;
	departmentId: string;
	type: SelfSchedulingType;
	periodStart: DateTime;
	periodEnd: DateTime;
	locked: boolean;
	sendNotifications: boolean;
};
export interface HistoryDistributionEntry {
	role_group: string;
	start_time: string;
	end_time: string;
	ratio: number;
	job_id?: string | null;
}

export enum TimeOffRequestReason {
	Vacation = "Vacation",
	SickLeave = "Sick Leave",
	MedicalAppointment = "Medical Appointment",
	FamilyObligation = "Family Obligation",
	PersonalReason = "Personal Reason",
	Other = "Other",
}

export enum TimeOffRequestStatus {
	PENDING = "pending",
	APPROVED = "approved",
	DECLINED = "declined",
}

export type TimeOffRequest = {
	id: string;
	startDate: DateTime;
	endDate: DateTime;
	status: TimeOffRequestStatus;
	requestingStaffId: number;
	lastChangedByAdminId?: string;
	departmentId: string;
	reason: TimeOffRequestReason;
	reasonExplanation?: string;
	createdAt: DateTime;
	updatedAt: DateTime;
	preferenceId?: string;
	deleted: boolean;
};

export type CreateTimeOffRequestBody = {
	nurseId: number;
	startDate: string;
	endDate: string;
	reason: TimeOffRequestReason;
	reasonExplanation?: string;
};

export type LocationItem = { id: string; value: string };

/** Persisted Leakage Report filters (date range & staff search are intentionally NOT saved). */
export type LeakageReportFilters = {
	roles: string[];
	jobs: string[];
	sections: string[];
	includePto: boolean;
};

export type DisciplinaryReportRowData = {
	nurseId: number;
	nurseName: string;
	violationCount: number;
	score: number;
	violations: DisciplinaryViolation[];
};
export type NurseSortItem = {
	column: string;
	order: string;
};

// One row per department. Global per-dept (no user scoping) — every user with access
// to the dept reads/writes the same row.
export type PbjModel = {
	departmentId: string;
	facilityId: string | null;
	// CMS Certification Number (CCN, e.g. "085057") — keys CASPER turnover data.
	// Distinct from facilityId (the PBJ submission id, e.g. "DE2880").
	ccn?: string | null;
	stateCode: string | null;
	stars: number;
	pccPdpmAvg: number;
	natNursingCaseMixIndex: number;
	// Explicit CMI manualness flag — true = the admin pinned the CMI pair (pccPdpmAvg +
	// natNursingCaseMixIndex) and it no longer tracks CASPER auto-population.
	cmiManual: boolean;
	natReportedTotalHrd: number;
	natReportedRnHrd: number;
	natReportedWeekendHrd: number;
	natCaseMixTotalHrd: number;
	natCaseMixRnHrd: number;
	natCaseMixWeekendHrd: number;
	averageCensus: number | null;
	// Manual assumed-wage overrides per role/day-type — null = fall back to the
	// computed dept wage the scorer derived. Drive the spend-impact recommendation math.
	wageOverrideRn: number | null;
	wageOverrideCna: number | null;
	wageOverrideLpn: number | null;
	wageOverrideWeekendRn: number | null;
	wageOverrideWeekendCna: number | null;
	wageOverrideWeekendLpn: number | null;
	// Manual turnover overrides — null = use the computed (state-machine) value.
	// Percentages for total/RN turnover; an integer departure count for admin.
	manualTotalTurnoverPct: number | null;
	manualRnTurnoverPct: number | null;
	manualAdminTurnoverCount: number | null;
	// When true, the corresponding turnover measure is excluded from the rating and
	// remaining measures rescale.
	adminTurnoverNa: boolean;
	// When true, the corresponding turnover measure is KEPT in the total but force-scored
	// at the lowest band. If the matching `*Na` flag is ALSO true, N/A wins.
	adminTurnoverInvalid: boolean;
	totalTurnoverNa: boolean;
	totalTurnoverInvalid: boolean;
	rnTurnoverNa: boolean;
	rnTurnoverInvalid: boolean;
	// Safety-margin buffers (default 0 = exact CMS scoring).
	// projectedScoreBufferPct is a flat-point buffer (legacy name) — added to the
	// greedy planner's stop-condition threshold. bandRangeBufferPct (0..100) tightens
	// band matching (HPPD/admin lo ×(1+p), turnover hi ×(1-p)).
	projectedScoreBufferPct: number;
	bandRangeBufferPct: number;
	// Long Shifts audit threshold (hours): a (employee, dept-local day) summing to >= this is flagged.
	// Editable in the Audit → Long Shifts tab. Default 22.5.
	longShiftThreshold: number;
	// Per-shift working-hours threshold, independent from the Single Day Flag threshold above.
	longShiftReviewThreshold: number;
	// Admin-chosen point target. Source of truth for the prediction's target — `stars`
	// is the derived band. Null on legacy rows; the BE resolves it on read to the
	// matching star band's lower bound so the FE always has a usable value.
	targetPoints: number | null;
};

// PUT body for the per-dept upsert. Year/quarter are not persisted — the FE owns them
// as page-session state and passes them as query params on prediction/audit calls.
export type PbjModelUpsertBody = {
	departmentId: string;
	facilityId: string | null;
	// CMS Certification Number (CCN) used to auto-fill CASPER turnover; see PbjModel.ccn.
	ccn?: string | null;
	stateCode: string | null;
	stars: number;
	pccPdpmAvg: number;
	natNursingCaseMixIndex: number;
	// Explicit CMI manualness flag. Omitted/null = "not specified": the BE preserves the
	// row's existing flag (a client that doesn't send it never clears a manual pin).
	cmiManual?: boolean | null;
	natReportedTotalHrd: number;
	natReportedRnHrd: number;
	natReportedWeekendHrd: number;
	natCaseMixTotalHrd: number;
	natCaseMixRnHrd: number;
	natCaseMixWeekendHrd: number;
	averageCensus: number | null;
	// Manual assumed-wage overrides per role/day-type — null = fall back to the computed wage.
	wageOverrideRn: number | null;
	wageOverrideCna: number | null;
	wageOverrideLpn: number | null;
	wageOverrideWeekendRn: number | null;
	wageOverrideWeekendCna: number | null;
	wageOverrideWeekendLpn: number | null;
	manualTotalTurnoverPct: number | null;
	manualRnTurnoverPct: number | null;
	manualAdminTurnoverCount: number | null;
	adminTurnoverNa: boolean;
	// When true, the corresponding turnover measure is KEPT in the total but force-scored
	// at the lowest band. N/A wins when both flags are set on the same measure.
	adminTurnoverInvalid: boolean;
	totalTurnoverNa: boolean;
	totalTurnoverInvalid: boolean;
	rnTurnoverNa: boolean;
	rnTurnoverInvalid: boolean;
	projectedScoreBufferPct: number;
	bandRangeBufferPct: number;
	// Long Shifts audit threshold (hours); editable in the Audit → Long Shifts tab.
	longShiftThreshold: number;
	longShiftReviewThreshold: number;
	// Admin-chosen point target; the BE re-derives `stars` from it on save.
	targetPoints: number | null;
};

export type PbjBand = {
	star: number;
	minValue: number | null;
	maxValue: number | null;
	points: number;
	displayRange: string;
	// Buffer-adjusted thresholds the scorer actually used. Equal to min/max when
	// bandRangeBufferPct = 0; otherwise scaled per kind so the breakdown can render
	// "needs 3.005 (5% buffer) — CMS threshold 2.862".
	bufferedMinValue: number | null;
	bufferedMaxValue: number | null;
};

export type PbjTurnoverDetails = {
	numerator: number;
	denominator: number;
	// DAT-668 — how many of `numerator` are mid-quarter projections rather than completed departures:
	// employees the inactivity alert has flagged who have no shift scheduled before their turnover
	// date. A subset of `numerator`, so it explains the figure without changing it. 0 on a closed
	// quarter, where every inactivity gap has already elapsed.
	pending: number;
};

// The inputs behind one adjusted-HPPD measure, rendered by the Scoring Breakdown's calculation
// tooltip (DAT-609). Every number is one the scorer used, so the tooltip's arithmetic reproduces
// `rawValue` rather than re-deriving it. `days`/`censusDays` are the measure's OWN denominators —
// the Weekend measure divides by weekend days only. Mirrors the BE `PbjHppdExplain`.
export type PbjHppdExplain = {
	// Distinct staff who worked shifts counting toward this measure. Manual prediction hours carry
	// no employee, so this can be 0 while `hours` is positive — see `manualHours`.
	employees: number;
	hours: number;
	manualHours: number;
	averageCensus: number;
	days: number;
	censusDays: number;
	reportedHppd: number;
	// What reported HPPD is divided by to reach `adjustedHppd` — the reciprocal of `scale`, so the
	// tooltip's division can never disagree with the scored value.
	caseMixDivisor: number;
	facilityCmi: number;
	nationalCmi: number;
	adjustedHppd: number;
};

export type PbjCategoryScore = {
	id: string;
	title: string;
	value: string;
	rawValue: number;
	score: number;
	maxScore: number;
	currentBandStar: number;
	nextBandStar: number | null;
	nextBandMinValue: number | null;
	nextBandPoints: number | null;
	bands: PbjBand[];
	turnoverDetails: PbjTurnoverDetails | null;
	kind: "hppd" | "turnover" | "admin";
	// False when the measure is flagged N/A — excluded from the total, remaining measures rescale.
	available: boolean;
	// True when Admin Turnover was flagged Invalid — kept in the total but force-scored at the lowest band.
	invalid?: boolean;
	// True when value/score come from a manual admin entry rather than the computed data.
	manualOverride: boolean;
	// Where a turnover measure's value originated. "manual" = admin-typed (see manualOverride),
	// "casper" = CMS CASPER public data, "casper_missing" = CASPER covers the facility but this
	// field is blank (arrives with available=false), "internal" = InHouse's own estimate (default).
	// Optional for forward-compat with older payloads; a missing value is treated as "internal".
	source?: "manual" | "casper" | "casper_missing" | "internal";
	// Adjusted-per-reported HPPD multiplier — `nat_case_mix_X / facility_cm_X`. Used by
	// the recommendations sidebar to convert hours-added → adjusted-HPPD bump with the
	// same math the BE greedy uses. 1.0 for non-HPPD categories.
	scale: number;
	// Calculation breakdown for the "how is this number reached" tooltip. Only the three HPPD
	// measures carry one; turnover/admin percentages have no hours/census arithmetic behind them,
	// and a future-only quarter withholds it rather than describing a window with no elapsed days.
	explain: PbjHppdExplain | null;
};

export type PbjStarThreshold = {
	star: number;
	min: number;
	max: number;
};

export type PbjAverageCensusSource = "user" | "computed" | "fallback";

/** Which worked hours the PBJ star prediction scored. Mirrors the BE `PbjPredictionHoursSource`. */
export type PbjPredictionHoursSource = "audit" | "schedule";

export type PbjDeptWages = {
	rn: number | null;
	cna: number | null;
	lpn: number | null;
	weekendRn: number | null;
	weekendCna: number | null;
	weekendLpn: number | null;
};

// Per-HPPD-category contribution inside one suggested-plan step. Because an
// action couples to multiple HPPDs (RN hours bump both Total + RN HPPD), each
// step ships one entry per category it touched so the FE can render
// "Total HPPD +X (+10 pts) / RN HPPD +Y (+10 pts)" beneath the action.
// Per-category contribution within a positive suggested-plan addition.
export type PbjSuggestedPlanStepCategoryGain = {
	categoryId: string; // "1" | "2" | "3"
	hppdGap: number; // positive adjusted-HPPD change this category received
	pointsGained: number; // non-negative marginal points
};

// One positive staffing addition from "Suggest plan". `pointsGained` sums the
// marginal points across every HPPD the lever touches; `breakdown` itemizes them.
// Signed cuts and swaps exist only in the user's manual Action Plan.
export type PbjSuggestedPlanStep = {
	// One of the four automatic-addition levers: CNA or RN, weekday or weekend.
	actionId: string; // RecommendationId
	hppdGap: number; // positive headline HPPD change (lever's primary category)
	// Sustained positive per-day rate over the full applicable window, rounded up.
	hours: number;
	// Positive per-remaining-applicable-day rate; null when no applicable days remain.
	hoursCatchup: number | null;
	spend: number; // positive hppdGap * representative wage (dollars-per-PPD basis)
	pointsGained: number; // non-negative marginal points
	wageKey: string; // "cna" | "rn" | "weekendCna" | "weekendRn" — maps to a PlanItem
	breakdown: PbjSuggestedPlanStepCategoryGain[];
};

// The cheapest positive additions that advance toward the target. `targetMet` is
// false when the best available partial plan cannot reach it. `projectedTotalPoints`
// is the starting points plus all step `pointsGained` values.
export type PbjSuggestedPlan = {
	steps: PbjSuggestedPlanStep[];
	targetMet: boolean;
	projectedTotalPoints: number;
};

// A staffing bucket the user has manually added to their action plan, as an add or a
// cut. Sent to the suggested-plan endpoint so the greedy re-optimizes the REMAINING
// buckets as if this one is already applied (its signed per-HPPD-category changes shift
// the baseline) and excludes the action itself from new positive suggestions.
// Mirrors the BE PbjLockedAction.
export type PbjLockedAction = {
	actionId: string; // RecommendationId
	categoryGaps: Record<string, number>; // HPPD category id ("1"/"2"/"3") -> signed adjusted-HPPD change
};

// How far one lever can be cut, in hours per applicable day (calendar days for a weekday
// lever, weekend days for a weekend lever — the same unit as a step's `hours`). The
// slider floor and backend manual-cut validation both read it, so the FE never re-derives the cap.
// `limitedBy` names which rule binds: the per-lever share cap, the federal RN-on-duty
// floor, or "none" when the lever has nothing to cut. Mirrors the BE PbjLeverCutLimit.
export type PbjLeverCutLimit = {
	currentHoursPerDay: number;
	maxCutHoursPerDay: number;
	limitedBy: "rn_floor" | "share" | "none";
};

// Request body for POST /pbj-model/suggested-plan. With an empty `lockedActions` this
// reproduces the prediction's pre-computed suggestedPlan; with locked actions it
// returns a plan re-optimized around them. Mirrors the BE PbjSuggestedPlanRequest.
export type PbjSuggestedPlanRequest = {
	departmentId: string;
	startDate: string; // ISO YYYY-MM-DD
	endDate: string; // ISO YYYY-MM-DD
	lockedActions: PbjLockedAction[];
};

export type PbjPrediction = {
	targetStars: number;
	currentStars: number;
	totalPoints: number;
	maxPoints: number;
	// True when one or more measures are N/A and totalPoints was rescaled to the 380 scale.
	// availableMaxPoints is the summed max of the still-available measures (the rescale denominator).
	rescaled: boolean;
	availableMaxPoints: number;
	categories: PbjCategoryScore[];
	config: PbjModel;
	starThresholds: PbjStarThreshold[];
	resolvedAverageCensus: number;
	averageCensusSource: PbjAverageCensusSource;
	deptWages: PbjDeptWages;
	suggestedPlan: PbjSuggestedPlan;
	// Per-lever cut limits. Empty for a quarter that hasn't started (no worked hours, so
	// nothing to cut); otherwise every lever is present. A missing key means "can't cut".
	cutLimits: Partial<Record<RecommendationId, PbjLeverCutLimit>>;
	// Effective window day counts (after clamping end_date to today). The FE renders
	// these in the "how impact is calculated" tooltip so users can see why a weekend
	// hour has a bigger per-hour effect on Weekend HPPD (smaller denominator).
	// For an in-progress quarter these are ELAPSED-to-today, not the full quarter.
	days: number;
	weekendDays: number;
	// Full calendar-quarter day counts (no today clamp). Used by the FE "Full quarter"
	// recommendations toggle to swap the math denominator without recomputing the greedy.
	quarterDays: number;
	quarterWeekendDays: number;
	// Remaining-from-today day counts. Drives the FE "Quarter to date" catch-up math:
	// add hours over these remaining days only to hit the Q-end target. 0 when the
	// quarter is past (catch-up infeasible).
	daysRemaining: number;
	weekendDaysRemaining: number;
	usingDefaults: boolean;
	dataThroughDate: string | null; // ISO YYYY-MM-DD; null when the quarter is fully in the future
	// Source of the effective Nursing CMI pair in `config`: manual (admin edited it),
	// casper (auto-populated from the facility's CMS CASPER row), default (hardcoded national).
	cmiSource?: "casper" | "manual" | "default";
	// Which worked hours the three HPPD measures were scored from.
	// "audit" — the department mirrors payroll punches, so every paid employee counts and breaks
	// come from the recorded punch-out gaps plus its configured PBJ break rules.
	// "schedule" — no mirror for this quarter, so only staff scheduled in InHouse count and the flat
	// CMS 0.5h-over-6h meal break applies.
	// null — no hours were read at all (a quarter that has not started yet).
	hoursSource: PbjPredictionHoursSource | null;
	// Audit hours in the window with no CMS job mapping, so they could not be scored. null — NOT 0 —
	// on the schedule source, where there is no mapping to have a gap in; 0 means the mapping is
	// complete.
	auditUnmappedHours: number | null;
	// The plan this department has committed to, or null when it tracks nothing. Rides the
	// prediction so the "Add to tracker" button knows its state without a second round trip.
	trackedPlan: PbjTrackedPlan | null;
};

// ---------------------------------------------------------------------------
// Tracked plan — what a department committed to (POST /pbj-model/tracked-plan)
// ---------------------------------------------------------------------------

// What one action contributes to ONE CMS measure. An action couples to several measures
// (weekday CNA moves Total only, weekend RN moves Total + RN + Weekend), so it carries one
// of these per measure it touches. The BE rejects a plan (422) whose gains don't cover
// EXACTLY the measures the lever moves — see `PBJ_TRACKED_PLAN_ACTION_CATEGORIES`.
// Mirrors the BE PbjTrackedPlanActionGain.
export type PbjTrackedPlanActionGain = {
	categoryId: string; // "1" = Total HPPD, "2" = RN HPPD, "3" = Weekend HPPD
	hppdGap: number;
	// Marginal points the Action Plan credited this action over the items added before it
	// (the cumulative pass in `cumulativeScoring.utils`), not its standalone gain.
	points: number;
};

// One staffing lever inside a tracked plan. Mirrors the BE PbjTrackedPlanAction.
export type PbjTrackedPlanAction = {
	id: string; // RecommendationId — "cna" | "lpn" | "rn" | "weekend_cna" | "weekend_lpn" | "weekend_rn"
	hours: number; // sustained per-applicable-day rate
	// Catch-up per-remaining-applicable-day rate. null records that catch-up was ALREADY
	// infeasible when the plan was saved — the tracker renders the action infeasible rather
	// than falling back to a rate the user never agreed to.
	hoursCatchup: number | null;
	spend: number;
	gains: PbjTrackedPlanActionGain[];
};

// POST body for saving the chosen plan. Deliberately carries neither census, wages,
// `startingPoints` nor `savedAt`: the server resolves those from a freshly computed
// prediction so a stored plan can't be skewed by a stale page. Mirrors PbjTrackedPlanSaveBody.
//
// Dates are ISO STRINGS here, unlike the response types below: only responses pass through
// `convertIsoDates`, so what we send and what comes back are genuinely different shapes.
export type PbjTrackedPlanSaveBody = {
	departmentId: string;
	periodMode: "qtd" | "quarter";
	quarterStart: string; // ISO YYYY-MM-DD — the FULL quarter, not the page's committed range
	quarterEnd: string; // ISO YYYY-MM-DD
	targetPoints: number;
	actions: PbjTrackedPlanAction[];
};

// A plan as persisted. Storage is append-only / latest-wins: "switching" plans is a new
// save, never an overwrite. Mirrors the BE PbjTrackedPlan.
//
// Every date is a Luxon `DateTime`, NOT the ISO string the API emits: the axios response
// interceptor runs `convertIsoDates` over every GET body, so anything matching an ISO date is
// already parsed by the time a component sees it. Typing these as `string` compiles fine and then
// fails at runtime — `DateTime.fromISO(aDateTime)` yields an Invalid DateTime, and `===` against
// an ISO string is never true.
export type PbjTrackedPlan = {
	savedAt: DateTime; // stamped server-side
	periodMode: "qtd" | "quarter";
	quarterStart: DateTime;
	quarterEnd: DateTime;
	targetPoints: number;
	census: number;
	startingPoints: number;
	wages: PbjDeptWages;
	actions: PbjTrackedPlanAction[];
};

// ---------------------------------------------------------------------------
// Plan progress — what the tracker renders (GET /pbj-model/tracked-plan/progress)
// ---------------------------------------------------------------------------

// One tracker number carried in BOTH bases at once. The plan-level absolute↔HPPD toggle
// picks an attribute; it never multiplies by a factor, because a per-day rate, a per-week
// rate and a cumulative quarter total each divide by a different denominator.
export type PbjPlanValue = {
	hours: number;
	hppd: number; // REPORTED (pre-case-mix) HPPD against the plan's FROZEN census
};

// The plan's weekly cadence. Plan-level: every action is measured over the same ISO
// Monday–Sunday weeks. Only fully-contained weeks count.
export type PbjPlanWeeks = {
	elapsed: number;
	total: number;
	remaining: number;
};

// Applicable-day counts for one action — calendar days for a weekday lever, weekend days
// only for a weekend lever. `covered` is the subset of `elapsed` inside COMPLETED weeks and
// is what `requiredToDate` is charged over; it is always <= `elapsed`. The two ship
// separately so the UI can explain the gap instead of leaving a department to wonder why a
// full week of work hasn't moved the badge.
export type PbjPlanActionDays = {
	baseline: number;
	plan: number;
	elapsed: number;
	covered: number;
	quarter: number;
};

// One CMS band edge still ahead of the department on an action's primary measure. Points are
// earned by CROSSING an edge, so half the hours to the next marker earns exactly nothing —
// never interpolate points between markers. On a CUT lever these are the buffered floors at or
// below the current value, and `hppdGap`, `hours` and `points` are all negative: `points` is
// what falling past that floor would cost.
export type PbjPlanMilestone = {
	categoryId: string;
	star: number;
	edgeValue: number; // buffered adjusted-HPPD threshold the scorer credits
	hppdGap: number; // that distance in REPORTED HPPD (case-mix reversed)
	hours: number; // TOTAL hours to add across the quarter's applicable days
	points: number;
};

// Where a lever lands at the end of the plan window if the department holds the pace it has set so
// far. `points` is a walk of the SAME milestone ladder the card draws its markers from, so the
// projection caption and the ticks beside it can never disagree — a projection landing short of a
// band edge earns nothing for it, because points come from crossing.
export type PbjPlanProjection = {
	delivered: PbjPlanValue; // cumulative delivery above baseline at quarter end, CAN BE NEGATIVE
	pct: number; // 0–100, already clamped by the engine — same axis as `progressPct`
	shortfall: PbjPlanValue; // how far short of the commitment, floored at zero
	points: number;
	meetsGoal: boolean;
};

// Everything the tracker renders for one committed lever. Mirrors PbjPlanActionProgress.
export type PbjPlanActionProgress = {
	id: string; // RecommendationId
	primaryCategoryId: string;
	// "add" when the stored goal rate is >= 0, else "cut". Every hours/HPPD figure on the card
	// stays signed (a cut's goal, remaining and delivery are negative); progress, on-track and the
	// projection are judged in this direction, and every label switches on it.
	direction: "add" | "cut";
	// False when the commitment can't be measured at all — no applicable days left in the plan
	// window, or catch-up was already infeasible at save time. Render as such, NOT as 0%.
	feasible: boolean;
	days: PbjPlanActionDays;
	baselineRate: PbjPlanValue;
	goalRate: PbjPlanValue;
	// The same committed rate per WEEK. Shown instead of `goalRate` wherever it sits beside
	// `updatedGoal`, which is per week by construction: an hours-per-day figure set against an
	// hours-per-week one reads as a sevenfold jump in the ask. Only `.hours` differs between the pair.
	goalRateWeekly: PbjPlanValue;
	goalTotal: PbjPlanValue;
	// COMPLETED weeks only — what the on-track badge is judged on, so it never reads "behind"
	// from Monday to Saturday just because the current week is unfinished. CAN BE NEGATIVE
	// (staffing below the department's own pre-plan baseline); do not clamp it in the UI.
	delivered: PbjPlanValue;
	// Every elapsed day — what the progress bar fills to. Differs from `delivered` by up to
	// six days of hours on the same card.
	deliveredToDate: PbjPlanValue;
	remaining: PbjPlanValue;
	requiredToDate: PbjPlanValue;
	// Hours per week for the weeks that remain; null once no whole week is left.
	updatedGoal: PbjPlanValue | null;
	onTrack: boolean;
	progressPct: number; // 0–100, already clamped by the engine
	baselineCumulative: PbjPlanValue;
	targetCumulative: PbjPlanValue;
	currentCumulative: PbjPlanValue;
	// Where the current pace lands at quarter end. null when there is no pace to extend — nothing
	// applicable has elapsed yet, or the lever is infeasible — so the projection is OMITTED rather
	// than drawn at zero, which would read as a confident forecast of total failure.
	projected: PbjPlanProjection | null;
	// The MARGINAL gain the Action Plan credited this lever at save time — frozen, and scored over the
	// PREDICTION's window, not the plan's quarter.
	points: number;
	// What the commitment is worth on TODAY's ladder if delivered in full, from the same walk
	// `projected.points` uses, so the two are read off one ladder. They are NOT ordered: a pace above the
	// committed rate projects PAST the commitment and is worth MORE than the plan promised, so "50 pts
	// committed" beside a projection worth 80 is correct. This is the figure the card
	// shows as "committed", because it is the one the milestone markers beside it agree with.
	goalPoints: number;
	hppdGap: number;
	price: number; // signed: full cost (add) or savings (cut, negative) at the frozen wage
	// Hours delivered ABOVE baseline × wage, on every lever — a cut lever whose staffing rose
	// counts here as spend, so a missed cut can't hide inside the savings.
	spendSoFar: number;
	// Hours delivered BELOW baseline × wage, on cut levers only (always 0 on an add lever).
	savingsSoFar: number;
	milestones: PbjPlanMilestone[];
	// Estimated manual hours folded into THIS lever's numbers (DAT-620), or null when none
	// were. Per action because an estimated figure sits beside a measured one on the same card.
	estimatedHours: number | null;
	// Manual hours a user ENTERED for this lever via the Manual Hours dialog, folded into the
	// numbers above. Kept apart from `estimatedHours`: one is a figure somebody typed, the other is
	// a guess at days no entry covers, and calling a user's own entry an estimate would be wrong.
	manualHours: number | null;
	// The share of `manualHours` dated before the plan was saved. Those hours raise the baseline
	// instead of clearing it, so without this the card shows a large manual total beside a delivered
	// figure of zero and gives the reader no way to see the two agree.
	manualHoursBeforePlan: number | null;
	// Hours entered on the Prediction tab that are NOT counted here, because the Audit tab already
	// books those days. The two stores describe the same admin-logged hours, so they are summed
	// under Audit precedence; this is what makes the drop visible instead of silent.
	manualHoursAlreadyBooked: number | null;
};

// The tracker header's score line. `currentPoints` is the LIVE score, NOT `startingPoints`
// plus the actions' milestone points — census, case mix and turnover move independently of
// staffing, so the two genuinely differ. That disagreement is what the accuracy banner explains.
export type PbjPlanStatus = {
	startingPoints: number;
	startingStars: number;
	currentPoints: number;
	currentStars: number;
	targetPoints: number;
	targetStars: number;
	pointsGained: number;
};

// What the plan was priced at versus what has actually been spent and saved, all on an
// hours × wage basis against the plan's FROZEN wages and census. Cost (adds) and savings
// (cuts) are kept apart and then netted; budget is judged on the NET basis. A plan with no
// cuts has every savings figure 0, so its numbers are identical to the add-only model.
export type PbjPlanCost = {
	totalPrice: number; // sum of positive action prices (the additions' full cost)
	totalSavings: number; // sum of |negative action prices| (the cuts' full savings), positive
	netPrice: number; // totalPrice - totalSavings; negative when the plan saves more than it spends
	spendSoFar: number;
	savingsSoFar: number; // positive
	netSoFar: number; // spendSoFar - savingsSoFar
	budgetToDate: number; // netPrice × elapsedShare
	savingsToDate: number; // totalSavings × elapsedShare
	elapsedShare: number;
	onBudget: boolean; // netSoFar <= budgetToDate
};

// Progress against the tracked plan, over the PLAN's own quarter (independent of whatever
// window the Prediction tab is currently scoped to). The endpoint answers 200 with a null
// body when the department tracks nothing — a normal state, not an error.
export type PbjPlanProgress = {
	periodMode: "qtd" | "quarter";
	// Luxon `DateTime`s, not ISO strings — see the note on `PbjTrackedPlan`: the axios response
	// interceptor parses every ISO date in a GET body before any component sees it.
	quarterStart: DateTime;
	quarterEnd: DateTime;
	trackingStartDate: DateTime; // first department-local day of the commitment
	elapsedThroughDate: DateTime; // last day measured so far
	census: number; // the plan's frozen census (prices + goals)
	liveCensus: number; // today's census (milestone ladder)
	// False when the plan was saved on the quarter's first day, leaving no span to derive a
	// baseline from — every existing hour then reads as progress, so say so rather than
	// rendering a flattering number silently.
	baselineAvailable: boolean;
	planWeeks: PbjPlanWeeks;
	status: PbjPlanStatus;
	cost: PbjPlanCost;
	actions: PbjPlanActionProgress[];
	// DAT-620: manual hours land at the END of a quarter, so mid-quarter every level is
	// measured against a fraction of what will eventually be reported. When an estimate was
	// folded in, the banner renders off `estimateIncluded` and individual numbers are marked
	// off each action's `estimatedHours`.
	estimateIncluded: boolean;
	estimatedHours: number | null;
	estimateSourceQuarter: string | null; // CMS quarter label, e.g. "Q2 2026"
	// Entered manual hours folded into the measured span, summed over the levers the plan tracks.
	manualHours: number | null;
};

export type PbjAuditDayRow = {
	nurseId: number;
	pbjId: string | null;
	name: string;
	role: NurseFullRole;
	date: string; // ISO yyyy-mm-dd in the dept's local tz; one row per (nurse, date) across the quarter.
	totalWorkingHrs: number;
	totalNursingHrs: number;
	// Manual rows are admin-logged entries (not derived from a finalized shift) and
	// share the real nurse's `nurseId`. The BE sends `isManual=true` for these and a
	// `manualEntryId` so the FE can target writes (inline-edit override, delete).
	isManual?: boolean;
	manualEntryId?: string;
};

// DAT-209 Staffing Hours row: pbj_shift-backed, one row per (pbj_employee_id, dept-local date, PBJ job).
// `hours` is worked time minus recorded breaks. `w2OrContractor` is "W2" when a matching
// external_employee exists, else null.
export type PbjStaffingHoursRow = {
	empId: string;
	name: string;
	date: string; // ISO yyyy-mm-dd in the dept's local tz
	hours: number;
	// The hours actually FILED when this (employee, day) exceeded the CMS 22.5h/day cap: the day's rows
	// scaled down so the filed total is exactly the cap. null on the vast majority of rows (day under the
	// cap). `hours` stays the real worked hours — the grid shows `cappedHours` in its place with a tooltip.
	cappedHours: number | null;
	jobDescription: string | null; // raw punch role string (vs the mapped pbjJobDescription)
	payTitleCode: string | null; // pbj_job.code (CMS 1–40)
	// EFFECTIVE Pay Type Code ("1"|"2"|"3"|null): the manual per-shift override when one exists,
	// else the per-employee derived code. When `payTypeCodeOverride` is null, this IS the derived value.
	payTypeCode: string | null;
	// Raw manual override ("1"|"2"|"3") or null when the effective code is derived — drives the
	// "manually set" indicator; clearing the override reverts to the derived value.
	payTypeCodeOverride: string | null;
	pbjJobDescription: string | null;
	w2OrContractor: string | null;
	employmentType: EmploymentType | null;
	departmentName: string | null;
	earnCode: string | null;
	distJobCode: string | null; // DAT-209: per-shift Dist Job Code synced from Paycom, or the Position code from iSolved (null for Empeon); read-only
	agencyName: string | null;
	// DAT-209 metadata editing. `editable` is true only for rows that map 1:1 to a single pbj_shift on
	// one dept-local date (inline edit allowed; `pbjShiftId` populated). Others use the row-expand sub-grid.
	pbjShiftId: string | null;
	pbjJobId: string | null;
	editable: boolean;
	// INFORMATIONAL: this employee-day crossed a Daylight-Saving transition (naive clock math over/under-
	// counts, but `hours` is already correct — the flag never changes any hours value). Defaults false.
	dstAffected: boolean;
	// When true, this employee is flagged "Exclude from PBJ". The backend drops these rows by default
	// (_all_pbj_staffing_rows include_excluded=False), so the Staffing Hours grid endpoint never returns
	// them; they surface only in the dedicated "Excluded from PBJ" audit tab (include_excluded=True) and
	// the verify reconciliation. Defaults false.
	excludedFromPbj: boolean;
	excludeFromPbjReason: string | null;
};

/**
 * DAT-209 one underlying pbj_shift in the multi-shift expand editor; each row's metadata/job fields
 * are independently editable. `hours` is the read-only worked-minus-breaks computation.
 */
export type PbjStaffingShiftRow = {
	id: string;
	pbjShiftId: string;
	startTime: string; // ISO datetime
	endTime: string; // ISO datetime
	hours: number;
	breakHours: number; // break time removed from this shift's staffing date (hours net + breakHours = worked)
	pbjJobId: string | null;
	payTitleCode: string | null; // pbj_job.code for the shift's mapped job (edited by picking a job)
	rawJob: string | null;
	departmentName: string | null;
	earnCode: string | null;
	agencyName: string | null;
	// EFFECTIVE Pay Type Code for THIS shift (its own override when set, else the employee-derived
	// code) + the raw override so the expand editor can render the "manually set" indicator per shift.
	payTypeCode: string | null;
	payTypeCodeOverride: string | null;
	// INFORMATIONAL: this shift crossed a Daylight-Saving transition (per-shift companion to
	// PbjStaffingHoursRow.dstAffected). `hours` is already correct; the flag never changes it. Defaults false.
	dstAffected: boolean;
};

/**
 * DAT-209: the editable pbj_shift Staffing-Hours fields. Only include the field(s) you changed —
 * an omitted field is left untouched on the BE, while an explicit `null` clears it (for
 * `payTypeCode`, clearing reverts to the derived value). Shared by the single-row PATCH body and
 * the bulk-edit body's `edits` object.
 */
export type PbjStaffingBulkEdits = {
	pbjJobId?: string | null;
	rawJob?: string | null;
	departmentName?: string | null;
	earnCode?: string | null;
	agencyName?: string | null;
	payTypeCode?: string | null; // manual override "1"|"2"|"3"; null clears → derived
};

/**
 * DAT-209 partial edit body for one pbj_shift's Staffing-Hours metadata/job fields (see
 * `PbjStaffingBulkEdits` for the absent-vs-null semantics).
 */
export type PbjShiftManualEditBody = PbjStaffingBulkEdits & {
	departmentId: string;
};

/**
 * One selected Staffing-Hours grid row for the bulk edit, identified by its group key
 * (pbj_employee_id, dept-local date, pbj_job_id — null pbjJobId = the unmapped group). The server
 * resolves each key to ALL contributing pbj_shift rows, so aggregated rows bulk-edit too.
 */
export type PbjStaffingBulkEditRowKey = {
	pbjEmployeeId: string;
	date: string; // ISO yyyy-mm-dd (dept-local)
	pbjJobId: string | null;
};

/** DAT-209 bulk edit: apply one `edits` object to every pbj_shift contributing to the selected rows. */
export type PbjStaffingBulkEditBody = {
	departmentId: string;
	rows: PbjStaffingBulkEditRowKey[];
	edits: PbjStaffingBulkEdits;
};

/**
 * Bulk-edit summary. `matchedRows < rows.length` means some selected rows didn't resolve to shifts;
 * `appliedRows < matchedRows` means some rows share a midnight-spanning shift with an unselected
 * date — those shifts are skipped so the edit never leaks onto unselected rows.
 */
export type PbjStaffingBulkEditResult = {
	matchedRows: number;
	appliedRows: number;
	updatedShifts: number;
};

/**
 * DAT-810 FE signal for the PBJ Audit tab: which table its numbers were read from.
 *
 * `"pbj_shift"` = the department has a PBJ mirror and every figure on the tab is the CMS basis.
 * `"nurse_in_shift_actual"` = it has none, so the tab fell back to the staffing schedule and the
 * numbers are partial: only the staff scheduled in InHouse (NOT a nursing-only filter — support roles
 * count whenever they are scheduled here), gross of meal breaks, and no CMS job codes. Read
 * `hasPbjMirror` for the banner condition; the string says WHICH source was used.
 */
export type PbjAuditWorkedSource = {
	workedSource: "pbj_shift" | "nurse_in_shift_actual";
	hasPbjMirror: boolean;
};

/** DAT-1044: one building of the PBJ network view — a department the caller supervises that files its own PBJ. */
export type PbjNetworkBuilding = {
	departmentId: string;
	name: string;
	/** IANA zone the building's PBJ days and punch times are read in. */
	timezone: string;
};

/**
 * DAT-1044: one building's row on the PBJ network Summary tab. Every count is the length of the list
 * its Audit tab shows; the hour buckets can overlap, so their sum is "hours to review", not a partition.
 */
export type PbjBuildingAuditSummary = {
	departmentId: string;
	totalHours: number;
	employeeDays: number;
	longShiftCount: number;
	longShiftHours: number;
	shortShiftCount: number;
	shortShiftHours: number;
	missingPunchCount: number;
	breaksRemovedCount: number;
	terminatedCount: number;
	afterTerminationHours: number;
	excludedCount: number;
	unmappedHours: number;
	unmappedJobCount: number;
	cappedHoursRemoved: number;
};

/**
 * FE signal for the Staffing Hours grid: when `hideSourceSpecificColumns` is true the EarnCode and
 * Dist Job Code columns are hidden (they're structurally always NULL for Empeon-only departments). When
 * `showPositionCode` is true (Pharos) the iSolved Position code carried in `distJobCode` shows as its
 * own Position Code column, replacing Dist Job Code. The server owns the rules; the FE just reads the bools.
 */
export type PbjStaffingColumnVisibility = {
	hideSourceSpecificColumns: boolean;
	showPositionCode: boolean;
};

/** Summary returned by the UI-triggered Paycom shift import (file upload → ShiftSyncer). */
export type PbjShiftImportResult = {
	inserted: number;
	deleted: number;
	ignored: number;
	unmappedEventTypes: string[];
	departmentsSynced: number;
	punchEvents: number;
	dryRun: boolean;
};

// PBJ manual-hours (admin submission) import preview — dry-run diff of an uploaded file against
// existing manual pbj_shift rows in scope. Rendered in the two-step import dialog before commit.
export type PbjManualImportRowStatus = "new" | "updated" | "removed" | "unchanged";
export type PbjManualImportWarningSeverity = "block" | "warn";

export type PbjManualImportWarning = {
	severity: PbjManualImportWarningSeverity;
	code: string;
	message: string;
};

export type PbjManualImportRow = {
	pbjEmployeeId: string;
	name: string;
	date: string;
	jobDescription: string;
	facility: string;
	departmentId: string | null;
	oldHours: number | null;
	newHours: number;
	status: PbjManualImportRowStatus;
};

export type PbjManualImportNewEmployee = {
	pbjEmployeeId: string;
	name: string;
	facility: string;
	departmentId: string;
};

export type PbjManualImportFacilityRoute = {
	facilityValue: string;
	departmentId: string | null;
	matched: boolean;
};

export type PbjManualImportSummary = {
	fileRows: number;
	employees: number;
	facilities: number;
	inserted: number;
	updated: number;
	deleted: number;
	unchanged: number;
	hoursAdded: number;
	hoursRemoved: number;
};

export type PbjManualImportPreview = {
	summary: PbjManualImportSummary;
	rows: PbjManualImportRow[];
	newEmployees: PbjManualImportNewEmployee[];
	facilityRouting: PbjManualImportFacilityRoute[];
	warnings: PbjManualImportWarning[];
	blocked: boolean;
	committed: boolean;
	dryRun: boolean;
};

// Admin-logged nursing-hours entry surfaced in the PBJ Audit "Nursing Hours" tab.
// One row per "Nurse X works Y hrs/day on these weekdays"; expanded to per-date
// audit rows on the BE.
export type PbjManualHoursEntry = {
	id: string;
	departmentId: string;
	nurseId: number;
	nurseName: string;
	nurseRole: NurseFullRole | null;
	hoursPerDay: number;
	daysOfWeek: number[]; // ISO weekdays 1..7 (Mon=1 .. Sun=7)
	startDate?: string | null;
	endDate?: string | null;
};

export type PbjManualHoursCreateBody = {
	departmentId: string;
	nurseId: number;
	hoursPerDay: number;
	daysOfWeek: number[];
	startDate?: string | null;
	endDate?: string | null;
};

export type PbjManualHoursOverrideUpsertBody = {
	departmentId: string;
	localDate: string; // ISO yyyy-mm-dd
	workingHours: number;
};

// Admin-entered lump-sum extra nursing hours added on top of worked-shift buckets
// when scoring the PBJ Prediction tab. Distinct from `PbjManualHoursEntry` above
// (Audit tab, per-nurse-per-weekday). Hours are split into weekday vs weekend so
// the Weekend HPPD bucket can be moved independently of Total HPPD. `isRn=true`
// contributes to BOTH the RN and Total HPPD buckets (RN ⊂ Total) — likewise the
// weekend-RN subset is a subset of Weekend.
/**
 * How the admin TYPED a manual-hours entry — never what the stored numbers mean.
 * `total`: the hour fields are the whole period's hours.
 * `recurring`: the admin typed a per-day rate; the client multiplied it by the period's day count
 * for that day class before saving. Either way `weekdayHours` / `weekendHours` are the period total.
 */
export type PbjPredictionHoursMode = "total" | "recurring";

export type PbjPredictionManualHoursEntry = {
	id: string;
	departmentId: string;
	periodStartDate: string; // ISO yyyy-mm-dd
	periodEndDate: string; // ISO yyyy-mm-dd
	isRn: boolean;
	weekdayHours: number;
	weekendHours: number;
	employeeName: string | null;
	/** The job these hours stand for. Null on entries written before jobs were captured. */
	jobId: string | null;
	/** Resolved server-side so the table can label a row without holding the job catalog. */
	jobDisplayName: string | null;
	hoursMode: PbjPredictionHoursMode;
	createdBy: string | null;
};

export type PbjPredictionManualHoursCreateBody = {
	departmentId: string;
	periodStartDate: string;
	periodEndDate: string;
	isRn: boolean;
	weekdayHours: number;
	weekendHours: number;
	employeeName: string | null;
	jobId: string | null;
	hoursMode: PbjPredictionHoursMode;
};

export type PbjPredictionManualHoursUpdateBody = PbjPredictionManualHoursCreateBody;

/** An inclusive `[start, end]` span of department-local ISO dates. */
export type PbjDateRange = {
	start: string;
	end: string;
};

/**
 * One PBJ-relevant job contributing NO hours to the prediction over part of the window — neither a
 * punched shift nor a manual-hours entry speaks for it on those days.
 */
export type PbjPredictionJobCoverageGap = {
	jobId: string;
	jobName: string;
	jobDisplayName: string;
	/** True when the job is absent for the ENTIRE elapsed window, not just some days of it. */
	fullPeriod: boolean;
	missingDays: number;
	gaps: PbjDateRange[];
};

export type PbjAuditBreakdownRow = {
	shiftId: string;
	departmentId: string;
	startTime: string;
	endTime: string;
	rateType: string;
	totalHours: number;
	rate: number;
	wage: number;
};

export type PbjAuditEmployeeRow = {
	nurseId: number | null; // null for load_only employees (sourced from pbj_shift, no linked nurse)
	pbjId: string; // the RAW synced id — also the edit key for the override endpoint
	name: string;
	hireDate: string | null; // ISO yyyy-mm-dd
	terminationDate: string | null;
	payTypeCode: "1" | "2" | "3"; // 1=exempt, 2=non-exempt, 3=contract — the EFFECTIVE (override-or-synced) code
	pbjOverrideEmployeeId: string | null; // DAT-501 manual override; effective/displayed id = pbjOverrideEmployeeId ?? pbjId
	payTypeCodeOverride: "1" | "2" | "3" | null; // the manual Pay-Type override alone; null = code is purely synced
};

/**
 * DAT-501 body for PATCH /pbj-model/audit/employees/pbj-override-id. `pbjEmployeeId` is the RAW
 * synced pbj_id from the roster row; `pbjOverrideEmployeeId: null` clears the override (reverts
 * the employee to the synced id).
 */
export type PbjEmployeeOverrideIdBody = {
	departmentId: string;
	pbjEmployeeId: string;
	pbjOverrideEmployeeId: string | null;
};

/**
 * Body for PATCH /pbj-model/audit/employees/pay-type-override. `pbjEmployeeId` is the RAW synced
 * pbj_id from the roster row; `payTypeCode: null` clears the override (reverts the employee to the
 * synced Pay Type Code). Pay Type Code is the only user-facing control for the exempt/tax overrides.
 */
export type PbjPayTypeOverrideBody = {
	departmentId: string;
	pbjEmployeeId: string;
	payTypeCode: "1" | "2" | "3" | null;
};

// PBJ override bulk import preview — dry-run diff of an uploaded overrides file (Override ID +
// Pay Type Code per employee) against the currently-synced roster. Mirrors the PbjManualImport*
// block above; rendered in the "Manage overrides → Upload file" flow before commit.
export type PbjOverrideImportRowStatus = "new" | "updated" | "removed" | "unchanged";
export type PbjOverrideImportWarningSeverity = "block" | "warn";

export type PbjOverrideImportWarning = {
	severity: PbjOverrideImportWarningSeverity;
	code: string;
	message: string;
	pbjEmployeeId?: string | null;
};

export type PbjOverrideImportRow = {
	pbjEmployeeId: string;
	name: string;
	oldOverrideId: string | null;
	newOverrideId: string | null;
	oldPayTypeCode: string | null;
	newPayTypeCode: string | null;
	status: PbjOverrideImportRowStatus;
};

export type PbjOverrideImportSummary = {
	new: number;
	updated: number;
	removed: number;
	unchanged: number;
};

export type PbjOverrideImportPreview = {
	summary: PbjOverrideImportSummary;
	rows: PbjOverrideImportRow[];
	warnings: PbjOverrideImportWarning[];
	blocked: boolean;
	committed: boolean;
	dryRun: boolean;
};

/**
 * Body for PATCH /pbj-model/audit/employees/pbj-exclude (→ 204). `pbjEmployeeId` is the RAW synced
 * pbj_id from the roster row; the flag is applied across the facility's rolled-up departments. When
 * `exclude` is true a non-blank `reason` is MANDATORY (it becomes the CMS-filing drop reason); clearing
 * (`exclude: false`) nulls the reason server-side (pass `reason: null`).
 */
export type PbjExcludeEmployeeBody = {
	departmentId: string;
	pbjEmployeeId: string;
	exclude: boolean;
	reason: string | null;
};

/**
 * One employee excluded from PBJ across the facility group (GET /pbj-model/audit/employees/pbj-excluded).
 * `excludedHours` is that employee's dropped hours for the selected quarter/date range (sum of their
 * staffing-hours rows removed from the filing) so the reviewer sees the impact of the exclusion.
 */
export type PbjExcludedEmployeeRow = {
	pbjEmployeeId: string;
	name: string;
	departmentId: string;
	reason: string | null;
	excludedBy: string | null;
	excludedAt: string | null; // ISO datetime
	excludedHours: number;
};

// PBJ bulk-exclusion import preview — dry-run diff of an uploaded ID + Reason list, keyed by the RAW
// synced pbj_id (mirrors PbjOverrideImport*; no fuzzy name matching). Rendered in the "Excluded from PBJ
// → Bulk import" flow before commit. Row status reuses the override enum: `new` = will exclude,
// `removed` = will clear an existing exclusion, `unchanged` = no-op (the dialog maps them to labels).
export type PbjExclusionImportRowStatus = PbjOverrideImportRowStatus;
export type PbjExclusionImportWarningSeverity = PbjOverrideImportWarningSeverity;

export type PbjExclusionImportWarning = {
	severity: PbjExclusionImportWarningSeverity;
	code: string;
	message: string;
	pbjEmployeeId?: string | null;
};

export type PbjExclusionImportRow = {
	pbjEmployeeId: string;
	name: string;
	currentlyExcluded: boolean;
	currentReason: string | null;
	newReason: string | null; // the reason to apply (null → clear the exclusion)
	status: PbjExclusionImportRowStatus;
};

export type PbjExclusionImportSummary = {
	fileRows: number;
	excluded: number; // rows that would be newly excluded on commit (status new)
	cleared: number; // rows that would be un-excluded on commit (status removed)
	unchanged: number; // no-op
	blocked: number; // rows blocked by an unknown / duplicate pbj_id
};

export type PbjExclusionImportPreview = {
	summary: PbjExclusionImportSummary;
	rows: PbjExclusionImportRow[];
	warnings: PbjExclusionImportWarning[];
	blocked: boolean;
	committed: boolean;
	dryRun: boolean;
};

// Audit raw-export sheet rows. Each mirrors one XLSX sheet in the PBJ raw export
// (camelCase per the BE response models). Read-only audit views in the Audit tab.

/**
 * Role Summary sheet: net-of-breaks hours rolled up by the mapped PBJ job. `role` is the PBJ job
 * description (or the literal "Unmapped" bucket, always last); `code` is the CMS Pay Title Code
 * (null for the Unmapped bucket). Also reused as the per-job totals row of PbjStaffingHoursSummary.
 */
export type PbjRoleSummaryRow = {
	role: string;
	hours: number;
	code: string | null;
};

/**
 * Hours-by-Role daily time-series for the Audit "Hours by Role" chart: total net-of-breaks hours per
 * role (outer key — the PBJ job description, or the literal "Unmapped" bucket) per dept-local calendar
 * day (inner key — plain ISO yyyy-mm-dd, daily; value = hours to 2 decimals). A role's daily values sum
 * to its Role Summary total. Hand-maintained mirror of the BE `PbjHoursByRoleDaily` response model.
 */
export type PbjHoursByRoleDaily = {
	hoursByRole: Record</* roleLabel */ string, Record</* ISO yyyy-mm-dd */ string, number>>;
	/**
	 * Stable group key ("NURSING" | "THERAPY" | "DIETARY" | "ADMINISTRATIVE" | "SUPPORT_AND_OTHER")
	 * -> the role labels present in that group. Every label also appears as a `hoursByRole` key.
	 * Drives the grouped role-filter tree beside the chart.
	 */
	roleGroups: Record</* groupKey */ string, /* roleLabels */ string[]>;
};

/**
 * Group-by summary of the Staffing Hours rows for one (department, date range): per-PBJ-job totals
 * (with an Unmapped bucket), grand total, weekend (Sat+Sun, dept-local staffing date) vs. weekday
 * split, and the agency/contract subset (pay type code "3"). Backend-computed from the same rows
 * the Staffing Hours grid renders, so the on-screen summary and the XLSX block can never diverge.
 */
export type PbjStaffingHoursSummary = {
	jobTotals: PbjRoleSummaryRow[];
	totalHours: number;
	weekendHours: number;
	weekdayHours: number;
	contractHours: number;
	// INFORMATIONAL: count of distinct employee-days that crossed a Daylight-Saving transition in this
	// period. Surfaced in the summary strip only when > 0; never alters any hours figure. Defaults 0.
	dstAffectedDays: number;
};

/** Breaks Removed sheet: one row per employee-day where break time was deducted from worked hours. */
export type PbjBreaksRemovedRow = {
	empId: string;
	name: string;
	jobDescription: string | null;
	date: string; // ISO yyyy-mm-dd
	breakHoursRemoved: number;
};

/** Terminated sheet: employees terminated within (or before) the window, with their worked hours. */
export type PbjTerminatedEmployeeRow = {
	empId: string;
	name: string;
	jobDescription: string | null;
	terminationDate: string; // ISO yyyy-mm-dd
	totalHours: number;
	lastShiftWorked: string | null; // ISO yyyy-mm-dd; null if no shift worked in window
};

/** Multiple Roles sheet: one row per (employee, job description) for staff that worked more than one role. */
export type PbjMultipleRoleRow = {
	empId: string;
	name: string;
	jobDescription: string;
	hours: number;
};

/** Long Shifts sheet: one row per (employee, dept-local day) whose net staffing hours summed across the day is >= 22.5h. */
export type PbjLongShiftRow = {
	empId: string;
	name: string;
	date: string; // ISO yyyy-mm-dd
	totalHours: number;
	jobDescriptions: string[];
};

/**
 * Long Shift Review sheet: one row per individual shift whose working hours exceed the threshold.
 * Keeps overnight shifts whole and exposes both elapsed and PBJ break-netted working hours.
 */
export type PbjLongShiftReviewRow = {
	pbjShiftId: string; // UUID — unique per shift; stable grid row id (composite empId/start/end can collide on dupes)
	empId: string;
	name: string;
	date: string; // ISO date (dept-local date of the shift start)
	startTime: string; // ISO datetime
	endTime: string; // ISO datetime
	duration: number; // gross wall-clock hours (end - start)
	workingHours: number; // PBJ hours after recorded breaks and any required meal-break deduction
};

/**
 * Long Shift Review audit response (its own endpoint, separate from the Single Day Flag's bare `PbjLongShiftRow[]`
 * so neither endpoint's response body changes shape). `rows` are the per-shift flags; `thresholdHours` is the saved
 * working-hours cutoff, echoed by the BE so the FE labels the section without re-hardcoding the constant.
 */
export type PbjLongShiftReviewResponse = {
	rows: PbjLongShiftReviewRow[];
	thresholdHours: number;
};

/**
 * One (employee, dept-local day) whose SUBMITTED PBJ hours — summed across every job title + pay type code —
 * exceeded the CMS daily cap and were trimmed to exactly it in the filed XLSX/XML. Surfaced read-only in the
 * Long Shifts audit tab so every override is auditable. `reductionHours` == originalHours - cappedHours.
 */
export type PbjDailyHoursCapRow = {
	empId: string;
	name: string;
	date: string; // ISO yyyy-mm-dd in the dept's local tz
	originalHours: number; // real worked hours before the cap
	cappedHours: number; // hours actually filed (always the fixed CMS cap)
	reductionHours: number; // originalHours - cappedHours
};

/**
 * The daily-hours-cap report for one (department, date range): every (employee, day) trimmed to the cap, plus
 * the FIXED cap they were trimmed to — echoed so the FE labels the section without re-hardcoding the constant.
 * Empty `rows` means nothing exceeded the cap (the common case).
 */
export type PbjDailyHoursCapReportResponse = {
	rows: PbjDailyHoursCapRow[];
	capHours: number;
};

/**
 * Short Shift Review sheet: the low-end mirror of the Long Shift Review — one row per individual shift whose
 * working hours are under a fixed 1h. A sub-hour punch pair is almost always a bad punch rather
 * than real direct-care time. Zero-length missing-punch MARKER rows are excluded by the BE (they have their own
 * Missed Punches tab); a zero-length row that isn't so flagged is a genuine finding and does appear.
 */
export type PbjShortShiftReviewRow = {
	pbjShiftId: string; // UUID — unique per shift; stable grid row id (composite empId/start/end can collide on dupes)
	empId: string;
	name: string;
	date: string; // ISO date (dept-local date of the shift start)
	startTime: string; // ISO datetime
	endTime: string; // ISO datetime
	duration: number; // gross wall-clock hours (end - start)
	workingHours: number; // PBJ hours after recorded breaks and any required meal-break deduction
};

/** Short Shift Review audit response. `thresholdHours` is the fixed working-hours floor, echoed by the BE so the
 * FE labels the section without re-hardcoding the constant. */
export type PbjShortShiftReviewResponse = {
	rows: PbjShortShiftReviewRow[];
	thresholdHours: number;
};

/** Missing Punches sheet: shifts missing a clock-in and/or clock-out punch. */
export type PbjMissingPunchRow = {
	empId: string;
	name: string;
	jobDescription: string;
	date: string; // ISO yyyy-mm-dd
	hours: number; // 0.00 for missing-punch rows; the table renders "—" off the missing-punch flags
	missingPunchIn: boolean;
	missingPunchOut: boolean;
};

/**
 * Upcoming Turnover audit: an employee approaching PBJ turnover, evaluated as-of-today (the audit
 * page's quarter/date range does not apply). Mirrors backend/model.py PbjUpcomingTurnoverRow — the
 * exact rule the nurse_inactivity_alert notification runs, sourced from the PBJ submission
 * (pbj_shift): nursing employees (CMS pay title codes 5-12 plus 13 Nurse Practitioner — the backend's
 * NURSING_PAY_TITLE_CODES), agency included, terminated excluded.
 * `empId` is null when the nurse has no external_employee mapping in the facility group (rendered
 * blank). The two scheduled-shift fields are mutually exclusive views of the SAME soonest committed
 * shift: `scheduledShiftDate` when it lands in time to prevent the turnover, `lateScheduledShiftDate`
 * when it lands after (rendered red — the employee is returning, just too late, so the fix is to move
 * that shift earlier). Both null = nothing on the schedule at all, the genuinely at-risk case.
 */
export type PbjUpcomingTurnoverRow = {
	nurseId: string; // internal nurse id — guaranteed unique, used as the grid row id
	empId: string | null;
	name: string;
	employmentType: EmploymentType;
	lastShiftDate: string; // ISO yyyy-mm-dd
	daysUntilTurnover: number;
	turnoverDate: string; // ISO yyyy-mm-dd
	scheduledShiftDate: string | null; // ISO yyyy-mm-dd, dept-local; null = none in time to prevent turnover
	lateScheduledShiftDate: string | null; // ISO yyyy-mm-dd, dept-local; null = no shift after turnoverDate either
};

// Per-nurse turnover audit. Mirrors backend/model.py PbjAudit{Transition,TurnoverRow,TurnoverResponse}.
export type PbjAuditTransition = {
	date: string; // ISO yyyy-mm-dd
	kind: "active" | "inactive";
	trigger: "threshold" | "gap" | "tail";
	gapStartDate?: string | null;
	consecutiveZeroDays?: number | null;
};

export type PbjAuditTurnoverRow = {
	nurseId: number;
	pbjId: string | null;
	name: string;
	role: NurseFullRole | null;
	isRn: boolean;
	isAdmin: boolean;
	activeTransitions: number;
	inactiveTransitions: number;
	// DAT-668 — flagged as inactive with no shift booked before their turnover date, so the prediction
	// already counts them as a departure before the state machine has walked them to inactive.
	pendingTurnover: boolean;
	finalState: "active" | "inactive";
	totalHours: number;
	lastShiftDate: string | null;
	transitions: PbjAuditTransition[];
};

export type PbjAuditTurnoverResponse = {
	rows: PbjAuditTurnoverRow[];
	totalActive: number;
	totalInactive: number;
	rnActive: number;
	rnInactive: number;
	adminActive: number;
	adminInactive: number;
	// DAT-668 projected departures, kept apart from the `*Inactive` counts above: the prediction's
	// turnover numerator is `*Inactive + *Pending`.
	totalPending: number;
	rnPending: number;
	adminPending: number;
};

/** How Management Center attributes a nurse-week's weekly OT across days. Mirrors the backend `ot_mode` query param. */
export type OtMode = "trigger" | "actual";

/**
 * One pending-approval row on Annie's open screen (GET /annie/recommendations). PHI-minimal and keyed on
 * shift identity, not a person — the queue can have multiple applicants per shift. `label` is an always-present
 * dept-local shift descriptor; `date` is an ISO `YYYY-MM-DD` dept-local calendar date; `times` is the shift's
 * time-range string; `role` is the shift's role and may be null (fall back to `label`); `pending_applicants`
 * is how many staff applied; `is_replacement` marks a pending replacement (vs an open-pickup applicant).
 * Mirrors the backend `PickupApprovalItem`.
 */
export interface PickupApprovalItem {
	request_id: string;
	label: string;
	date: string;
	role: string | null;
	times: string;
	pending_applicants: number;
	is_replacement: boolean;
}

/**
 * How many recent shifts a nurse has worked at each location, over a trailing window. A location absent
 * from `countsByLocation` means zero recent shifts there. Powers the location picker's "N recent shifts"
 * subtitle and its "works most" ordering.
 */
export interface NurseLocationHistoryResponse {
	nurseId: number;
	windowDays: number;
	countsByLocation: Record<string, number>;
}

/** Where a request's outreach stands. Mirrors the planned `RequestOutreach` backend model (APP-918). */
export enum OutreachStatus {
	/** Still asking people. */
	Running = "running",
	/** Stopped by a user; Resume picks up where it left off. */
	Paused = "paused",
	/** Enough people were approved, so it stopped asking. */
	Filled = "filled",
	/** Nobody is left to text or call. */
	Finished = "finished",
	/** The request was canceled, so outreach stopped for good. */
	Canceled = "canceled",
}

/**
 * How hard outreach pushes for one shift. Each level adds a way of reaching people on top of the ones under it, and
 * the plan steps up on its own on a schedule set by the shift's urgency; the user can set a level for one shift.
 */
export enum OutreachLevel {
	/** Texts to the best matches, a few each day. */
	Text = "text",
	/** Texts to everyone who fits, sooner, and an alert in the staff app. */
	MoreTexts = "more_texts",
	/** Phone calls, best matches first. */
	Calls = "calls",
	/** Offers to people the shift would put into daily overtime; calls speed up. */
	Overtime = "overtime",
	/** The shift goes to a staffing agency. */
	Agency = "agency",
}

/** The levels, lowest first. */
export const OUTREACH_LEVELS: readonly OutreachLevel[] = [
	OutreachLevel.Text,
	OutreachLevel.MoreTexts,
	OutreachLevel.Calls,
	OutreachLevel.Overtime,
	OutreachLevel.Agency,
];

/** What outreach may use for this shift. Each one is used from its level up; switched off, that level adds nothing. */
export type OutreachAllowed = { calls: boolean; overtime: boolean; agency: boolean };

/** How fast the plan steps up: set by how soon the shift is, how hard it usually is to fill, and how short-staffed it is. */
export enum OutreachUrgency {
	Normal = "normal",
	Urgent = "urgent",
	Critical = "critical",
}

/** One reason behind the shift's urgency. `speedsUp` is whether it pushes the plan to step up sooner. */
export enum OutreachPaceReasonKind {
	LeadTime = "lead_time",
	FillHistory = "fill_history",
	Staffing = "staffing",
}

export type OutreachPaceReason = { speedsUp: boolean } & (
	| { kind: OutreachPaceReasonKind.LeadTime; daysBefore: number }
	| { kind: OutreachPaceReasonKind.FillHistory; hardToFill: boolean }
	| { kind: OutreachPaceReasonKind.Staffing; scheduled: number; target: number; minimum: number }
);

/** When a level starts. `null` for one that won't, because the user is holding below it. */
export interface OutreachLevelStart {
	level: OutreachLevel;
	startsAt: DateTime | null;
}

export interface OutreachEffort {
	/** The level outreach is on now. */
	level: OutreachLevel;
	/** The level the plan would be on now without the user: "our pick". */
	automaticLevel: OutreachLevel;
	setByUser: boolean;
	/** The user stopped the plan stepping up past `level`; the levels above it have no start. */
	holding: boolean;
	/** Every level, lowest first, with when it started or will. */
	schedule: OutreachLevelStart[];
	allowed: OutreachAllowed;
	urgency: OutreachUrgency;
	paceReasons: OutreachPaceReason[];
}

/** The post to an agency platform the Agency level makes, only if enough spots are still open then. */
export interface OutreachAgencyPost {
	source: AgencySource;
	minOpenSpots: number;
	/** `null` while it hasn't gone up. */
	postedAt: DateTime | null;
}

/** How a person heard from us. */
export enum OutreachVia {
	Text = "text",
	/** A push notification in the staff app. */
	AppAlert = "app_alert",
	Call = "call",
}

/** The scoring algorithm's six factor sections, in the algorithm doc's order. */
export enum OutreachSection {
	Preferences = "preferences",
	WorkHistory = "work_history",
	Acceptance = "acceptance",
	WeeklyHours = "weekly_hours",
	RecurringShifts = "recurring_shifts",
	NotificationLoad = "notification_load",
}

/** Every factor the scorer reports. Its text lives in i18n under `request.outreach.factor.*`, so the backend sends kinds, not copy. */
export enum OutreachFactorKind {
	PrefersShiftType = "prefers_shift_type",
	WorkedSameHours = "worked_same_hours",
	NotWorkedHoursLately = "not_worked_hours_lately",
	LastAcceptedDaysAgo = "last_accepted_days_ago",
	LastAcceptedWeeksAgo = "last_accepted_weeks_ago",
	AcceptsMostOffers = "accepts_most_offers",
	AcceptsFewOffers = "accepts_few_offers",
	AcceptingMoreThanUsual = "accepting_more_than_usual",
	AcceptingLessThanUsual = "accepting_less_than_usual",
	AcceptedYesterday = "accepted_yesterday",
	NeverAccepted = "never_accepted",
	LowHoursThatWeek = "low_hours_that_week",
	HighHoursThatWeek = "high_hours_that_week",
	OvertimeThatWeek = "overtime_that_week",
	FewRecurringShifts = "few_recurring_shifts",
	ManyRecurringShifts = "many_recurring_shifts",
	FewNotifications = "few_notifications",
	NotificationsThisWeek = "notifications_this_week",
	NotifiedToday = "notified_today",
}

/** A factor's pull on the score: ±1 some, ±2 strong. Never 0 — a factor with no pull isn't reported. */
export type OutreachFactorStrength = -2 | -1 | 1 | 2;

/** Factor kinds whose text names a number (times, days, weeks, hours, notifications). */
export type OutreachCountedFactorKind =
	| OutreachFactorKind.WorkedSameHours
	| OutreachFactorKind.LastAcceptedDaysAgo
	| OutreachFactorKind.LastAcceptedWeeksAgo
	| OutreachFactorKind.LowHoursThatWeek
	| OutreachFactorKind.HighHoursThatWeek
	| OutreachFactorKind.OvertimeThatWeek
	| OutreachFactorKind.NotificationsThisWeek;

/** Factor kinds whose text names nothing. */
export type OutreachPlainFactorKind = Exclude<OutreachFactorKind, OutreachFactorKind.PrefersShiftType | OutreachCountedFactorKind>;

export type OutreachFactor = { section: OutreachSection; strength: OutreachFactorStrength } & (
	| { kind: OutreachFactorKind.PrefersShiftType; shiftType: ShiftType }
	| { kind: OutreachCountedFactorKind; count: number }
	| { kind: OutreachPlainFactorKind }
);

/** The label a person's total earns. An ask-last factor (just took a shift, never took one) makes it `LongShot` whatever the total. */
export enum OutreachMatch {
	Great = "great",
	Good = "good",
	Fair = "fair",
	Maybe = "maybe",
	LongShot = "long_shot",
}

export interface OutreachScoredPerson {
	nurseId: number;
	name: string;
	match: OutreachMatch;
	factors: OutreachFactor[];
}

/** How someone we asked answered, short of accepting (an acceptance is a sign-up). */
export enum OutreachAnswer {
	Declined = "declined",
	NoAnswer = "no_answer",
}

/** How someone got onto the send list: ranked by the scorer, or reached past an overridable filter (texted anyway, or offered overtime). */
export enum OutreachReachedKind {
	Scored = "scored",
	Override = "override",
}

export type OutreachReachedPerson = { via: OutreachVia; answer: OutreachAnswer } & (
	| { kind: OutreachReachedKind.Scored; person: OutreachScoredPerson }
	| { kind: OutreachReachedKind.Override; person: OutreachExcludedPerson; filter: OutreachExclusionFilter }
);

/** One send. `byUser` sends are the manual ones (Text now, Text anyway, and what a raised level sends at once). */
export interface OutreachSentBatch {
	id: string;
	sentAt: DateTime;
	byUser: boolean;
	people: OutreachReachedPerson[];
}

/** A planned text or app alert; calls are planned one by one in `plannedCalls`. */
export interface OutreachPlannedBatch {
	id: string;
	via: OutreachVia.Text | OutreachVia.AppAlert;
	plannedAt: DateTime;
	people: OutreachScoredPerson[];
}

/** The eligibility filters that run before scoring, in the order they run. */
export enum OutreachExclusionFilter {
	ChargeNurse = "charge_nurse",
	DifferentRole = "different_role",
	InactiveOrLeaving = "inactive_or_leaving",
	Pto = "pto",
	OverlappingShift = "overlapping_shift",
	OverMaxDailyHours = "over_max_daily_hours",
	DailyOvertime = "daily_overtime",
}

export enum OutreachExclusionReason {
	ChargeNurse = "charge_nurse",
	Role = "role",
	LastDay = "last_day",
	ProfileInactive = "profile_inactive",
	Pto = "pto",
	WorkingShift = "working_shift",
	WorkingThatDay = "working_that_day",
	/** On leave on the shift's day; `to` is the first day back, `null` when open-ended. */
	OnLeave = "on_leave",
	/** Suspended on the shift's day; `to` as for `OnLeave`. */
	Suspended = "suspended",
}

/** One phone call outreach will make, in match order. */
export interface OutreachPlannedCall {
	person: OutreachScoredPerson;
	plannedAt: DateTime;
}

/** Each reason carries what its text names: the role they hold, their last day, the PTO or shift's range, or the day they're back. */
export type OutreachExcludedPerson = { nurseId: number; name: string } & (
	| { reason: OutreachExclusionReason.ChargeNurse | OutreachExclusionReason.ProfileInactive }
	| { reason: OutreachExclusionReason.Role; role: string }
	| { reason: OutreachExclusionReason.LastDay; date: DateTime }
	| { reason: OutreachExclusionReason.Pto | OutreachExclusionReason.WorkingShift | OutreachExclusionReason.WorkingThatDay; from: DateTime; to: DateTime }
	| { reason: OutreachExclusionReason.OnLeave | OutreachExclusionReason.Suspended; to: DateTime | null }
);

export interface OutreachExcludedGroup {
	filter: OutreachExclusionFilter;
	/** Whether a user may text these people anyway. Only daily overtime can be overridden; the Overtime level offers it on its own. */
	canOverride: boolean;
	/** The daily hour cap `OverMaxDailyHours` enforces; `null` for every other filter. */
	limitHours: number | null;
	people: OutreachExcludedPerson[];
}

/** Where a sign-up stands: waiting for a manager, approved by one or automatically, or turned down. */
export enum OutreachApproval {
	Waiting = "waiting",
	ByManager = "by_manager",
	Automatic = "automatic",
	NotApproved = "not_approved",
}

/** What we checked before recommending a sign-up: the role they work, their hours that week with this shift, and their schedule. */
export interface OutreachSignUpCheck {
	role: string;
	hoursThatWeek: number;
	/** Hours of overtime this shift would put them into that week; 0 for none. */
	overtimeHours: number;
	/** They're on another shift that overlaps this one. */
	hasConflict: boolean;
}

/** Someone who accepted the shift. */
export interface OutreachSignUp {
	nurseId: number;
	name: string;
	signedUpAt: DateTime;
	approval: OutreachApproval;
	/** When it was approved or turned down; `null` while waiting, the sign-up time itself when approved automatically. */
	decidedAt: DateTime | null;
	check: OutreachSignUpCheck;
}

export interface RequestOutreach {
	requestId: string;
	/** The moment the status, effort and call plan are worked out for; times on screen read relative to it. */
	asOf: DateTime;
	/**
	 * `shiftType` is the request's own `message.shift_type`, `null` when it's none we name (the backend also sends `All`);
	 * `jobId`/`subRole` are `null` on a request with no job.
	 */
	shift: {
		start: DateTime;
		end: DateTime;
		shiftType: ShiftType | null;
		roles: string[];
		jobId: string | null;
		subRole: NurseRole | null;
		neededCount: number;
	};
	status: OutreachStatus;
	effort: OutreachEffort;
	agency: OutreachAgencyPost;
	/** Whether the department auto-approves sign-ups that fit its rules; set in department configuration, not per shift. */
	autoApprove: boolean;
	/**
	 * People, not sends. `asked`: texted or called (an app alert alone doesn't count). `declined`: said no. `approved`:
	 * on the shift. Someone texted and then called counts once.
	 */
	counts: { eligible: number; asked: number; declined: number; approved: number };
	/** Newest first. */
	sentBatches: OutreachSentBatch[];
	/** Planned texts and app alerts, soonest first. */
	plannedBatches: OutreachPlannedBatch[];
	/** Everyone outreach will call, soonest first; empty when calls are off or haven't a start. */
	plannedCalls: OutreachPlannedCall[];
	/** In filter order. */
	excludedGroups: OutreachExcludedGroup[];
	/** Skipped by the user, newest first; Undo returns them to Up next. */
	skipped: OutreachScoredPerson[];
	/** Newest first. */
	signUps: OutreachSignUp[];
}
